import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import './OnboardingStyles.css';
import PlacementChainDisplay from '../../erprecruitment/Components/PlacementChainDisplay';
import EmployeeModal from '../../TimeSheetManagement/components/EmployeeModal';
import EmployeeDetailsPage from '../../TimeSheetManagement/components/UserDetails';
import '../../TimeSheetManagement/styles/ManagerDashboard.css';
// import ShareButton from '../../components/common/ShareButton';
import CommercialDocumentModal from './CommercialDocumentModal';
import {
  X, CheckCircle, Clock, AlertTriangle,
  ArrowRight, ChevronLeft, ChevronDown, ChevronUp, ShieldCheck, History, Paperclip,
  MessageSquare, Edit, Edit2, Trash2, Building2, Briefcase, Layers, Plus, UserCheck, User, DollarSign, Info, Sparkles, FileText,
  XCircle, Ban, RotateCcw, Upload, Loader2, Download, ExternalLink, Eye
} from 'lucide-react';

const NOTE_CATEGORY_COLORS = {
  GENERAL: { bg: '#f3f4f6', color: '#1f2937', border: '#e5e7eb' },
  CALL_LOG: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  EMAIL_LOG: { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe' },
  INTERVIEW_FEEDBACK: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  COMPLIANCE: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  PLACEMENT: { bg: '#fff7ed', color: '#c2410c', border: '#ffedd5' },
  FOLLOW_UP: { bg: '#fdf2f8', color: '#be185d', border: '#fbcfe8' },
  INTERNAL: { bg: '#fafafa', color: '#4b5563', border: '#e5e7eb' }
};

const NOTE_CATEGORY_LABELS = {
  GENERAL: 'General',
  CALL_LOG: 'Call Log',
  EMAIL_LOG: 'Email Log',
  INTERVIEW_FEEDBACK: 'Interview Feedback',
  COMPLIANCE: 'Compliance',
  PLACEMENT: 'Placement',
  FOLLOW_UP: 'Follow Up',
  INTERNAL: 'Internal'
};

const RECRUITMENT_ONBOARDING_STAGES_15 = [
  { index: 1, name: 'New', code: 'NEW' },
  { index: 2, name: 'Resume Review', code: 'RESUME_REVIEW' },
  { index: 3, name: 'Internal Interview 1', code: 'INTERNAL_INTERVIEW_1' },
  { index: 4, name: 'Internal Interview 2', code: 'INTERNAL_INTERVIEW_2' },
  { index: 5, name: 'Re-evaluation', code: 'RE_EVALUATION' },
  { index: 6, name: 'Ready for Client', code: 'READY_FOR_CLIENT' },
  { index: 7, name: 'Submitted to Client', code: 'SUBMITTED_TO_CLIENT' },
  { index: 8, name: 'Client Review', code: 'CLIENT_REVIEW' },
  { index: 9, name: 'Client Interview 1', code: 'CLIENT_INTERVIEW_1' },
  { index: 10, name: 'Client Interview 2', code: 'CLIENT_INTERVIEW_2' },
  { index: 11, name: 'Client Final Round', code: 'CLIENT_FINAL_ROUND' },
  { index: 12, name: 'Offer', code: 'OFFER' },
  { index: 13, name: 'Onboarding Initiated', code: 'ONBOARDING_INITIATED' },
  { index: 14, name: 'Onboarding Completed', code: 'ONBOARDING_COMPLETED' },
  { index: 15, name: 'Hired', code: 'HIRED' }
];

// Helper to determine if an onboarding subtask item supports multi-attachments
// Universal multi-file attachment support enabled for all onboarding subtasks
const isMultiAttachmentSubtask = (sub) => {
  return true;
};

const OnboardingDetailDrawer = ({ instanceId, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [savingStepId, setSavingStepId] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [showGuardModal, setShowGuardModal] = useState(false);
  const [openStepsList, setOpenStepsList] = useState([]);
  const [forceComplete, setForceComplete] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyStep, setHistoryStep] = useState(null);
  const [activeTab, setActiveTab] = useState('CHECKLIST'); // 'CHECKLIST' | 'NOTES'
  const [showChainEditor, setShowChainEditor] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [selectedBulkAssignee, setSelectedBulkAssignee] = useState('');
  const [applyingAll, setApplyingAll] = useState(false);

  // Outcome (Reject / Withdrawn) State
  const [outcomeModal, setOutcomeModal] = useState({
    open: false,
    type: 'WITHDRAWN', // 'WITHDRAWN' | 'REJECT'
    notes: '',
    submitting: false
  });

  // Revoke Outcome State
  const [revokeModal, setRevokeModal] = useState({
    open: false,
    outcomeType: 'WITHDRAWN',
    notes: '',
    submitting: false
  });

  // Action Step Modals State
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [empFormData, setEmpFormData] = useState({
    employeeCode: '',
    employeeType: 'DEPLOYED',
    startDate: '',
    email: '',
    phone: '',
    entityId: 4
  });
  const [savingEmp, setSavingEmp] = useState(false);
  // Employee search state for Create / Link Employee modal
  const [empSearchResults, setEmpSearchResults] = useState([]);
  const [isSearchingEmp, setIsSearchingEmp] = useState(false);
  const [showEmpSuggestions, setShowEmpSuggestions] = useState(false);
  const [selectedLinkedEmp, setSelectedLinkedEmp] = useState(null);

  // CRM Companies state for Referral Source dropdown
  const [crmCompanies, setCrmCompanies] = useState([]);

  const [showDepModal, setShowDepModal] = useState(false);
  const [depFormData, setDepFormData] = useState({
    placedJobTitle: '',
    startDate: '',
    endDate: '',
    clientRate: '',
    vmsPct: '',
    rateAfterVms: '',
    supplierRate: '',
    supplierVmsPct: '',
    supplierRateAfterVms: '',
    supplierNetTermsDays: '',
    compliancePayRate: '',
    actualPayRate: '',
    margin: '',
    referralSource: '',
    referralFee: '',
    entityId: 4
  });
  const [savingDep, setSavingDep] = useState(false);

  // Rate & Referral Edit State
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [savingRates, setSavingRates] = useState(false);
  const [showRateReferralDropdown, setShowRateReferralDropdown] = useState(false);
  const [showDepReferralDropdown, setShowDepReferralDropdown] = useState(false);
  const [showBulkAssignDropdown, setShowBulkAssignDropdown] = useState(false);
  const [activeAssigneeDropdownStepId, setActiveAssigneeDropdownStepId] = useState(null);
  const [stepAssigneeSearchQuery, setStepAssigneeSearchQuery] = useState({});
  const [activeSubtaskAssigneeDropdownId, setActiveSubtaskAssigneeDropdownId] = useState(null);
  const [subtaskAssigneeSearchQuery, setSubtaskAssigneeSearchQuery] = useState({});

  // Subtask Accordion & Ad-hoc Subtask State
  const [expandedSteps, setExpandedSteps] = useState({});
  const [addingSubtaskStepId, setAddingSubtaskStepId] = useState(null);
  const [newSubtaskName, setNewSubtaskName] = useState('');
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState('');
  const [savingSubtask, setSavingSubtask] = useState(false);
  const [uploadingSubtaskId, setUploadingSubtaskId] = useState(null);

  // Commercial Document Modal State (MSA/PO Linking & Drafting)
  const [commercialDocModal, setCommercialDocModal] = useState({
    open: false,
    step: null,
    typeCode: 'MSA',
    side: 'SUPPLIER',
    companyId: null,
    companyName: ''
  });

  // Employee Details Modal Popup State
  const [selectedEmployeeIdForModal, setSelectedEmployeeIdForModal] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [loadingDocx, setLoadingDocx] = useState(false);
  const [docxError, setDocxError] = useState(null);
  const [isChecklistEditMode, setIsChecklistEditMode] = useState(false);

  // Helper to safely resolve document streaming URLs with database & remote server fallbacks
  const resolveDocUrl = useCallback((storagePath, fileName, docId) => {
    if (!storagePath && !fileName && !docId) return '#';

    // External cloud URLs (S3, Azure blobs outside local ERP) can load directly
    if (storagePath && (storagePath.startsWith('http://') || storagePath.startsWith('https://')) && !storagePath.includes('/uploads/')) {
      return storagePath;
    }

    const cleanBase = (BASE_URL || '').replace(/\/+$/, '');
    const apiBase = cleanBase.endsWith('/api') ? cleanBase : `${cleanBase}/api`;

    const params = new URLSearchParams();
    if (docId) params.set('docId', docId);
    if (storagePath) params.set('path', storagePath);
    if (fileName) params.set('name', fileName);

    return `${apiBase}/onboarding/documents/view?${params.toString()}`;
  }, []);

  // Standardized document preview trigger
  const handleOpenDocumentPreview = useCallback((fileName, storagePath, docId) => {
    if (!fileName && !storagePath && !docId) return;
    const fileExt = (fileName || storagePath || '').split('.').pop().toLowerCase();
    const resolvedUrl = resolveDocUrl(storagePath, fileName, docId);
    setPreviewDoc({
      fileName: fileName || 'document',
      storagePath: storagePath || '',
      fileUrl: resolvedUrl,
      fileExt,
      docId: docId || null
    });
  }, [resolveDocUrl]);

  // Derived data references
  const instance = data?.instance;
  const deployment = data?.deployment;
  const steps = data?.steps || [];
  const placementChain = data?.placementChain || [];
  const crmMsas = data?.crmMsas || [];
  const historyLogs = data?.historyLogs || [];
  const candidateNotes = data?.notes || [];

  const instanceStatus = (instance?.StatusCode || '').toUpperCase();
  const isTerminatedOrCompleted = ['COMPLETED', 'WITHDRAWN', 'REJECTED', 'CANCELLED'].includes(instanceStatus);
  const isEffectivelyLocked = isTerminatedOrCompleted && !(instanceStatus === 'COMPLETED' && isChecklistEditMode);

  const isW2 = useMemo(() => {
    const rawType = String(instance?.EmploymentType || deployment?.EmploymentType || '').trim().toUpperCase();
    if (rawType.includes('W2') || rawType.includes('W-2')) return true;
    if (rawType.includes('C2C') || rawType.includes('CORP') || rawType.includes('1099')) return false;
    // Check placement chain for W2 Employer flag or lack of supplier
    if (placementChain?.some(c => c.IsW2Employer)) return true;
    if (!instance?.SupplierId && !placementChain?.some(c => c.ChainSide === 'SUPPLIER')) {
      // If candidate has actual pay rate and no supplier exists, it is direct/W2
      if (instance?.ActualPayRate || instance?.PayRate || instance?.SubmissionRate) return true;
    }
    return false;
  }, [instance?.EmploymentType, instance?.SupplierId, instance?.ActualPayRate, instance?.PayRate, instance?.SubmissionRate, deployment?.EmploymentType, placementChain]);
  const supplierStepCodes = ['MSA_SUPPLIER', 'SUPPLIER_DOC', 'SUPPLIER_PO'];

  // Resolve all Client Company IDs associated with this placement (including End Client, Direct Client, Job Client like TCS)
  const resolvedClientCompanyIds = useMemo(() => {
    const ids = new Set();
    if (instance?.ClientId) ids.add(Number(instance.ClientId));
    if (instance?.JobClientId) ids.add(Number(instance.JobClientId));

    (placementChain || []).forEach(c => {
      if ((c.ChainSide === 'CLIENT' || c.IsEndClient) && c.CompanyId) {
        ids.add(Number(c.CompanyId));
      }
    });

    // Check if any client name or job client name refers to TCS
    const allClientNames = [
      instance?.ClientName,
      instance?.JobClientName,
      instance?.ClientCompanyName,
      instance?.JobTitle,
      instance?.JobCode,
      ...(placementChain || []).filter(c => (c.ChainSide === 'CLIENT' || c.IsEndClient)).map(c => c.DisplayName || c.CompanyName || '')
    ].filter(Boolean).map(n => String(n).toLowerCase());

    const isTcsPlacement = allClientNames.some(n => n.includes('tcs') || n.includes('tata consultancy'));
    if (isTcsPlacement) {
      ids.add(6);
      ids.add(45);
      ids.add(12);
      ids.add(95);
    }

    return Array.from(ids);
  }, [instance, placementChain]);

  // Primary Client Company resolution (for commercial documents or single-company contexts)
  const clientCompany = (placementChain || []).find(c => c.ChainSide === 'CLIENT') || {};
  const resolvedCompanyId = (resolvedClientCompanyIds.length > 0 ? resolvedClientCompanyIds[0] : null) || instance?.ClientId || clientCompany?.CompanyId;

  // Custom step runtime state (persisted per instance)
  const [customStepStatusMap, setCustomStepStatusMap] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_onb_custom_step_status_${instanceId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [customStepAssigneeMap, setCustomStepAssigneeMap] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_onb_custom_step_assignee_${instanceId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [customStepDocsMap, setCustomStepDocsMap] = useState(() => {
    try {
      const saved = localStorage.getItem(`crm_onb_custom_step_docs_${instanceId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      const savedDocs = localStorage.getItem(`crm_onb_custom_step_docs_${instanceId}`);
      setCustomStepDocsMap(savedDocs ? JSON.parse(savedDocs) : {});
      const savedStatus = localStorage.getItem(`crm_onb_custom_step_status_${instanceId}`);
      setCustomStepStatusMap(savedStatus ? JSON.parse(savedStatus) : {});
      const savedAssignee = localStorage.getItem(`crm_onb_custom_step_assignee_${instanceId}`);
      setCustomStepAssigneeMap(savedAssignee ? JSON.parse(savedAssignee) : {});
    } catch {
      setCustomStepDocsMap({});
    }
  }, [instanceId]);

  // Handle ESC key to dismiss document preview modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && previewDoc) {
        setPreviewDoc(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewDoc]);

  // Automatically convert & load DOCX HTML preview via backend Mammoth when a .docx / .doc file is previewed
  useEffect(() => {
    if (!previewDoc) {
      setDocxHtml(null);
      setLoadingDocx(false);
      setDocxError(null);
      return;
    }

    const ext = (previewDoc.fileExt || '').toLowerCase();
    if (ext === 'docx' || ext === 'doc') {
      setLoadingDocx(true);
      setDocxError(null);
      setDocxHtml(null);

      axios.post(`${BASE_URL}/api/onboarding/documents/docx-preview`, {
        path: previewDoc.storagePath || previewDoc.fileUrl,
        fileUrl: previewDoc.fileUrl,
        fileName: previewDoc.fileName,
        docId: previewDoc.docId
      })
        .then(res => {
          if (res.data?.success && res.data?.html) {
            setDocxHtml(res.data.html);
          } else {
            setDocxError(res.data?.message || 'Could not parse Word document content.');
          }
        })
        .catch(err => {
          console.error('Error loading DOCX preview via Mammoth:', err);
          setDocxError(err.response?.data?.message || err.message || 'Failed to preview DOCX document.');
        })
        .finally(() => {
          setLoadingDocx(false);
        });
    }
  }, [previewDoc]);

  // Client customizations configured in Company Compliance Config (merged across all chain client companies)
  const clientCustomSteps = useMemo(() => {
    if (!resolvedClientCompanyIds.length) return [];
    try {
      const allSteps = [];
      const seenCodes = new Set();

      for (const compId of resolvedClientCompanyIds) {
        const saved = localStorage.getItem(`crm_comp_custom_steps_${compId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            parsed.forEach(step => {
              if (step && step.stepCode && !seenCodes.has(step.stepCode)) {
                seenCodes.add(step.stepCode);
                allSteps.push(step);
              }
            });
          }
        }
      }
      return allSteps;
    } catch {
      return [];
    }
  }, [resolvedClientCompanyIds]);

  const clientStepLabels = useMemo(() => {
    if (!resolvedClientCompanyIds.length) return {};
    try {
      const mergedLabels = {};
      for (const compId of resolvedClientCompanyIds) {
        const saved = localStorage.getItem(`crm_comp_step_labels_${compId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          Object.assign(mergedLabels, parsed);
        }
      }
      return mergedLabels;
    } catch {
      return {};
    }
  }, [resolvedClientCompanyIds]);

  const clientDeletedSteps = useMemo(() => {
    if (!resolvedClientCompanyIds.length) return [];
    try {
      const mergedDeleted = new Set();
      for (const compId of resolvedClientCompanyIds) {
        const saved = localStorage.getItem(`crm_comp_deleted_steps_${compId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            parsed.forEach(c => mergedDeleted.add(c));
          }
        }
      }
      return Array.from(mergedDeleted);
    } catch {
      return [];
    }
  }, [resolvedClientCompanyIds]);

  const visibleSteps = useMemo(() => {
    let list = steps
      .filter(s => !(isW2 && supplierStepCodes.includes(s.StepCode)))
      .filter(s => !clientDeletedSteps.includes(s.StepCode));

    list = list.map(s => {
      const override = clientStepLabels[s.StepCode];
      return override ? { ...s, StepName: override.title || s.StepName } : s;
    });

    (clientCustomSteps || []).forEach(cs => {
      if (!list.some(s => s.StepCode === cs.stepCode)) {
        let targetIdx = -1;
        if (cs.insertBeforeStep && list.some(s => s.StepCode === cs.insertBeforeStep)) {
          targetIdx = list.findIndex(s => s.StepCode === cs.insertBeforeStep);
        } else if (cs.insertAfterStep && list.some(s => s.StepCode === cs.insertAfterStep)) {
          targetIdx = list.findIndex(s => s.StepCode === cs.insertAfterStep) + 1;
        } else if (typeof cs.insertIndex === 'number' && cs.insertIndex >= 0 && cs.insertIndex <= list.length) {
          targetIdx = cs.insertIndex;
        } else {
          targetIdx = list.length;
        }

        const currStatus = customStepStatusMap[cs.stepCode] || '';
        const isTerm = currStatus === 'COMPLETED' || currStatus === 'WAIVED';
        const customDocInfo = customStepDocsMap[cs.stepCode] || null;
        const customAttList = Array.isArray(customDocInfo)
          ? customDocInfo.map((cd, idx) => ({
              CoreDocumentId: cd.documentId || `custom_doc_${idx}`,
              OriginalFileName: cd.originalFileName,
              DocumentStoragePath: cd.storageLocator,
              documentId: cd.documentId
            }))
          : (customDocInfo ? [{
              CoreDocumentId: customDocInfo.documentId || 'custom_doc_0',
              OriginalFileName: customDocInfo.originalFileName,
              DocumentStoragePath: customDocInfo.storageLocator,
              documentId: customDocInfo.documentId
            }] : []);

        const customStepObj = {
          OnboardingInstanceStepId: `custom_${cs.stepCode}`,
          OnboardingStepDefinitionId: null,
          StepCode: cs.stepCode,
          StepName: cs.title,
          StepSequence: targetIdx + 1,
          CurrentStatusCode: currStatus,
          IsTerminal: isTerm,
          CoreDocumentId: customAttList[0]?.CoreDocumentId || null,
          OriginalFileName: customAttList[0]?.OriginalFileName || null,
          DocumentStoragePath: customAttList[0]?.DocumentStoragePath || null,
          attachments: customAttList,
          items: [],
          availableStatusOptions: [
            { StatusCode: 'PENDING', StatusName: 'Pending' },
            { StatusCode: 'IN_PROGRESS', StatusName: 'In Progress' },
            { StatusCode: 'COMPLETED', StatusName: 'Completed', IsTerminal: true },
            { StatusCode: 'WAIVED', StatusName: 'Waived', IsTerminal: true }
          ],
          isCustomStep: true,
          AssignedToUserName: customStepAssigneeMap[cs.stepCode] || instance?.RecruiterName || 'Unassigned'
        };

        if (targetIdx >= 0 && targetIdx <= list.length) {
          list.splice(targetIdx, 0, customStepObj);
        } else {
          list.push(customStepObj);
        }
      }
    });

    list = list.map((s, idx) => ({ ...s, StepSequence: idx + 1 }));

    return list;
  }, [steps, isW2, clientDeletedSteps, clientStepLabels, clientCustomSteps, customStepStatusMap, customStepAssigneeMap, customStepDocsMap, instance]);

  // Helper to compute Margin dynamically based on Employment Type (C2C vs W2), expenses, and referrals
  // C2C Net: Rate After VMS - (Supplier Rate After VMS || Actual Pay Rate) - expenses - Referrals
  // W2 Net: Rate After VMS - (Actual Pay Rate || Compliance Pay Rate) - expenses - Referrals
  const calculateMargin = (
    rateAfterVms,
    supplierRateAfterVms,
    actualPayRate,
    empType,
    referralsList,
    referralFeeFallback,
    expensesVal,
    clientRateFallback = null,
    compliancePayRateFallback = null,
    vmsPctFallback = null,
    supplierRateFallback = null,
    supplierVmsPctFallback = null
  ) => {
    // 1. Resolve Bill Rate (Rate After VMS, or derive from Client Rate * (1 - VMS%))
    let rBill = parseFloat(rateAfterVms);
    if (isNaN(rBill) || rBill <= 0) {
      const cRate = parseFloat(clientRateFallback);
      const vPct = parseFloat(vmsPctFallback) || 0;
      if (!isNaN(cRate) && cRate > 0) {
        rBill = cRate * (1 - vPct / 100);
      }
    }
    const hasBillRate = !isNaN(rBill) && rBill > 0;
    const finalBillRate = hasBillRate ? rBill : 0;

    // 2. Resolve Deductions (Referrals & Expenses)
    let totalRef = 0;
    if (Array.isArray(referralsList) && referralsList.length > 0) {
      totalRef = referralsList.reduce((acc, r) => acc + (parseFloat(r.referralFee != null ? r.referralFee : r.ReferralFee) || 0), 0);
    } else if (referralFeeFallback != null && referralFeeFallback !== '') {
      totalRef = parseFloat(referralFeeFallback) || 0;
    }
    const exp = parseFloat(expensesVal) || 0;

    // 3. Resolve Cost Basis (C2C vs W2 with mutual fallbacks)
    const rawEmp = String(empType || '').trim().toUpperCase();
    const isW2Type = rawEmp.includes('W2') || rawEmp.includes('W-2') || Boolean(isW2);

    const actPay = parseFloat(actualPayRate);
    const compPay = parseFloat(compliancePayRateFallback);

    let sAfter = parseFloat(supplierRateAfterVms);
    if (isNaN(sAfter) || sAfter <= 0) {
      const sRate = parseFloat(supplierRateFallback);
      const svPct = parseFloat(supplierVmsPctFallback) || 0;
      if (!isNaN(sRate) && sRate > 0) {
        sAfter = sRate * (1 - svPct / 100);
      }
    }

    let costBasis = 0;
    if (isW2Type) {
      if (!isNaN(actPay) && actPay > 0) {
        costBasis = actPay;
      } else if (!isNaN(compPay) && compPay > 0) {
        costBasis = compPay;
      }
    } else {
      // For C2C: Prioritize Supplier Rate After VMS. If empty or 0, fallback to Candidate Actual Pay Rate
      if (!isNaN(sAfter) && sAfter > 0) {
        costBasis = sAfter;
      } else if (!isNaN(actPay) && actPay > 0) {
        costBasis = actPay;
      } else if (!isNaN(compPay) && compPay > 0) {
        costBasis = compPay;
      }
    }

    // 4. If all rates and deductions are zero/empty, return '0.00'
    if (!hasBillRate && costBasis === 0 && exp === 0 && totalRef === 0) {
      return '0.00';
    }

    // 5. If Bill Rate is not set (> 0), margin is '0.00' (prevents false negative deductions before billing)
    if (!hasBillRate) {
      return '0.00';
    }

    // 6. Margin = Bill Rate - Cost Basis - Expenses - Referrals
    const margin = finalBillRate - costBasis - exp - totalRef;
    return margin.toFixed(2);
  };

  const toggleStepAccordion = (stepId) => {
    setExpandedSteps(prev => ({
      ...prev,
      [stepId]: !prev[stepId]
    }));
  };

  const handleSubtaskStatusChange = async (stepId, itemId, newStatus) => {
    try {
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(s => {
          if (s.OnboardingInstanceStepId !== stepId) return s;
          const updatedItems = (s.items || []).map(it => {
            if (it.OnboardingStepItemId !== itemId) return it;
            const isTerminal = ['COMPLETED', 'FAILED', 'WAIVED', 'NOT_APPLICABLE'].includes(newStatus);
            return { ...it, CurrentStatusCode: newStatus, IsTerminal: isTerminal };
          });
          const completedCount = updatedItems.filter(it => it.IsTerminal && (it.CurrentStatusCode === 'COMPLETED' || it.CurrentStatusCode === 'WAIVED')).length;
          return { ...s, items: updatedItems, completedItemsCount: completedCount };
        });
        return { ...prev, steps: updatedSteps };
      });

      await axios.put(`${BASE_URL}/api/onboarding/steps/items/${itemId}`, {
        currentStatusCode: newStatus
      }, { withCredentials: true });
    } catch (err) {
      console.error('Error updating subtask status:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.response?.data?.message || err.message
      });
      fetchDetails(true);
    }
  };

  const handleSubtaskAssigneeChange = async (stepId, itemId, assigneeId, assigneeName) => {
    try {
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(s => {
          if (s.OnboardingInstanceStepId !== stepId) return s;
          const updatedItems = (s.items || []).map(it => {
            if (it.OnboardingStepItemId !== itemId) return it;
            return { ...it, AssignedToUserId: assigneeId, AssignedToUserName: assigneeName, AssignedToUserFullName: assigneeName };
          });
          return { ...s, items: updatedItems };
        });
        return { ...prev, steps: updatedSteps };
      });

      await axios.put(`${BASE_URL}/api/onboarding/steps/items/${itemId}`, {
        assignedToUserId: assigneeId || null,
        assignedToUserName: assigneeName || null
      }, { withCredentials: true });
    } catch (err) {
      console.error('Error updating subtask assignee:', err);
      fetchDetails(true);
    }
  };

  const handleAddSubtask = async (stepId) => {
    if (!newSubtaskName.trim()) return;
    setSavingSubtask(true);
    try {
      const selectedUser = recruiters.find(r => String(r.id || r.EmployeeId) === String(newSubtaskAssignee));
      const res = await axios.post(`${BASE_URL}/api/onboarding/steps/${stepId}/items`, {
        itemName: newSubtaskName.trim(),
        assignedToUserId: newSubtaskAssignee || null,
        assignedToUserName: selectedUser ? (selectedUser.FullName || selectedUser.name) : null
      }, { withCredentials: true });

      if (res.data?.success && res.data.item) {
        const newItem = {
          ...res.data.item,
          AssignedToUserFullName: selectedUser ? (selectedUser.FullName || selectedUser.name) : 'Unassigned'
        };
        setData(prev => {
          if (!prev) return prev;
          const updatedSteps = (prev.steps || []).map(s => {
            if (s.OnboardingInstanceStepId !== stepId) return s;
            const updatedItems = [...(s.items || []), newItem];
            return {
              ...s,
              items: updatedItems,
              totalItemsCount: updatedItems.length
            };
          });
          return { ...prev, steps: updatedSteps };
        });
      }
      setNewSubtaskName('');
      setNewSubtaskAssignee('');
      setAddingSubtaskStepId(null);
    } catch (err) {
      console.error('Error adding subtask:', err);
      Swal.fire({
        icon: 'error',
        title: 'Failed to Add Subtask',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setSavingSubtask(false);
    }
  };

  const handleDeleteSubtask = async (stepId, itemId) => {
    const confirm = await Swal.fire({
      title: 'Delete Subtask?',
      text: 'Are you sure you want to remove this ad-hoc subtask?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Yes, delete it'
    });
    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${BASE_URL}/api/onboarding/steps/items/${itemId}`, { withCredentials: true });
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(s => {
          if (s.OnboardingInstanceStepId !== stepId) return s;
          const updatedItems = (s.items || []).filter(it => it.OnboardingStepItemId !== itemId);
          const completedCount = updatedItems.filter(it => it.IsTerminal && (it.CurrentStatusCode === 'COMPLETED' || it.CurrentStatusCode === 'WAIVED')).length;
          return { ...s, items: updatedItems, totalItemsCount: updatedItems.length, completedItemsCount: completedCount };
        });
        return { ...prev, steps: updatedSteps };
      });
    } catch (err) {
      console.error('Error deleting subtask:', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: err.response?.data?.message || err.message
      });
    }
  };

  const handleDeleteStep = async (step) => {
    if (isEffectivelyLocked) {
      Swal.fire({
        icon: 'warning',
        title: 'Action Not Allowed',
        text: 'Cannot delete steps because checklist editing is currently locked.'
      });
      return;
    }

    const confirm = await Swal.fire({
      title: 'Delete Step?',
      html: `<div style="text-align: left; font-size: 13px; color: #334155; line-height: 1.5;">
        Are you sure you want to delete step <b>"${step.StepName || 'this step'}"</b>?
        <br/><br/>
        <span style="color: #64748b;">This will remove this step and its subtasks from the checklist.</span>
      </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete step',
      cancelButtonText: 'Cancel'
    });
    if (!confirm.isConfirmed) return;

    if (step.isCustomStep) {
      resolvedClientCompanyIds.forEach(compId => {
        try {
          const savedDel = localStorage.getItem(`crm_comp_deleted_steps_${compId}`);
          const parsedDel = savedDel ? JSON.parse(savedDel) : [];
          if (!parsedDel.includes(step.StepCode)) {
            parsedDel.push(step.StepCode);
            localStorage.setItem(`crm_comp_deleted_steps_${compId}`, JSON.stringify(parsedDel));
          }
        } catch (e) {}
      });
      Swal.fire({
        icon: 'success',
        title: 'Step Deleted',
        text: `Step "${step.StepName}" has been deleted.`,
        timer: 1600,
        showConfirmButton: false
      });
      if (onUpdated) onUpdated();
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.delete(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}`, {
        data: { userId: user.id }
      });
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).filter(s => s.OnboardingInstanceStepId !== step.OnboardingInstanceStepId);
        return { ...prev, steps: updatedSteps };
      });
      Swal.fire({
        icon: 'success',
        title: 'Step Deleted',
        text: `Step "${step.StepName}" has been deleted.`,
        timer: 1600,
        showConfirmButton: false
      });
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error deleting step:', err);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: err.response?.data?.message || err.message || 'Failed to delete step.'
      });
    }
  };

  const handleRemoveStepDocument = async (step) => {
    if (!step || !step.OnboardingInstanceStepId) return;

    const result = await Swal.fire({
      title: 'Remove Document?',
      text: `Are you sure you want to remove "${step.OriginalFileName || 'this document'}" from ${step.StepName || 'this step'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    if (step.isCustomStep) {
      const updatedDocs = { ...customStepDocsMap };
      delete updatedDocs[step.StepCode];
      setCustomStepDocsMap(updatedDocs);
      try {
        localStorage.setItem(`crm_onb_custom_step_docs_${instanceId}`, JSON.stringify(updatedDocs));
      } catch (e) {}
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        await axios.delete(`${BASE_URL}/api/onboarding/steps/0/document`, {
          data: { userId: user.id }
        });
      } catch (e) {}
      Swal.fire({
        icon: 'success',
        title: 'Document Removed',
        text: `Removed document from ${step.StepName || 'step'}.`,
        timer: 1600,
        showConfirmButton: false
      });
      if (onUpdated) onUpdated();
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.delete(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}/document`, {
        data: { userId: user.id }
      });

      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(s => {
          if (s.OnboardingInstanceStepId !== step.OnboardingInstanceStepId) return s;
          return { ...s, CoreDocumentId: null, OriginalFileName: null, DocumentStoragePath: null, FileSizeBytes: null };
        });
        return { ...prev, steps: updatedSteps };
      });

      Swal.fire({
        icon: 'success',
        title: 'Document Removed',
        text: `Removed document from ${step.StepName || 'step'}.`,
        timer: 1600,
        showConfirmButton: false
      });

      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error removing step document:', err);
      Swal.fire({
        icon: 'error',
        title: 'Removal Failed',
        text: err.response?.data?.message || err.message || 'Failed to remove document.'
      });
    }
  };

  const getFilteredRecruiters = (query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return recruiters.slice(0, 30);
    return recruiters.filter(r => {
      const name = (r.FullName || r.name || '').toLowerCase();
      const code = (r.EmployeeCode || '').toLowerCase();
      const email = (r.Email || '').toLowerCase();
      return name.includes(q) || code.includes(q) || email.includes(q);
    }).slice(0, 30);
  };

  const [rateFormData, setRateFormData] = useState({
    clientRate: '',
    vmsPct: '',
    rateAfterVms: '',
    supplierRate: '',
    supplierVmsPct: '',
    supplierRateAfterVms: '',
    supplierNetTermsDays: '',
    compliancePayRate: '',
    actualPayRate: '',
    expenses: '',
    margin: '',
    referralSource: '',
    referralSources: [],
    referrals: [],
    referralFee: '',
    notes: ''
  });
  const [rateReferralSearchText, setRateReferralSearchText] = useState('');

  const handleAddReferralSource = (clientObjOrName) => {
    let companyId = null;
    let name = '';
    let legal = '';

    if (typeof clientObjOrName === 'object' && clientObjOrName !== null) {
      companyId = clientObjOrName.companyId || clientObjOrName.CompanyId || null;
      name = (clientObjOrName.displayName || clientObjOrName.DisplayName || clientObjOrName.companyName || clientObjOrName.CompanyName || '').trim();
      legal = (clientObjOrName.companyName || clientObjOrName.CompanyName || '').trim();
    } else {
      name = (clientObjOrName || '').trim();
      const matched = (crmCompanies || []).find(c => 
        (c.DisplayName && c.DisplayName.toLowerCase() === name.toLowerCase()) ||
        (c.CompanyName && c.CompanyName.toLowerCase() === name.toLowerCase())
      );
      if (matched) {
        companyId = matched.CompanyId;
        legal = matched.CompanyName;
      }
    }

    if (!name) return;

    setRateFormData(prev => {
      const currentList = prev.referrals || [];
      if (currentList.some(r => (r.displayName || '').toLowerCase() === name.toLowerCase())) {
        return prev;
      }
      const newRef = {
        referralCompanyId: companyId,
        displayName: name,
        companyName: legal,
        referralFee: '',
        referralNetTermsDays: '',
        notes: ''
      };
      const updatedList = [...currentList, newRef];
      return {
        ...prev,
        referrals: updatedList,
        referralSources: updatedList.map(r => r.displayName),
        referralSource: updatedList.map(r => r.displayName).join(', ')
      };
    });

    setRateReferralSearchText('');
    setShowRateReferralDropdown(false);
  };

  const handleReferralItemChange = (index, field, value) => {
    setRateFormData(prev => {
      const updated = [...(prev.referrals || [])];
      if (!updated[index]) return prev;
      updated[index] = { ...updated[index], [field]: value };

      const totalFee = updated.reduce((sum, r) => {
        const val = parseFloat(r.referralFee);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);

      const nextMargin = calculateMargin(
        prev.rateAfterVms,
        prev.supplierRateAfterVms,
        prev.actualPayRate,
        instance?.EmploymentType,
        updated,
        totalFee,
        prev.expenses,
        prev.clientRate,
        prev.compliancePayRate,
        prev.vmsPct,
        prev.supplierRate,
        prev.supplierVmsPct
      );

      return {
        ...prev,
        referrals: updated,
        referralFee: totalFee > 0 ? totalFee.toFixed(2) : '',
        margin: nextMargin != null ? nextMargin : '0.00'
      };
    });
  };

  const handleRemoveReferralSource = (indexToRemove) => {
    setRateFormData(prev => {
      const updated = (prev.referrals || []).filter((_, idx) => idx !== indexToRemove);
      const totalFee = updated.reduce((sum, r) => {
        const val = parseFloat(r.referralFee);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);

      const nextMargin = calculateMargin(
        prev.rateAfterVms,
        prev.supplierRateAfterVms,
        prev.actualPayRate,
        instance?.EmploymentType,
        updated,
        totalFee,
        prev.expenses,
        prev.clientRate,
        prev.compliancePayRate,
        prev.vmsPct,
        prev.supplierRate,
        prev.supplierVmsPct
      );

      return {
        ...prev,
        referrals: updated,
        referralSources: updated.map(r => r.displayName),
        referralSource: updated.map(r => r.displayName).join(', '),
        referralFee: totalFee > 0 ? totalFee.toFixed(2) : '',
        margin: nextMargin != null ? nextMargin : '0.00'
      };
    });
  };

  const getFilteredCrmCompanies = (searchQuery) => {
    const q = (searchQuery || '').trim().toLowerCase();
    const list = (crmCompanies && crmCompanies.length > 0
      ? crmCompanies.map(c => {
        const disp = (c.DisplayName || c.CompanyName || '').trim();
        const legal = (c.CompanyName || '').trim();
        return {
          companyId: c.CompanyId,
          displayName: disp,
          companyName: legal && legal.toLowerCase() !== disp.toLowerCase() ? legal : (c.CompanyCode || '')
        };
      }).filter(item => item.displayName)
      : [
        { companyId: 6, displayName: 'TCS (US)', companyName: 'Tata Consultancy Services' },
        { companyId: 10, displayName: 'Wipro (US)', companyName: 'Wipro Limited' },
        { companyId: 11, displayName: 'Infosys (US)', companyName: 'Infosys Limited' },
        { companyId: 12, displayName: 'Capgemini (US)', companyName: 'Capgemini SE' },
        { companyId: 14, displayName: 'Cognizant (US)', companyName: 'Cognizant Technology Solutions' }
      ]
    );

    const unique = list.filter((item, idx, self) => self.findIndex(s => s.displayName.toLowerCase() === item.displayName.toLowerCase()) === idx);
    if (!q) return unique.slice(0, 40);
    return unique.filter(c =>
      c.displayName.toLowerCase().includes(q) ||
      (c.companyName && c.companyName.toLowerCase().includes(q))
    ).slice(0, 40);
  };

  // Candidate Notes State
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteCategory, setNewNoteCategory] = useState('GENERAL');
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteCategory, setEditingNoteCategory] = useState('GENERAL');
  const [editingNoteText, setEditingNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const fetchDetails = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/onboarding/instances/${instanceId}`);
      if (res.data.success) {
        const instData = res.data.data;
        setData(instData);

        // Prefill modal form data from instance
        if (instData.instance) {
          const inst = instData.instance;
          const dep = instData.deployment;
          const emp = instData.linkedEmployee;

          const resolvedStartDate = dep?.StartDate
            ? dep.StartDate.substring(0, 10)
            : (emp?.StartDate
              ? emp.StartDate.substring(0, 10)
              : (inst.JobStartDate ? inst.JobStartDate.substring(0, 10) : new Date().toISOString().substring(0, 10)));

          setEmpFormData({
            employeeCode: emp?.EmployeeCode || '',
            employeeType: emp?.EmployeeType || (inst.EmploymentType === 'C2C' ? 'C2C' : (inst.EmploymentType === 'INTERNAL' ? 'INTERNAL' : 'DEPLOYED')),
            startDate: resolvedStartDate,
            email: inst.CandidateEmail || '',
            phone: inst.CandidatePhone || '',
            entityId: inst.EntityId || 4
          });

          const cRateInit = dep?.ClientRate ?? inst.ClientRate ?? inst.SubmissionRate ?? '';
          const vPctInit = dep?.VmsPct ?? inst.VmsPct ?? '';
          let rAfterInit = dep?.RateAfterVms ?? inst.RateAfterVms ?? '';
          if (!rAfterInit && cRateInit) {
            const c = parseFloat(cRateInit);
            const v = parseFloat(vPctInit);
            if (!isNaN(c) && c > 0) {
              rAfterInit = !isNaN(v) && v > 0 ? (c * (1 - v / 100)).toFixed(2) : c.toFixed(2);
            }
          }

          const sRateInit = dep?.SupplierRate ?? inst.SupplierRate ?? '';
          const svPctInit = dep?.SupplierVmsPct ?? inst.SupplierVmsPct ?? '';
          let actRateInit = dep?.ActualPayRate ?? inst.ActualPayRate ?? '';
          if (!actRateInit && sRateInit) {
            const s = parseFloat(sRateInit);
            const sv = parseFloat(svPctInit);
            if (!isNaN(s) && s > 0) {
              actRateInit = !isNaN(sv) && sv > 0 ? (s * (1 - sv / 100)).toFixed(2) : s.toFixed(2);
            }
          }

          setDepFormData({
            placedJobTitle: dep?.PlacedJobTitle || inst.JobTitle || 'Consultant',
            startDate: dep?.StartDate ? dep.StartDate.substring(0, 10) : (inst.JobStartDate ? inst.JobStartDate.substring(0, 10) : new Date().toISOString().substring(0, 10)),
            endDate: dep?.EndDate ? dep.EndDate.substring(0, 10) : '',
            clientRate: cRateInit,
            vmsPct: vPctInit,
            rateAfterVms: rAfterInit,
            supplierRate: sRateInit,
            supplierVmsPct: svPctInit,
            supplierRateAfterVms: dep?.SupplierRateAfterVms ?? inst.SupplierRateAfterVms ?? (sRateInit && svPctInit ? (sRateInit * (1 - svPctInit / 100)).toFixed(2) : ''),
            supplierNetTermsDays: dep?.SupplierNetTermsDays ?? inst.SupplierNetTermsDays ?? '',
            compliancePayRate: dep?.CompliancePayRate ?? inst.CompliancePayRate ?? '',
            actualPayRate: actRateInit,
            margin: dep?.Margin ?? inst.Margin ?? '',
            referralSource: dep?.ReferralSource ?? inst.ReferralSource ?? '',
            referralFee: dep?.ReferralFee ?? inst.ReferralFee ?? '',
            entityId: dep?.EntityId || inst.EntityId || 4
          });

          const rawRef = inst.ReferralSource || dep?.ReferralSource || '';
          const activeRefs = Array.isArray(inst.referrals) && inst.referrals.length > 0
            ? inst.referrals.map(r => ({
                submissionReferralId: r.SubmissionReferralId,
                referralCompanyId: r.ReferralCompanyId,
                displayName: r.DisplayName || r.CompanyName,
                companyName: r.CompanyName,
                referralFee: r.ReferralFee != null ? r.ReferralFee : '',
                referralNetTermsDays: r.ReferralNetTermsDays != null ? r.ReferralNetTermsDays : '',
                notes: r.Notes || ''
              }))
            : (rawRef ? rawRef.split(',').map(s => ({
                referralCompanyId: null,
                displayName: s.trim(),
                companyName: '',
                referralFee: inst.ReferralFee || '',
                referralNetTermsDays: '',
                notes: ''
              })).filter(r => r.displayName) : []);

          const totalRefFee = activeRefs.length > 0 
            ? activeRefs.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0)
            : (parseFloat(inst.ReferralFee) || 0);

          const computedMargin = calculateMargin(
            inst.RateAfterVms ?? rAfterInit,
            inst.SupplierRateAfterVms ?? (sRateInit && svPctInit ? (sRateInit * (1 - svPctInit / 100)).toFixed(2) : ''),
            inst.ActualPayRate ?? inst.SubmissionRate ?? actRateInit,
            inst.EmploymentType,
            activeRefs,
            totalRefFee,
            inst.Expenses,
            inst.ClientRate ?? inst.SubmissionRate ?? cRateInit,
            inst.CompliancePayRate,
            inst.VmsPct ?? vPctInit,
            inst.SupplierRate ?? sRateInit,
            inst.SupplierVmsPct ?? svPctInit
          );

          const resolvedInitMargin = (computedMargin != null && computedMargin !== '')
            ? computedMargin
            : (inst.Margin != null && Number(inst.Margin) > 0 && Number(inst.RateAfterVms || rAfterInit || inst.ClientRate) > 0 ? Number(inst.Margin).toFixed(2) : '0.00');

          setRateFormData({
            clientRate: inst.ClientRate ?? inst.SubmissionRate ?? '',
            vmsPct: inst.VmsPct ?? '',
            rateAfterVms: inst.RateAfterVms ?? '',
            supplierRate: inst.SupplierRate ?? '',
            supplierVmsPct: inst.SupplierVmsPct ?? '',
            supplierRateAfterVms: inst.SupplierRateAfterVms ?? (inst.SupplierRate && inst.SupplierVmsPct ? (inst.SupplierRate * (1 - inst.SupplierVmsPct / 100)).toFixed(2) : ''),
            supplierNetTermsDays: inst.SupplierNetTermsDays ?? '',
            compliancePayRate: inst.CompliancePayRate ?? '',
            actualPayRate: inst.ActualPayRate ?? inst.SubmissionRate ?? '',
            expenses: inst.Expenses != null ? inst.Expenses : '',
            margin: resolvedInitMargin,
            referralSource: rawRef,
            referralSources: activeRefs.map(r => r.displayName),
            referrals: activeRefs,
            referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : (inst.ReferralFee ?? ''),
            notes: inst.RateNotes || inst.Notes || dep?.RateNotes || dep?.Notes || ''
          });
        }
      }
    } catch (err) {
      console.error('Error fetching onboarding details:', err);
      if (!silent) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Failed to load onboarding instance details.'
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    if (instanceId) {
      fetchDetails();
    }
  }, [instanceId, fetchDetails]);

  const fetchAssignees = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/onboarding/assignees`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data?.success) {
        setRecruiters(res.data.data || []);
      }
    } catch (e) {
      console.warn('Could not fetch assignees from hrm.Employee:', e.message);
    }
  }, []);

  useEffect(() => {
    fetchAssignees();
  }, [fetchAssignees]);

  // Fetch CRM companies (Clients & Suppliers) for Referral Source
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BASE_URL}/api/crm/companies`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (res.data.success && Array.isArray(res.data.data)) {
          setCrmCompanies(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching CRM companies for referral source:', err);
      }
    };
    fetchCompanies();
  }, []);

  // Debounced search for existing employees when typing into Employee Code
  useEffect(() => {
    if (!showEmpModal) return;
    const query = empFormData.employeeCode?.trim() || '';
    if (!query) {
      setEmpSearchResults([]);
      setShowEmpSuggestions(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingEmp(true);
        const res = await axios.get(`${BASE_URL}/api/onboarding/employees/search`, {
          params: { q: query, limit: 20 }
        });
        if (res.data.success) {
          setEmpSearchResults(res.data.data || []);
          setShowEmpSuggestions(true);
        }
      } catch (err) {
        console.error('Error searching employees:', err);
      } finally {
        setIsSearchingEmp(false);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [empFormData.employeeCode, showEmpModal]);

  const handleSelectEmployeeMatch = (emp) => {
    setSelectedLinkedEmp(emp);
    setEmpFormData(prev => ({
      ...prev,
      employeeCode: emp.EmployeeCode,
      employeeType: emp.EmployeeType || prev.employeeType,
      email: emp.Email || prev.email,
      phone: emp.Phone || prev.phone
    }));
    setShowEmpSuggestions(false);
  };

  // Helper to determine candidate stage index in 15-stage pipeline
  const getCandidateStageIndex = () => {
    if (!data || !data.instance) return 13;
    const instance = data.instance;

    // 1. If Candidate or Submission or Instance is HIRED -> Step 15: HIRED
    if (instance.CurrentStageCode === 'HIRED' || instance.StatusCode === 'HIRED') {
      return 15;
    }

    // 2. If Onboarding status is COMPLETED -> Step 14: ONBOARDING_COMPLETED
    if (instance.StatusCode === 'COMPLETED') {
      return 14;
    }

    // 3. If candidate has CurrentStageCode (from recruit.Submission), match it
    if (instance.CurrentStageCode) {
      const stageCode = instance.CurrentStageCode.toUpperCase().replace(/\s+/g, '_');
      const matched = RECRUITMENT_ONBOARDING_STAGES_15.find(s => s.code === stageCode || stageCode.includes(s.code) || s.code.includes(stageCode));
      if (matched) return matched.index;
    }

    // 4. For any active onboarding instance, default is ONBOARDING INITIATED (Step 13)
    return 13;
  };

  // Helper to format date badge for pipeline stage (2-line badge: "Sep 11", "03:47 AM")
  const formatStageDateBadge = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    const time = d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return {
      date: `${month} ${day < 10 ? '0' + day : day}`,
      time: time
    };
  };

  // Helper to get timestamp for a stage from stageHistory or instance timestamps
  const getStageDate = (stage) => {
    if (!data) return null;
    const currentStageIdx = getCandidateStageIndex();

    // Future unreached stages should never have a timestamp badge
    if (stage.index > currentStageIdx) {
      return null;
    }

    const stageHist = data.stageHistory || [];
    const instance = data.instance || {};

    // 1. Check direct match in stageHistory
    const histMatch = stageHist.find(h => {
      if (!h.StageCode) return false;
      const code = h.StageCode.toUpperCase().replace(/\s+/g, '_');
      return code === stage.code || code.includes(stage.code) || stage.code.includes(code);
    });
    if (histMatch?.ChangedAtUtc) {
      return histMatch.ChangedAtUtc;
    }

    // 2. Specific stage fallbacks from instance timestamps
    if (stage.code === 'NEW') {
      return instance.CreatedAtUtc || instance.InitiatedAtUtc;
    }
    if (stage.code === 'SUBMITTED_TO_CLIENT') {
      return instance.FirstSubmittedToClientAtUtc || instance.CreatedAtUtc;
    }
    if (stage.code === 'ONBOARDING_INITIATED') {
      return instance.InitiatedAtUtc || instance.CreatedAtUtc;
    }
    if (stage.code === 'ONBOARDING_COMPLETED') {
      return (instance.StatusCode === 'COMPLETED' || instance.CurrentStageCode === 'ONBOARDING_COMPLETED' || instance.CurrentStageCode === 'HIRED')
        ? instance.CompletedAtUtc
        : null;
    }
    if (stage.code === 'HIRED') {
      return (instance.StatusCode === 'HIRED' || instance.CurrentStageCode === 'HIRED')
        ? (instance.HiredAtUtc || null)
        : null;
    }

    return null;
  };

  // Handle Apply All Assignee
  const handleApplyAllAssignee = async () => {
    if (!selectedBulkAssignee.trim()) {
      Swal.fire({ icon: 'warning', title: 'Select Assignee', text: 'Please choose or type an assignee to apply to all steps.' });
      return;
    }

    try {
      setApplyingAll(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const matched = recruiters.find(r =>
        (r.FullName && r.FullName.toLowerCase() === selectedBulkAssignee.toLowerCase()) ||
        (r.name && r.name.toLowerCase() === selectedBulkAssignee.toLowerCase())
      );
      const assignedToUserId = matched?.EmployeeId || null;

      const res = await axios.put(`${BASE_URL}/api/onboarding/instances/${instanceId}/assign-all`, {
        assignedToUserId,
        assignedToUserName: selectedBulkAssignee,
        userId: user.id || null,
        userName: user.username || 'Team Member'
      });

      if (res.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Assigned to All Steps',
          text: `Successfully assigned all checklist steps to ${selectedBulkAssignee}!`,
          timer: 1600,
          showConfirmButton: false
        });

        // Optimistically update all steps and child subtasks in local state immediately
        setData(prev => {
          if (!prev) return prev;
          const updatedSteps = (prev.steps || []).map(s => ({
            ...s,
            AssignedToUserId: assignedToUserId,
            AssignedToUserName: selectedBulkAssignee,
            AssignedToUserFullName: selectedBulkAssignee,
            items: (s.items || []).map(it => ({
              ...it,
              AssignedToUserId: assignedToUserId,
              AssignedToUserName: selectedBulkAssignee,
              AssignedToUserFullName: selectedBulkAssignee
            }))
          }));
          return { ...prev, steps: updatedSteps };
        });
        setSubtaskAssigneeSearchQuery({});

        await fetchDetails();
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Error applying assignee to all steps:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to assign all steps.' });
    } finally {
      setApplyingAll(false);
    }
  };

  // Handle Rate Form changes with dynamic auto-calculation
  const handleRateChange = (field, value) => {
    setRateFormData(prev => {
      const next = { ...prev, [field]: value };
      // Auto-compute rateAfterVms if clientRate or vmsPct changes
      if (field === 'clientRate' || field === 'vmsPct') {
        const cRate = parseFloat(field === 'clientRate' ? value : next.clientRate);
        const vPct = parseFloat(field === 'vmsPct' ? value : next.vmsPct);
        if (!isNaN(cRate) && cRate > 0) {
          if (!isNaN(vPct) && vPct > 0) {
            next.rateAfterVms = (cRate * (1 - vPct / 100)).toFixed(2);
          } else {
            next.rateAfterVms = cRate.toFixed(2);
          }
        } else if (value === '' && field === 'clientRate') {
          next.rateAfterVms = '';
        }
      } else if (field === 'rateAfterVms') {
        const cRate = parseFloat(next.clientRate);
        const rAfter = parseFloat(value);
        if (!isNaN(cRate) && cRate > 0 && !isNaN(rAfter) && rAfter >= 0 && rAfter <= cRate) {
          next.vmsPct = (((cRate - rAfter) / cRate) * 100).toFixed(2);
        }
      }

      // Auto-compute supplierRateAfterVms if supplierRate or supplierVmsPct changes
      if (field === 'supplierRate' || field === 'supplierVmsPct') {
        const sRate = parseFloat(field === 'supplierRate' ? value : next.supplierRate);
        const sVms = parseFloat(field === 'supplierVmsPct' ? value : next.supplierVmsPct);
        if (!isNaN(sRate) && sRate > 0) {
          if (!isNaN(sVms) && sVms > 0) {
            next.supplierRateAfterVms = (sRate * (1 - sVms / 100)).toFixed(2);
          } else {
            next.supplierRateAfterVms = sRate.toFixed(2);
          }
        } else if (value === '' && field === 'supplierRate') {
          next.supplierRateAfterVms = '';
        }
      }

      // Auto-compute Margin on every rate change
      const empType = instance?.EmploymentType || '';
      const calcM = calculateMargin(
        next.rateAfterVms,
        next.supplierRateAfterVms,
        next.actualPayRate,
        empType,
        next.referrals,
        next.referralFee,
        next.expenses,
        next.clientRate,
        next.compliancePayRate,
        next.vmsPct,
        next.supplierRate,
        next.supplierVmsPct
      );
      next.margin = calcM != null ? calcM : '0.00';

      return next;
    });
  };

  // Handle Placement Deployment Form changes with dynamic auto-calculation
  const handleDepFieldChange = (field, value) => {
    setDepFormData(prev => {
      const next = { ...prev, [field]: value };

      // Client Rate & VMS calculations (Only RateAfterVms is auto-derived)
      if (field === 'clientRate' || field === 'vmsPct') {
        const cRate = parseFloat(field === 'clientRate' ? value : next.clientRate);
        const vPct = parseFloat(field === 'vmsPct' ? value : next.vmsPct);
        if (!isNaN(cRate) && cRate > 0) {
          if (!isNaN(vPct) && vPct > 0) {
            next.rateAfterVms = (cRate * (1 - vPct / 100)).toFixed(2);
          } else if (vPct === 0 || value === '' || next.vmsPct === '') {
            next.rateAfterVms = cRate.toFixed(2);
          }
        } else if (value === '' && field === 'clientRate') {
          next.rateAfterVms = '';
        }
      } else if (field === 'rateAfterVms') {
        const cRate = parseFloat(next.clientRate);
        const rAfter = parseFloat(value);
        if (!isNaN(cRate) && cRate > 0 && !isNaN(rAfter) && rAfter >= 0) {
          if (rAfter <= cRate) {
            const computedVms = ((cRate - rAfter) / cRate) * 100;
            next.vmsPct = parseFloat(computedVms.toFixed(2)).toString();
          }
        }
      }

      // Auto-compute supplierRateAfterVms if supplierRate or supplierVmsPct changes
      if (field === 'supplierRate' || field === 'supplierVmsPct') {
        const sRate = parseFloat(field === 'supplierRate' ? value : next.supplierRate);
        const sVms = parseFloat(field === 'supplierVmsPct' ? value : next.supplierVmsPct);
        if (!isNaN(sRate) && sRate > 0) {
          if (!isNaN(sVms) && sVms > 0) {
            next.supplierRateAfterVms = (sRate * (1 - sVms / 100)).toFixed(2);
          } else {
            next.supplierRateAfterVms = sRate.toFixed(2);
          }
        } else if (value === '' && field === 'supplierRate') {
          next.supplierRateAfterVms = '';
        }
      }

      // Auto-compute Margin
      const empType = instance?.EmploymentType || '';
      const calcM = calculateMargin(
        next.rateAfterVms,
        next.supplierRateAfterVms,
        next.actualPayRate,
        empType,
        null,
        next.referralFee,
        next.expenses,
        next.clientRate,
        next.compliancePayRate,
        next.vmsPct,
        next.supplierRate,
        next.supplierVmsPct
      );
      next.margin = calcM != null ? calcM : '0.00';

      return next;
    });
  };

  const computedGrossMargin = (() => {
    return calculateMargin(
      instance?.RateAfterVms || rateFormData?.rateAfterVms,
      instance?.SupplierRateAfterVms || rateFormData?.supplierRateAfterVms,
      instance?.ActualPayRate || rateFormData?.actualPayRate,
      instance?.EmploymentType,
      instance?.referrals || rateFormData?.referrals,
      instance?.ReferralFee || rateFormData?.referralFee,
      instance?.Expenses || rateFormData?.expenses,
      instance?.ClientRate || rateFormData?.clientRate,
      instance?.CompliancePayRate || rateFormData?.compliancePayRate,
      instance?.VmsPct || rateFormData?.vmsPct,
      instance?.SupplierRate || rateFormData?.supplierRate,
      instance?.SupplierVmsPct || rateFormData?.supplierVmsPct
    );
  })();

  // Submit Rate & Referral Changes
  const handleSaveRates = async () => {
    try {
      setSavingRates(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Include any typed text that hasn't been added with Enter
      let currentReferrals = [...(rateFormData.referrals || [])];
      if (rateReferralSearchText.trim() && !currentReferrals.some(r => (r.displayName || '').toLowerCase() === rateReferralSearchText.trim().toLowerCase())) {
        const matched = (crmCompanies || []).find(c => 
          (c.DisplayName && c.DisplayName.toLowerCase() === rateReferralSearchText.trim().toLowerCase()) ||
          (c.CompanyName && c.CompanyName.toLowerCase() === rateReferralSearchText.trim().toLowerCase())
        );
        currentReferrals.push({
          referralCompanyId: matched?.CompanyId || null,
          displayName: rateReferralSearchText.trim(),
          companyName: matched?.CompanyName || '',
          referralFee: '',
          notes: ''
        });
      }

      const res = await axios.put(`${BASE_URL}/api/onboarding/instances/${instanceId}/rate-referral`, {
        ...rateFormData,
        referrals: currentReferrals,
        referralSource: currentReferrals.map(r => r.displayName).join(', '),
        notes: rateFormData.notes || '',
        userId: user.id || null,
        userName: user.username || 'Team Member'
      });

      if (res.data?.success) {
        setIsEditingRates(false);
        setRateReferralSearchText('');
        Swal.fire({
          icon: 'success',
          title: 'Rates & Notes Updated!',
          text: 'Rate, Referral and Notes have been successfully updated.',
          timer: 1500,
          showConfirmButton: false
        });
        await fetchDetails(true);
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Error saving rate & referral details:', err);
      Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: err.response?.data?.message || 'Failed to update rate details.'
      });
    } finally {
      setSavingRates(false);
    }
  };

  // Callback when employee is created via EmployeeModal (+ Add Employee)
  const handleEmployeeCreated = async (newEmp) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await axios.post(`${BASE_URL}/api/onboarding/instances/${instanceId}/create-employee`, {
        ...empFormData,
        employeeId: newEmp.id || newEmp.EmployeeId,
        employeeCode: newEmp.employeeCode || newEmp.EmployeeCode,
        userId: user.id || null,
        userName: user.username || 'Team Member'
      });

      if (res.data.success) {
        setShowEmpModal(false);
        setShowAddEmployeeModal(false);
        await fetchDetails(true);
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Error linking newly created employee:', err);
    }
  };

  // Submit Action Step 85: Link Existing Employee
  const handleCreateEmployeeSubmit = async (e) => {
    e.preventDefault();

    if (!empFormData.employeeCode || !empFormData.employeeCode.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Employee Code Required',
        text: 'Please enter the Employee Code or click "+ Add Employee" above to create the employee in Timesheet Management first.',
        confirmButtonColor: '#0f766e'
      });
      return;
    }

    try {
      setSavingEmp(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await axios.post(`${BASE_URL}/api/onboarding/instances/${instanceId}/create-employee`, {
        ...empFormData,
        employeeCode: empFormData.employeeCode.trim(),
        userId: user.id || null,
        userName: user.username || 'Team Member'
      });

      if (res.data.success) {
        setShowEmpModal(false);
        const isUpdate = Boolean(data?.linkedEmployee || steps.some(s => s.StepCode === 'CREATE_LINK_EMP' && s.ResultRefId));
        Swal.fire({
          icon: 'success',
          title: isUpdate ? 'Employee Updated!' : 'Employee Linked!',
          text: isUpdate ? `Successfully updated Employee (${res.data.data.resultRefText})` : `Successfully linked Employee (${res.data.data.resultRefText})`,
          timer: 1800,
          showConfirmButton: false
        });
        await fetchDetails();
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Error linking employee:', err);
      Swal.fire({
        icon: 'warning',
        title: 'Employee Not Found',
        text: err.response?.data?.message || 'Employee record not found in Timesheet Management. Please click "+ Add Employee" above to add the employee first before linking.',
        confirmButtonColor: '#0f766e'
      });
    } finally {
      setSavingEmp(false);
    }
  };

  // Submit Action Step 87: Create Deployment
  const handleCreateDeploymentSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingDep(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const sanitizeNum = (v) => (v !== undefined && v !== null && String(v).trim() !== '' && !isNaN(v)) ? parseFloat(v) : null;

      const payload = {
        placedJobTitle: depFormData.placedJobTitle,
        startDate: depFormData.startDate,
        endDate: depFormData.endDate ? depFormData.endDate : null,
        clientRate: sanitizeNum(depFormData.clientRate),
        vmsPct: sanitizeNum(depFormData.vmsPct),
        rateAfterVms: sanitizeNum(depFormData.rateAfterVms),
        supplierRate: sanitizeNum(depFormData.supplierRate),
        supplierVmsPct: sanitizeNum(depFormData.supplierVmsPct),
        supplierRateAfterVms: sanitizeNum(depFormData.supplierRateAfterVms),
        supplierNetTermsDays: sanitizeNum(depFormData.supplierNetTermsDays),
        compliancePayRate: sanitizeNum(depFormData.compliancePayRate),
        actualPayRate: sanitizeNum(depFormData.actualPayRate),
        expenses: sanitizeNum(depFormData.expenses),
        margin: sanitizeNum(depFormData.margin),
        referralSource: depFormData.referralSource ? String(depFormData.referralSource).trim() : null,
        referralFee: sanitizeNum(depFormData.referralFee),
        entityId: depFormData.entityId || null,
        userId: user.id || null,
        userName: user.username || 'Team Member'
      };

      const res = await axios.post(`${BASE_URL}/api/onboarding/instances/${instanceId}/create-deployment`, payload);

      if (res.data.success) {
        setShowDepModal(false);
        const isUpdate = Boolean(data?.deployment || steps.some(s => s.StepCode === 'CREATE_DEPLOYMENT' && s.ResultRefId));
        Swal.fire({
          icon: 'success',
          title: isUpdate ? 'Deployment Updated!' : 'Deployment Created!',
          text: isUpdate ? `Deployment #${res.data.data.deploymentId} updated successfully!` : `Successfully created Deployment #${res.data.data.deploymentId} and locked placement chain!`,
          timer: 1800,
          showConfirmButton: false
        });
        await fetchDetails();
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Error creating deployment:', err);
      Swal.fire({ icon: 'error', title: 'Deployment Creation Failed', text: err.response?.data?.message || 'Failed to create deployment.' });
    } finally {
      setSavingDep(false);
    }
  };

  // Handle step status change
  const handleStatusChange = async (step, newStatusCode) => {
    if (step.isCustomStep) {
      const updated = { ...customStepStatusMap, [step.StepCode]: newStatusCode };
      setCustomStepStatusMap(updated);
      try {
        localStorage.setItem(`crm_onb_custom_step_status_${instanceId}`, JSON.stringify(updated));
      } catch (e) {}
      if (onUpdated) onUpdated();
      return;
    }

    try {
      setSavingStepId(step.OnboardingInstanceStepId);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await axios.patch(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}/status`, {
        statusCode: newStatusCode,
        userId: user.id || localStorage.getItem('userId') || null,
        userName: user.username || localStorage.getItem('username') || 'Team Member'
      });

      if (res.data.success) {
        await fetchDetails();
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      console.error('Error updating status:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.response?.data?.message || 'Failed to update step status.'
      });
    } finally {
      setSavingStepId(null);
    }
  };

  // Handle owner assignment change
  const handleAssigneeChange = async (step, assignedName) => {
    if (step.isCustomStep) {
      const updated = { ...customStepAssigneeMap, [step.StepCode]: assignedName };
      setCustomStepAssigneeMap(updated);
      try {
        localStorage.setItem(`crm_onb_custom_step_assignee_${instanceId}`, JSON.stringify(updated));
      } catch (e) {}
      return;
    }

    try {
      setSavingStepId(step.OnboardingInstanceStepId);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const matched = recruiters.find(r =>
        (r.FullName && r.FullName.toLowerCase() === (assignedName || '').toLowerCase()) ||
        (r.name && r.name.toLowerCase() === (assignedName || '').toLowerCase())
      );
      const assignedToUserId = matched?.EmployeeId || null;

      // Optimistically update both the step AND all its child subtasks in local state
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(s => {
          if (s.OnboardingInstanceStepId !== step.OnboardingInstanceStepId) return s;
          return {
            ...s,
            AssignedToUserId: assignedToUserId,
            AssignedToUserName: assignedName,
            items: (s.items || []).map(it => ({
              ...it,
              AssignedToUserId: assignedToUserId,
              AssignedToUserName: assignedName,
              AssignedToUserFullName: assignedName
            }))
          };
        });
        return { ...prev, steps: updatedSteps };
      });

      // Clear any typed subtask search query cache for items under this step
      setSubtaskAssigneeSearchQuery(prev => {
        const next = { ...prev };
        (step.items || []).forEach(it => {
          delete next[it.OnboardingStepItemId];
        });
        return next;
      });

      await axios.patch(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}/assign`, {
        assignedToUserId,
        assignedToUserName: assignedName,
        userId: user.id || null,
        userName: user.username || 'Team Member'
      });
      await fetchDetails(true);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error assigning step:', err);
    } finally {
      setSavingStepId(null);
    }
  };

  // Handle document file attachment for parent step (supports single file and multi-file selection)
  const handleStepFileUpload = async (step, fileOrFiles) => {
    if (!step || !fileOrFiles) return;

    const files = fileOrFiles instanceof FileList || Array.isArray(fileOrFiles)
      ? Array.from(fileOrFiles)
      : [fileOrFiles];

    if (files.length === 0) return;

    try {
      setSavingStepId(step.OnboardingInstanceStepId);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const uploadStepId = step.isCustomStep || isNaN(Number(step.OnboardingInstanceStepId))
        ? 0
        : step.OnboardingInstanceStepId;

      let successCount = 0;
      const failedFiles = [];
      let currentCustomDocs = Array.isArray(customStepDocsMap[step.StepCode])
        ? [...customStepDocsMap[step.StepCode]]
        : (customStepDocsMap[step.StepCode] ? [{ ...customStepDocsMap[step.StepCode] }] : []);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formData = new FormData();
          formData.append('file', file);
          if (user.id) formData.append('userId', user.id);

          const res = await axios.post(`${BASE_URL}/api/onboarding/steps/${uploadStepId}/upload-document`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (step.isCustomStep) {
            const docData = {
              documentId: res.data?.documentId || Date.now() + i,
              originalFileName: res.data?.originalFileName || file.name,
              storageLocator: res.data?.storageLocator || `/uploads/company-docs/${file.name}`
            };
            currentCustomDocs.push(docData);
          }
          successCount++;
        } catch (err) {
          console.error(`Error uploading step document "${file.name}":`, err);
          const isTooLarge = err.response?.status === 413;
          failedFiles.push({
            name: file.name,
            reason: isTooLarge ? 'File too large (exceeds server limit)' : (err.response?.data?.message || err.message || 'Upload failed')
          });
        }
      }

      if (step.isCustomStep && successCount > 0) {
        const updatedDocs = { ...customStepDocsMap, [step.StepCode]: currentCustomDocs };
        setCustomStepDocsMap(updatedDocs);
        try {
          localStorage.setItem(`crm_onb_custom_step_docs_${instanceId}`, JSON.stringify(updatedDocs));
        } catch (e) {}
      }

      if (successCount > 0) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: successCount === 1 ? 'Document Uploaded' : `${successCount} Documents Uploaded`,
          text: successCount === 1 ? `"${files[0].name}"` : `Successfully attached ${successCount} document(s) to ${step.StepName}`,
          timer: 1600,
          showConfirmButton: false
        });
      }

      if (failedFiles.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: `${failedFiles.length} Upload(s) Failed`,
          html: `<div style="text-align: left; font-size: 0.82rem;">${failedFiles.map(f => `<div><strong>${f.name}</strong>: ${f.reason}</div>`).join('')}</div>`
        });
      }

      if (!step.isCustomStep) {
        await fetchDetails(true);
      }
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error in handleStepFileUpload:', err);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: err.response?.data?.message || err.message || 'Failed to upload document.'
      });
    } finally {
      setSavingStepId(null);
    }
  };

  // Handle removing a specific individual attachment from a step (multi-attachment support)
  const handleRemoveStepIndividualAttachment = async (step, att) => {
    if (!step || !att) return;

    const docName = att.OriginalFileName || att.originalFileName || 'this document';
    const result = await Swal.fire({
      title: 'Remove Attachment?',
      text: `Are you sure you want to remove "${docName}" from ${step.StepName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setSavingStepId(step.OnboardingInstanceStepId);

      if (step.isCustomStep) {
        const existingDocs = Array.isArray(customStepDocsMap[step.StepCode])
          ? customStepDocsMap[step.StepCode]
          : (customStepDocsMap[step.StepCode] ? [customStepDocsMap[step.StepCode]] : []);
        const updatedList = existingDocs.filter(d => 
          (d.documentId && att.documentId && d.documentId !== att.documentId) ||
          (d.originalFileName !== (att.OriginalFileName || att.originalFileName))
        );
        const updatedDocs = { ...customStepDocsMap, [step.StepCode]: updatedList };
        setCustomStepDocsMap(updatedDocs);
        try {
          localStorage.setItem(`crm_onb_custom_step_docs_${instanceId}`, JSON.stringify(updatedDocs));
        } catch (e) {}
      } else {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const docId = att.CoreDocumentId || att.documentId;
        await axios.delete(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}/documents/${docId}`, {
          data: { userId: user.id || null }
        });
        await fetchDetails(true);
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Attachment Removed',
        timer: 1200,
        showConfirmButton: false
      });
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error removing step attachment:', err);
      Swal.fire({
        icon: 'error',
        title: 'Removal Failed',
        text: err.response?.data?.message || err.message || 'Failed to remove document.'
      });
    } finally {
      setSavingStepId(null);
    }
  };

  // Handle open commercial doc modal (MSA / PO)
  const handleOpenCommercialDocModal = (step) => {
    const isClient = step.StepCode === 'MSA_CLIENT' || step.StepCode === 'CLIENT_PO' || (step.StepTitle && step.StepTitle.toLowerCase().includes('client'));
    
    // Check placement chain nodes
    const pChain = data?.placementChain || [];
    const clientNode = pChain.find(c => (c.ChainSide === 'CLIENT' || c.IsEndClient) && !c.IsW2Employer && c.ChainSide !== 'SUPPLIER');
    const supplierNode = pChain.find(c => c.ChainSide === 'SUPPLIER' || c.IsW2Employer);

    let resolvedCompanyId = null;
    let resolvedCompanyName = '';

    if (isClient) {
      resolvedCompanyId = clientNode?.CompanyId || instance?.ClientId || null;
      resolvedCompanyName = clientNode?.DisplayName || clientNode?.CompanyName || instance?.ClientCompanyName || instance?.ClientName || 'Client';
    } else {
      resolvedCompanyId = supplierNode?.CompanyId || instance?.SupplierId || null;
      resolvedCompanyName = supplierNode?.DisplayName || supplierNode?.CompanyName || instance?.ContractorCompanyName || instance?.SupplierName || 'Supplier';
    }

    const docTypeCode = (step.StepCode && (step.StepCode.includes('PO') || step.StepCode.endsWith('_PO'))) || (step.StepTitle && step.StepTitle.toUpperCase().includes('PO')) ? 'PO' : 'MSA';

    setCommercialDocModal({
      open: true,
      step,
      typeCode: docTypeCode,
      side: isClient ? 'CLIENT' : 'SUPPLIER',
      companyId: resolvedCompanyId,
      companyName: resolvedCompanyName
    });
  };

  // Handle commercial document linked
  const handleCommercialDocLinked = async (doc) => {
    try {
      const step = commercialDocModal.step;
      if (step && doc) {
        // If the document has a final document or draft, update the step status to COMPLETED or PENDING_REVIEW
        const targetStatus = (doc.StatusCode === 'EXECUTED' || doc.StatusCode === 'ACTIVE') ? 'COMPLETED' : 'IN_PROGRESS';
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const refId = doc.CommercialDocumentId || doc.DocumentHeaderId || doc.DocumentId || doc.id || null;
        const refText = [doc.DocumentNumber, doc.Title].filter(Boolean).join(' - ') || (doc.DocumentNumber ? doc.DocumentNumber : 'Agreement');

        await axios.patch(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}/status`, {
          statusCode: targetStatus,
          userId: user.id || null,
          userName: user.username || 'Team Member',
          resultRefId: refId,
          resultRefText: refText
        });
      }
      Swal.fire({
        icon: 'success',
        title: 'Agreement Linked',
        text: `Commercial Document ${doc?.DocumentNumber || ''} successfully linked to ${commercialDocModal.step?.StepName || 'step'}.`,
        timer: 2000,
        showConfirmButton: false
      });
      await fetchDetails();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error linking commercial document to step:', err);
    } finally {
      setCommercialDocModal(prev => ({ ...prev, open: false, step: null }));
    }
  };

  // Handle document file attachment for subtask (supports single file and multi-file selection)
  const handleSubtaskFileUpload = async (sub, fileOrFiles) => {
    if (!sub || !fileOrFiles) return;

    const files = fileOrFiles instanceof FileList || Array.isArray(fileOrFiles)
      ? Array.from(fileOrFiles)
      : [fileOrFiles];

    if (files.length === 0) return;

    setUploadingSubtaskId(sub.OnboardingStepItemId);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      let latestAttachments = sub.attachments || [];
      let lastUploadedDocId = sub.CoreDocumentId;
      let lastUploadedOrigName = sub.OriginalFileName;
      let lastUploadedLocator = sub.DocumentStoragePath;
      let successCount = 0;
      const failedFiles = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formData = new FormData();
          formData.append('file', file);
          if (user.id) formData.append('userId', user.id);

          const res = await axios.post(`${BASE_URL}/api/onboarding/subtasks/${sub.OnboardingStepItemId}/upload-document`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          lastUploadedDocId = res.data?.documentId || lastUploadedDocId;
          lastUploadedOrigName = res.data?.originalFileName || file.name;
          lastUploadedLocator = res.data?.storageLocator || lastUploadedLocator;

          if (res.data?.attachments && res.data.attachments.length > 0) {
            latestAttachments = res.data.attachments;
          } else if (res.data?.documentId) {
            latestAttachments = [
              ...latestAttachments,
              {
                CoreDocumentId: res.data.documentId,
                OriginalFileName: res.data.originalFileName || file.name,
                DocumentStoragePath: res.data.storageLocator
              }
            ];
          }
          successCount++;
        } catch (err) {
          console.error(`Error uploading subtask document "${file.name}":`, err);
          const is413 = err.response?.status === 413;
          failedFiles.push({
            name: file.name,
            reason: is413 ? 'File too large (exceeds server limit)' : (err.response?.data?.message || err.message || 'Upload failed')
          });
        }
      }

      // 1. Immediately update local subtask state in-place so file pills appear without any flicker or refresh
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(st => {
          if (st.OnboardingInstanceStepId !== sub.OnboardingInstanceStepId) return st;
          const updatedItems = (st.items || []).map(it => {
            if (it.OnboardingStepItemId !== sub.OnboardingStepItemId) return it;
            const finalAtts = latestAttachments.length > 0 ? latestAttachments : (it.attachments || []);
            const lastAtt = finalAtts[finalAtts.length - 1];
            return {
              ...it,
              CoreDocumentId: lastAtt?.CoreDocumentId || lastUploadedDocId || it.CoreDocumentId,
              OriginalFileName: lastAtt?.OriginalFileName || lastUploadedOrigName || it.OriginalFileName,
              DocumentStoragePath: lastAtt?.DocumentStoragePath || lastUploadedLocator || it.DocumentStoragePath,
              attachments: finalAtts
            };
          });
          return { ...st, items: updatedItems };
        });
        return { ...prev, steps: updatedSteps };
      });

      // 2. Ensure the parent step accordion remains open so the dropdown does not automatically close
      if (sub.OnboardingInstanceStepId) {
        setExpandedSteps(prev => ({
          ...prev,
          [sub.OnboardingInstanceStepId]: true
        }));
      }

      if (successCount > 0) {
        Swal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: successCount === 1 ? 'Document Uploaded' : `${successCount} Documents Uploaded`,
          text: failedFiles.length > 0
            ? `Uploaded ${successCount} file(s). Failed: ${failedFiles.map(f => (typeof f === 'string' ? f : f.name)).join(', ')}`
            : (files.length === 1 ? `"${files[0].name}"` : undefined),
          timer: 1500,
          showConfirmButton: false
        });
      }

      if (failedFiles.length > 0 && successCount === 0) {
        const any413 = failedFiles.some(f => typeof f === 'object' && f.reason.includes('too large'));
        Swal.fire({
          icon: 'error',
          title: any413 ? 'File Size Exceeded (413)' : 'Upload Failed',
          html: `
            <div style="text-align: left; font-size: 0.88rem;">
              <p style="margin-bottom: 6px;">The following file(s) could not be uploaded:</p>
              <ul style="margin: 6px 0; padding-left: 20px;">
                ${failedFiles.map(f => `<li><strong>${typeof f === 'string' ? f : f.name}</strong>: <span style="color: #dc2626;">${typeof f === 'string' ? 'Upload failed' : f.reason}</span></li>`).join('')}
              </ul>
              ${any413 ? '<p style="margin-top: 10px; color: #4b5563; font-size: 0.8rem;"><em>Note: Photos and scanned reports can be several megabytes in size. If this happens, please compress or downscale the file, or increase Nginx <code>client_max_body_size</code>.</em></p>' : ''}
            </div>
          `
        });
      } else if (failedFiles.length > 0 && successCount > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Partial Upload Complete',
          html: `
            <div style="text-align: left; font-size: 0.88rem;">
              <p style="margin-bottom: 6px;">Uploaded <strong>${successCount}</strong> file(s), but <strong>${failedFiles.length}</strong> file(s) failed:</p>
              <ul style="margin: 6px 0; padding-left: 20px;">
                ${failedFiles.map(f => `<li><strong>${typeof f === 'string' ? f : f.name}</strong>: <span style="color: #dc2626;">${typeof f === 'string' ? 'Upload failed' : f.reason}</span></li>`).join('')}
              </ul>
            </div>
          `
        });
      }

      await fetchDetails(true);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error in subtask batch file upload:', err);
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: err.response?.data?.message || err.message || 'Failed to upload document.'
      });
    } finally {
      setUploadingSubtaskId(null);
    }
  };

  // Handle removing document file attachment from subtask
  const handleRemoveSubtaskDocument = async (sub) => {
    if (!sub || !sub.OnboardingStepItemId) return;

    const result = await Swal.fire({
      title: 'Remove Document?',
      text: `Are you sure you want to remove "${sub.OriginalFileName || 'this document'}" from ${sub.ItemName || 'subtask'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.delete(`${BASE_URL}/api/onboarding/subtasks/${sub.OnboardingStepItemId}/document`, {
        data: { userId: user.id }
      });

      // 1. Immediately update local subtask state in-place to clear document
      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(st => {
          if (st.OnboardingInstanceStepId !== sub.OnboardingInstanceStepId) return st;
          const updatedItems = (st.items || []).map(it => {
            if (it.OnboardingStepItemId !== sub.OnboardingStepItemId) return it;
            return {
              ...it,
              CoreDocumentId: null,
              OriginalFileName: null,
              DocumentStoragePath: null,
              FileSizeBytes: null
            };
          });
          return { ...st, items: updatedItems };
        });
        return { ...prev, steps: updatedSteps };
      });

      // 2. Ensure the parent step accordion remains open
      if (sub.OnboardingInstanceStepId) {
        setExpandedSteps(prev => ({
          ...prev,
          [sub.OnboardingInstanceStepId]: true
        }));
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Document Removed',
        timer: 700,
        showConfirmButton: false
      });

      await fetchDetails(true);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error removing subtask document:', err);
      Swal.fire({
        icon: 'error',
        title: 'Removal Failed',
        text: err.response?.data?.message || err.message || 'Failed to remove document.'
      });
    }
  };

  // Handle removing a specific individual attachment from subtask (multi-attachment support)
  const handleRemoveIndividualAttachment = async (sub, att) => {
    if (!sub || !att) return;

    const result = await Swal.fire({
      title: 'Remove Attachment?',
      text: `Are you sure you want to remove "${att.OriginalFileName || 'this attachment'}" from ${sub.ItemName || 'subtask'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      let updatedAttachments = [];

      if (att.CoreDocumentId) {
        try {
          const res = await axios.delete(`${BASE_URL}/api/onboarding/subtasks/${sub.OnboardingStepItemId}/documents/${att.CoreDocumentId}`, {
            data: { userId: user.id }
          });
          if (res.data?.attachments && Array.isArray(res.data.attachments)) {
            updatedAttachments = res.data.attachments;
          } else {
            updatedAttachments = (sub.attachments || []).filter(a => {
              if (att.CoreDocumentId && a.CoreDocumentId) {
                return a.CoreDocumentId !== att.CoreDocumentId;
              }
              if (att.OnboardingStepItemDocumentId && a.OnboardingStepItemDocumentId) {
                return a.OnboardingStepItemDocumentId !== att.OnboardingStepItemDocumentId;
              }
              return a !== att;
            });
          }
        } catch (subErr) {
          if (!sub.attachments || sub.attachments.length <= 1) {
            await axios.delete(`${BASE_URL}/api/onboarding/subtasks/${sub.OnboardingStepItemId}/document`, {
              data: { userId: user.id }
            });
            updatedAttachments = [];
          } else {
            throw subErr;
          }
        }
      } else {
        await axios.delete(`${BASE_URL}/api/onboarding/subtasks/${sub.OnboardingStepItemId}/document`, {
          data: { userId: user.id }
        });
        updatedAttachments = [];
      }

      const latestAtt = updatedAttachments[updatedAttachments.length - 1];

      setData(prev => {
        if (!prev) return prev;
        const updatedSteps = (prev.steps || []).map(st => {
          if (st.OnboardingInstanceStepId !== sub.OnboardingInstanceStepId) return st;
          const updatedItems = (st.items || []).map(it => {
            if (it.OnboardingStepItemId !== sub.OnboardingStepItemId) return it;
            return {
              ...it,
              CoreDocumentId: latestAtt?.CoreDocumentId || null,
              OriginalFileName: latestAtt?.OriginalFileName || null,
              DocumentStoragePath: latestAtt?.DocumentStoragePath || null,
              FileSizeBytes: latestAtt?.FileSizeBytes || null,
              attachments: updatedAttachments
            };
          });
          return { ...st, items: updatedItems };
        });
        return { ...prev, steps: updatedSteps };
      });

      if (sub.OnboardingInstanceStepId) {
        setExpandedSteps(prev => ({
          ...prev,
          [sub.OnboardingInstanceStepId]: true
        }));
      }

      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Attachment Removed',
        timer: 1200,
        showConfirmButton: false
      });

      await fetchDetails(true);
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error removing attachment:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to remove attachment.'
      });
    }
  };

  const handleFileUpload = handleStepFileUpload;

  // 1-Click Link CRM MSA to MSA Step
  const handleLinkCrmMsa = async (step, crmDocId) => {
    try {
      setSavingStepId(step.OnboardingInstanceStepId);
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      await axios.patch(`${BASE_URL}/api/onboarding/steps/${step.OnboardingInstanceStepId}/status`, {
        statusCode: 'EXISTED',
        coreDocumentId: crmDocId,
        notes: 'Linked to signed CRM MSA Document #' + crmDocId,
        userId: user.id || null,
        userName: user.username || 'Team Member'
      });

      Swal.fire({
        icon: 'success',
        title: 'CRM MSA Linked',
        text: 'Successfully linked existing signed MSA from CRM!',
        timer: 1800,
        showConfirmButton: false
      });

      await fetchDetails();
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error('Error linking CRM MSA:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSavingStepId(null);
    }
  };

  // Complete Onboarding Trigger
  const handleCompleteClick = async (isForced = false) => {
    try {
      setCompleting(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await axios.post(`${BASE_URL}/api/onboarding/instances/${instanceId}/complete`, {
        force: isForced,
        notes: completionNotes,
        userId: user.id || localStorage.getItem('userId') || null,
        userName: user.username || localStorage.getItem('username') || 'Onboarding Lead'
      });

      if (res.data.success) {
        setShowGuardModal(false);
        Swal.fire({
          icon: 'success',
          title: 'Onboarding Completed!',
          html: `
            <div style="text-align: left; padding: 8px 12px; font-size: 0.95rem;">
              <p><strong>Candidate:</strong> ${instance?.CandidateFirstName} ${instance?.CandidateLastName}</p>
              <p style="color: #059669; margin-top: 8px;">✓ Submission advanced to ONBOARDING_COMPLETED.</p>
            </div>
          `,
          confirmButtonColor: '#10b981'
        });
        await fetchDetails();
        if (onUpdated) onUpdated();
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.guardFailed) {
        setOpenStepsList(err.response.data.openSteps || []);
        setShowGuardModal(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Completion Failed',
          text: err.response?.data?.message || 'Failed to complete onboarding.'
        });
      }
    } finally {
      setCompleting(false);
    }
  };

  // Outcome Handlers (Reject / Withdrawn)
  const handleOpenOutcomeModal = (type) => {
    setOutcomeModal({
      open: true,
      type,
      notes: '',
      submitting: false
    });
  };

  const handleCloseOutcomeModal = () => {
    if (outcomeModal.submitting) return;
    setOutcomeModal({
      open: false,
      type: 'WITHDRAWN',
      notes: '',
      submitting: false
    });
  };

  const handleConfirmOutcome = async () => {
    const isWithdraw = outcomeModal.type === 'WITHDRAWN';
    const actionLabel = isWithdraw ? 'Withdrawal' : 'Rejection';
    const notesText = outcomeModal.notes.trim();

    if (!notesText) {
      Swal.fire({
        icon: 'warning',
        title: 'Notes Required',
        text: `Please enter the reason or remarks for ${actionLabel.toLowerCase()}.`
      });
      return;
    }

    try {
      setOutcomeModal(prev => ({ ...prev, submitting: true }));
      const token = localStorage.getItem('token');
      const endpoint = isWithdraw
        ? `${BASE_URL}/api/onboarding/instances/${instanceId}/withdraw`
        : `${BASE_URL}/api/onboarding/instances/${instanceId}/reject`;

      const res = await axios.post(endpoint, { notes: notesText }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.success) {
        handleCloseOutcomeModal();
        Swal.fire({
          icon: 'success',
          title: isWithdraw ? 'Candidate Withdrawn' : 'Candidate Rejected',
          text: `Onboarding marked as ${outcomeModal.type}. Remaining steps set to N/A, and application updated.`,
          timer: 2000,
          showConfirmButton: false
        });
        await fetchDetails();
        if (onUpdated) onUpdated();
      } else {
        throw new Error(res.data?.message || `Failed to ${actionLabel.toLowerCase()} candidate`);
      }
    } catch (err) {
      console.error(`Error processing ${actionLabel}:`, err);
      Swal.fire({
        icon: 'error',
        title: `${actionLabel} Failed`,
        text: err.response?.data?.message || err.message || `Failed to process ${actionLabel.toLowerCase()}.`
      });
      setOutcomeModal(prev => ({ ...prev, submitting: false }));
    }
  };

  // Revoke Outcome Handlers
  const handleOpenRevokeModal = (outcomeType) => {
    setRevokeModal({
      open: true,
      outcomeType: outcomeType || instance?.StatusCode || 'WITHDRAWN',
      notes: '',
      submitting: false
    });
  };

  const handleCloseRevokeModal = () => {
    if (revokeModal.submitting) return;
    setRevokeModal({
      open: false,
      outcomeType: 'WITHDRAWN',
      notes: '',
      submitting: false
    });
  };

  const handleConfirmRevoke = async () => {
    try {
      setRevokeModal(prev => ({ ...prev, submitting: true }));
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BASE_URL}/api/onboarding/instances/${instanceId}/revoke`, {
        notes: revokeModal.notes.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data?.success) {
        handleCloseRevokeModal();
        Swal.fire({
          icon: 'success',
          title: 'Outcome Revoked',
          text: 'Candidate outcome revoked. Onboarding restored to in-progress.',
          timer: 2000,
          showConfirmButton: false
        });
        await fetchDetails();
        if (onUpdated) onUpdated();
      } else {
        throw new Error(res.data?.message || 'Failed to revoke candidate outcome');
      }
    } catch (err) {
      console.error('Error revoking outcome:', err);
      Swal.fire({
        icon: 'error',
        title: 'Revoke Failed',
        text: err.response?.data?.message || err.message || 'Failed to revoke outcome.'
      });
      setRevokeModal(prev => ({ ...prev, submitting: false }));
    }
  };

  // Handle Save Note
  const handleSaveNote = async () => {
    if (!newNoteText.trim()) return;
    try {
      setSavingNote(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const res = await axios.post(`${BASE_URL}/api/onboarding/instances/${instanceId}/notes`, {
        noteText: newNoteText.trim(),
        noteTag: newNoteCategory || 'GENERAL',
        userId: user.id || localStorage.getItem('userId') || null,
        userName: user.name || (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : null) || localStorage.getItem('name') || localStorage.getItem('fullName') || (user.username && !user.username.includes('@') ? user.username : null) || 'Prabhu Chinnaswamy'
      });

      if (res.data.success) {
        setNewNoteText('');
        setIsAddingNote(false);
        setNewNoteCategory('GENERAL');
        await fetchDetails(true);
      }
    } catch (err) {
      console.error('Error adding note:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to save note.' });
    } finally {
      setSavingNote(false);
    }
  };

  // Handle Update Note
  const handleUpdateNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      setSavingNote(true);
      const res = await axios.put(`${BASE_URL}/api/onboarding/notes/${noteId}`, {
        noteText: editingNoteText.trim(),
        noteTag: editingNoteCategory || 'GENERAL'
      });

      if (res.data.success) {
        setEditingNoteId(null);
        setEditingNoteText('');
        setEditingNoteCategory('GENERAL');
        await fetchDetails(true);
      }
    } catch (err) {
      console.error('Error updating note:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to update note.' });
    } finally {
      setSavingNote(false);
    }
  };

  // Handle Delete Note
  const handleDeleteNote = async (noteId) => {
    const result = await Swal.fire({
      title: 'Delete Note?',
      text: 'Are you sure you want to delete this note?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${BASE_URL}/api/onboarding/notes/${noteId}`);
        await fetchDetails();
      } catch (err) {
        console.error('Error deleting note:', err);
        Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to delete note.' });
      }
    }
  };

  if (!instanceId) return null;

  const completedStepsCount = visibleSteps.filter(s => s.IsTerminal).length;
  const progressPercent = visibleSteps.length > 0 ? Math.round((completedStepsCount / visibleSteps.length) * 100) : 0;

  // Resolve Pipeline Chain Nodes (including baseline Job Client like TCS (US) if not in chain)
  const defaultJobClient = (instance?.JobClientName || instance?.ClientName || '').trim();

  // Client-side nodes (must be CLIENT side or explicit EndClient, not supplier or w2)
  let clientNodes = [...placementChain.filter(c => (c.ChainSide === 'CLIENT' || c.IsEndClient) && !c.IsW2Employer && c.ChainSide !== 'SUPPLIER')];

  if (defaultJobClient) {
    const trimmed = defaultJobClient.toLowerCase();
    const alreadyExists = clientNodes.some(n => {
      const disp = (n.DisplayName || '').trim().toLowerCase();
      const comp = (n.CompanyName || '').trim().toLowerCase();
      const dispBase = disp.replace(/\s*\((in|us|usa|india)\)/gi, '').trim();
      const compBase = comp.replace(/\s*\((in|us|usa|india)\)/gi, '').trim();
      return disp === trimmed || 
             comp === trimmed || 
             disp.startsWith(trimmed) || 
             comp.startsWith(trimmed) ||
             trimmed.startsWith(disp) ||
             (dispBase.length >= 3 && trimmed.includes(dispBase)) ||
             (compBase.length >= 3 && trimmed.includes(compBase));
    });
    if (!alreadyExists) {
      const hasEndClient = clientNodes.some(l => l.IsEndClient);
      const minClientSeq = clientNodes.reduce((m, x) => Math.min(m, x.SequenceNo || 1), 999);
      const maxClientSeq = clientNodes.reduce((m, x) => Math.max(m, x.SequenceNo || 0), 0);
      let seq = 1;
      if (hasEndClient || minClientSeq === 1) {
        seq = Math.max(2, maxClientSeq + 1);
      } else if (clientNodes.length > 0) {
        seq = 1;
      }
      clientNodes.push({
        CompanyName: defaultJobClient,
        DisplayName: defaultJobClient,
        ChainSide: 'CLIENT',
        SequenceNo: seq,
        IsVirtual: true
      });
    }
  }

  // Sort: End Client first (left-most), then direct/intermediary client, then MSP (closest to Prophecy), then by SequenceNo asc
  clientNodes.sort((a, b) => {
    const aEnd = Boolean(a.IsEndClient);
    const bEnd = Boolean(b.IsEndClient);
    if (aEnd && !bEnd) return -1;
    if (!aEnd && bEnd) return 1;

    // MSP nodes sit closest to Prophecy on the client side (after intermediary / direct client)
    const aIsMsp = Boolean(a.IsMSP || (a.RoleLabel && a.RoleLabel.toUpperCase() === 'MSP'));
    const bIsMsp = Boolean(b.IsMSP || (b.RoleLabel && b.RoleLabel.toUpperCase() === 'MSP'));
    if (!aIsMsp && bIsMsp) return -1;
    if (aIsMsp && !bIsMsp) return 1;

    return (a.SequenceNo || 0) - (b.SequenceNo || 0);
  });

  // Supplier / Vendor / W2 Employer nodes (ChainSide === 'SUPPLIER' || IsW2Employer)
  const supplierNodes = placementChain.filter(c => c.ChainSide === 'SUPPLIER' || c.IsW2Employer).sort((a, b) => (a.SequenceNo || 0) - (b.SequenceNo || 0));
  const clientName = clientNodes.length > 0 ? (clientNodes[clientNodes.length - 1].DisplayName || clientNodes[clientNodes.length - 1].CompanyName) : (instance?.ClientCompanyName || 'Client');
  const vendorName = supplierNodes.length > 0 ? (supplierNodes[0].DisplayName || supplierNodes[0].CompanyName) : null;
  const candidateFullName = `${instance?.CandidateFirstName || ''} ${instance?.CandidateLastName || ''}`.trim() || 'Candidate';
  // Compact Step Row Renderer
  const renderStepRow = (step, idx) => {
    const isStepTerminal = step.IsTerminal;
    const stepNumber = idx !== undefined ? idx + 1 : (step.StepSequence || 1);
    const isActionEmp = step.StepCode === 'CREATE_LINK_EMP';
    const isActionDep = step.StepCode === 'CREATE_DEPLOYMENT';
    const isActionStep = isActionEmp || isActionDep;

    const isReferralDoc = Boolean(step.StepCode && step.StepCode.startsWith('REFERRAL_DOC'));
    const hasSubItems = !isReferralDoc && step.items && step.items.length > 0;
    const isStepExpanded = !!expandedSteps[step.OnboardingInstanceStepId];
    const isExpandable = !isReferralDoc && (hasSubItems || step.StepCode === 'CAND_COMP_DOC' || step.StepCode === 'SUPPLIER_DOC');

    return (
      <div key={step.OnboardingInstanceStepId} style={{ display: 'flex', flexDirection: 'column', width: '100%', marginBottom: '2px' }}>
        <div
          className={`onb-compact-step-row ${isStepTerminal ? 'terminal-success' : 'in-progress'} ${isActionStep ? 'action-step-row' : ''}`}
        >
          <div className="onb-compact-step-left">
            <span className="onb-compact-seq">{stepNumber}</span>
            <div className="onb-compact-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span 
                  className="onb-compact-name"
                  onClick={() => {
                    const resolvedEmpId = step.ResultRefId || instance?.ResultEmployeeId || data?.linkedEmployee?.EmployeeId;
                    if (isActionEmp && resolvedEmpId) {
                      setSelectedEmployeeIdForModal(resolvedEmpId);
                    }
                  }}
                  style={isActionEmp && (step.ResultRefId || instance?.ResultEmployeeId || data?.linkedEmployee?.EmployeeId) ? { cursor: 'pointer' } : {}}
                  title={isActionEmp && (step.ResultRefId || instance?.ResultEmployeeId || data?.linkedEmployee?.EmployeeId) ? "Click to view Employee Details" : ""}
                >
                  {step.StepName} <span style={{ color: '#ef4444', fontWeight: 700, marginLeft: '2px' }} title="Mandatory step">*</span>
                </span>

                {/* Subtasks Accordion Toggle */}
                {isExpandable && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStepAccordion(step.OnboardingInstanceStepId);
                    }}
                    title={isStepExpanded ? "Collapse subtasks" : "Expand subtasks"}
                    style={{
                      background: isStepExpanded ? '#e0f2fe' : 'transparent',
                      border: '1px solid',
                      borderColor: isStepExpanded ? '#7dd3fc' : 'transparent',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      padding: '2px 4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: '#0284c7',
                      marginLeft: '2px'
                    }}
                  >
                    {isStepExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </button>
                )}

                {/* Subtask count badge */}
                {!isReferralDoc && (step.totalItemsCount > 0 || hasSubItems) && (
                  <span
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: step.completedItemsCount === step.totalItemsCount && step.totalItemsCount > 0 ? '#dcfce7' : '#f1f5f9',
                      color: step.completedItemsCount === step.totalItemsCount && step.totalItemsCount > 0 ? '#166534' : '#475569',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    {step.completedItemsCount || 0}/{step.totalItemsCount || (step.items || []).length} Subtasks
                  </span>
                )}

                {/* Action Step Badges */}
                {isActionEmp && (step.ResultRefId || data?.linkedEmployee?.EmployeeId) && (
                  <span 
                    className="onb-action-badge linked" 
                    title={`Linked Employee ID: ${step.ResultRefId || data?.linkedEmployee?.EmployeeId} (Click to view Employee Details)`}
                    onClick={() => setSelectedEmployeeIdForModal(step.ResultRefId || instance?.ResultEmployeeId || data?.linkedEmployee?.EmployeeId)}
                    style={{ cursor: 'pointer' }}
                  >
                    ✓ Linked: {step.ResultRefText || `EMP #${step.ResultRefId || data?.linkedEmployee?.EmployeeId}`}
                  </span>
                )}
                {isActionDep && step.ResultRefId && (
                  <span className="onb-action-badge deployed" title={`Created Deployment ID: ${step.ResultRefId}`}>
                    ✓ {step.ResultRefText || `Deployment #${step.ResultRefId}`}
                  </span>
                )}
                {['MSA', 'MSA_CLIENT', 'MSA_SUPPLIER', 'PO', 'CLIENT_PO', 'SUPPLIER_PO'].includes(step.StepCode) && (step.ResultRefId || step.ResultRefText) && (
                  <span className="onb-action-badge linked" title={`Linked Commercial Agreement: ${step.ResultRefText || step.ResultRefId}`}>
                    ✓ Linked: {step.ResultRefText || `DOC #${step.ResultRefId}`}
                  </span>
                )}
                {['MSA_SUPPLIER', 'SUPPLIER_DOC', 'SUPPLIER_PO'].includes(step.StepCode) && (instance?.EmploymentType || '').toUpperCase() === 'W2' && (
                  <span
                    style={{
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      border: '1px solid #fde68a'
                    }}
                    title="Direct W2 hire — supplier step typically Not Applicable"
                  >
                    Direct W2 (Supplier N/A)
                  </span>
                )}
              </div>

              {step.StepDescription && (
                <span className="onb-compact-desc">{step.StepDescription}</span>
              )}
              {['MSA', 'MSA_CLIENT', 'MSA_SUPPLIER', 'PO', 'CLIENT_PO', 'SUPPLIER_PO'].includes(step.StepCode) && !isEffectivelyLocked && (
                <button
                  type="button"
                  className={step.ResultRefId || step.ResultRefText ? "onb-action-trigger-btn emp" : "onb-compact-link-btn"}
                  onClick={() => handleOpenCommercialDocModal(step)}
                  title={step.ResultRefId || step.ResultRefText ? "Update / Relink Commercial Agreement" : "Link or Draft Commercial Agreement"}
                  style={step.ResultRefId || step.ResultRefText ? { display: 'inline-flex', alignItems: 'center', gap: '4px' } : {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: '#f0fdf4',
                    color: '#166534',
                    border: '1px solid #bbf7d0',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.74rem',
                    fontWeight: 600
                  }}
                >
                  <ShieldCheck size={12} /> {step.ResultRefId || step.ResultRefText ? 'Update / Relink Agreement' : 'Link / Draft Agreement'}
                </button>
              )}

              {/* Step Multi-Attachment List (with inline preview & individual remove) */}
              {(() => {
                const stepAttList = (step.attachments && step.attachments.length > 0)
                  ? step.attachments
                  : (step.OriginalFileName ? [{
                      CoreDocumentId: step.CoreDocumentId,
                      OriginalFileName: step.OriginalFileName,
                      DocumentStoragePath: step.DocumentStoragePath
                    }] : []);

                if (stepAttList.length === 0) return null;

                return (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', maxWidth: '340px' }}>
                    {stepAttList.map((att, aIdx) => {
                      const docName = att.OriginalFileName || att.originalFileName;
                      const docPath = att.DocumentStoragePath || att.storageLocator;
                      return (
                        <div
                          key={att.CoreDocumentId || att.OnboardingInstanceStepDocumentId || aIdx}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            fontSize: '0.72rem',
                            color: '#0369a1',
                            background: '#f0f9ff',
                            border: '1px solid #bae6fd',
                            padding: '2px 7px',
                            borderRadius: '4px',
                            maxWidth: '145px',
                            whiteSpace: 'nowrap',
                            fontWeight: 500
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleOpenDocumentPreview(docName, docPath, att.CoreDocumentId || att.documentId);
                            }}
                            title={`Attached Document: ${docName} (Click to preview)`}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px',
                              color: '#0369a1',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.72rem',
                              fontWeight: 500
                            }}
                          >
                            <Paperclip size={11} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {docName}
                            </span>
                          </button>
                          {!isEffectivelyLocked && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveStepIndividualAttachment(step, att);
                              }}
                              title={`Remove attached document (${docName})`}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0 2px',
                                color: '#94a3b8',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
                            >
                              <X size={11} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Action Step Triggers */}
              {isActionEmp && !isEffectivelyLocked && (
                <button
                  type="button"
                  className="onb-action-trigger-btn emp"
                  onClick={() => {
                    const emp = data?.linkedEmployee;
                    const dep = data?.deployment;
                    const inst = data?.instance;
                    const resolvedStartDate = dep?.StartDate
                      ? dep.StartDate.substring(0, 10)
                      : (emp?.StartDate
                        ? emp.StartDate.substring(0, 10)
                        : (inst?.JobStartDate ? inst.JobStartDate.substring(0, 10) : new Date().toISOString().substring(0, 10)));

                    setEmpFormData({
                      employeeCode: emp?.EmployeeCode || '',
                      employeeType: emp?.EmployeeType || (inst?.EmploymentType === 'C2C' ? 'C2C' : (inst?.EmploymentType === 'INTERNAL' ? 'INTERNAL' : 'DEPLOYED')),
                      startDate: resolvedStartDate,
                      email: inst?.CandidateEmail || '',
                      phone: inst?.CandidatePhone || '',
                      entityId: inst?.EntityId || 4
                    });
                    setShowEmpModal(true);
                  }}
                >
                  <UserCheck size={12} /> {step.ResultRefId ? 'Update / Relink Employee' : 'Create / Link Employee'}
                </button>
              )}

              {isActionDep && !isEffectivelyLocked && (
                <button
                  type="button"
                  className="onb-action-trigger-btn dep"
                  onClick={() => {
                    const empLinked = steps.some(s => s.StepCode === 'CREATE_LINK_EMP' && s.ResultRefId) || instance?.ResultEmployeeId;
                    if (!empLinked) {
                      Swal.fire({
                        icon: 'warning',
                        title: 'Employee Required',
                        text: 'Please complete Create / Link Employee step first before creating deployment.'
                      });
                      return;
                    }
                    const dep = data?.deployment;
                    const emp = data?.linkedEmployee;
                    const resolvedDepStartDate = dep?.StartDate
                      ? dep.StartDate.substring(0, 10)
                      : (emp?.StartDate
                        ? emp.StartDate.substring(0, 10)
                        : (empFormData.startDate || (instance?.JobStartDate ? instance.JobStartDate.substring(0, 10) : new Date().toISOString().substring(0, 10))));

                    const cRateModal = dep?.ClientRate ?? instance?.ClientRate ?? '';
                    const vPctModal = dep?.VmsPct ?? instance?.VmsPct ?? '';
                    let rAfterModal = dep?.RateAfterVms ?? instance?.RateAfterVms ?? '';
                    if (!rAfterModal && cRateModal) {
                      const c = parseFloat(cRateModal);
                      const v = parseFloat(vPctModal);
                      if (!isNaN(c) && c > 0) {
                        rAfterModal = !isNaN(v) && v > 0 ? (c * (1 - v / 100)).toFixed(2) : c.toFixed(2);
                      }
                    }

                    const sRateModal = dep?.SupplierRate ?? instance?.SupplierRate ?? '';
                    const svPctModal = dep?.SupplierVmsPct ?? instance?.SupplierVmsPct ?? '';
                    let actRateModal = dep?.ActualPayRate ?? instance?.ActualPayRate ?? '';

                    setDepFormData({
                      placedJobTitle: dep?.PlacedJobTitle || instance?.JobTitle || 'Consultant',
                      startDate: resolvedDepStartDate,
                      endDate: dep?.EndDate ? dep.EndDate.substring(0, 10) : '',
                      clientRate: cRateModal,
                      vmsPct: vPctModal,
                      rateAfterVms: rAfterModal,
                      supplierRate: sRateModal,
                      supplierVmsPct: svPctModal,
                      supplierRateAfterVms: dep?.SupplierRateAfterVms ?? instance?.SupplierRateAfterVms ?? '',
                      supplierNetTermsDays: dep?.SupplierNetTermsDays ?? instance?.SupplierNetTermsDays ?? '',
                      compliancePayRate: dep?.CompliancePayRate ?? instance?.CompliancePayRate ?? '',
                      actualPayRate: actRateModal,
                      expenses: dep?.Expenses ?? instance?.Expenses ?? '',
                      margin: calculateMargin(
                        rAfterModal,
                        dep?.SupplierRateAfterVms ?? instance?.SupplierRateAfterVms ?? '',
                        actRateModal,
                        instance?.EmploymentType,
                        null,
                        dep?.ReferralFee ?? instance?.ReferralFee ?? '',
                        dep?.Expenses ?? instance?.Expenses ?? '',
                        cRateModal,
                        dep?.CompliancePayRate ?? instance?.CompliancePayRate ?? '',
                        vPctModal,
                        sRateModal,
                        svPctModal
                      ) ?? (dep?.Margin != null && Number(dep.Margin) > 0 ? Number(dep.Margin).toFixed(2) : '0.00'),
                      referralSource: dep?.ReferralSource ?? instance?.ReferralSource ?? '',
                      referralFee: dep?.ReferralFee ?? instance?.ReferralFee ?? '',
                      entityId: dep?.EntityId || instance?.EntityId || 4
                    });
                    setShowDepModal(true);
                  }}
                >
                  <Briefcase size={12} /> {step.ResultRefId ? 'Update Deployment' : 'Create Deployment'}
                </button>
              )}
            </div>
          </div>

          <div className="onb-compact-step-right">
            {/* Status Dropdown */}
            <select
              className={`onb-compact-select ${isStepTerminal ? 'terminal-success' : (step.CurrentStatusCode ? 'in-progress' : 'default-status')}`}
              value={step.CurrentStatusCode || ''}
              disabled={isEffectivelyLocked || savingStepId === step.OnboardingInstanceStepId}
              onChange={(e) => handleStatusChange(step, e.target.value)}
            >
              <option value="">
                -- Status (Default) --
              </option>
              {(step.availableStatusOptions || []).map(opt => (
                <option key={opt.StatusCode} value={opt.StatusCode}>
                  {opt.StatusName} {opt.IsTerminal ? '✓' : ''}
                </option>
              ))}
            </select>

            {/* Custom Assignee Input with Search Dropdown */}
            <div style={{ position: 'relative', width: '130px', flexShrink: 0 }}>
              <input
                type="text"
                className="onb-compact-assignee"
                placeholder="Assignee"
                value={stepAssigneeSearchQuery[step.OnboardingInstanceStepId] !== undefined ? stepAssigneeSearchQuery[step.OnboardingInstanceStepId] : (step.AssignedToUserName || '')}
                disabled={isEffectivelyLocked}
                autoComplete="off"
                onChange={(e) => {
                  const val = e.target.value;
                  setStepAssigneeSearchQuery(prev => ({ ...prev, [step.OnboardingInstanceStepId]: val }));
                  setActiveAssigneeDropdownStepId(step.OnboardingInstanceStepId);
                }}
                onFocus={() => {
                  setActiveAssigneeDropdownStepId(step.OnboardingInstanceStepId);
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setActiveAssigneeDropdownStepId(curr => curr === step.OnboardingInstanceStepId ? null : curr);
                  }, 220);
                }}
              />
              {activeAssigneeDropdownStepId === step.OnboardingInstanceStepId && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  width: '100%',
                  minWidth: '280px',
                  zIndex: 1000,
                  maxHeight: '260px',
                  overflowY: 'auto',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                  marginTop: '4px'
                }}>
                  {getFilteredRecruiters(stepAssigneeSearchQuery[step.OnboardingInstanceStepId] !== undefined ? stepAssigneeSearchQuery[step.OnboardingInstanceStepId] : step.AssignedToUserName).map((r, idx) => {
                    const name = r.FullName || r.name;
                    const extra = [r.EmployeeCode, r.Email].filter(Boolean).join(' • ');
                    return (
                      <div
                        key={r.EmployeeId || r.id || idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: '#ffffff',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseDown={() => {
                          handleAssigneeChange(step, name);
                          setStepAssigneeSearchQuery(prev => ({ ...prev, [step.OnboardingInstanceStepId]: name }));
                          setActiveAssigneeDropdownStepId(null);
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>{name}</div>
                          {extra && <div style={{ fontSize: '11px', color: '#64748b' }}>{extra}</div>}
                        </div>
                        <User size={13} color="#94a3b8" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>



            {/* Hidden File Input for document upload */}
            <input
              type="file"
              id={`step-file-upload-${step.OnboardingInstanceStepId}`}
              style={{ display: 'none' }}
              multiple={true}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleStepFileUpload(step, e.target.files);
                }
                e.target.value = '';
              }}
            />

            {/* Step Document upload button */}
            <button
              type="button"
              className="onb-compact-hist-btn"
              disabled={isEffectivelyLocked}
              title={step.OriginalFileName ? `Replace Step Document (${step.OriginalFileName})` : "Attach Step Document"}
              onClick={() => {
                const fileInput = document.getElementById(`step-file-upload-${step.OnboardingInstanceStepId}`);
                if (fileInput) fileInput.click();
              }}
            >
              <Paperclip size={13} />
            </button>

            {/* History icon */}
            <button
              type="button"
              className="onb-compact-hist-btn"
              title="View Status History"
              onClick={() => {
                setHistoryStep(step);
                setShowHistoryModal(true);
              }}
            >
              <History size={13} />
            </button>

            {/* Delete Step icon */}
            <button
              type="button"
              className="onb-compact-hist-btn onb-compact-delete-btn"
              disabled={isEffectivelyLocked}
              title={`Delete Step: ${step.StepName}`}
              onClick={() => handleDeleteStep(step)}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {/* ── Subtasks Accordion Panel ── */}
        {isStepExpanded && !isReferralDoc && (
          <div style={{
            width: '100%',
            marginTop: '4px',
            marginBottom: '4px',
            padding: '12px 16px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderLeft: '3px solid #0284c7',
            borderRadius: '6px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Layers size={14} color="#0284c7" />
                <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                  Subtasks ({step.completedItemsCount || 0} of {(step.items || []).length} Completed)
                </span>
              </div>
              {!isEffectivelyLocked && (
                <button
                  type="button"
                  onClick={() => {
                    setAddingSubtaskStepId(step.OnboardingInstanceStepId);
                    setNewSubtaskName('');
                    setNewSubtaskAssignee(step.AssignedToUserId || '');
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: '#0284c7',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '4px 10px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={13} /> Add Subtask
                </button>
              )}
            </div>

            {/* Inline Add Subtask Form */}
            {addingSubtaskStepId === step.OnboardingInstanceStepId && (
              <div style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginBottom: '10px',
                padding: '10px',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '6px'
              }}>
                <input
                  type="text"
                  value={newSubtaskName}
                  onChange={(e) => setNewSubtaskName(e.target.value)}
                  placeholder="Subtask name (e.g. Background Check Consent)..."
                  style={{ flex: 1, padding: '6px 10px', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px' }}
                  autoFocus
                />
                <select
                  value={newSubtaskAssignee}
                  onChange={(e) => setNewSubtaskAssignee(e.target.value)}
                  style={{ padding: '6px 10px', fontSize: '0.8rem', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', maxWidth: '160px' }}
                >
                  <option value="">Select Assignee</option>
                  {recruiters.map(r => (
                    <option key={r.EmployeeId || r.id} value={r.EmployeeId || r.id}>
                      {r.FullName || r.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => handleAddSubtask(step.OnboardingInstanceStepId)}
                  disabled={savingSubtask || !newSubtaskName.trim()}
                  style={{
                    background: '#229C8B',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: savingSubtask ? 'not-allowed' : 'pointer'
                  }}
                >
                  {savingSubtask ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => { setAddingSubtaskStepId(null); setNewSubtaskName(''); }}
                  style={{
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #cbd5e1',
                    borderRadius: '4px',
                    padding: '6px 10px',
                    fontSize: '0.78rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Subtasks List */}
            {(!step.items || step.items.length === 0) ? (
              <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.78rem', color: '#94a3b8' }}>
                No subtasks defined. Click "+ Add Subtask" to add one.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* ── Subtasks Table Header ── */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '5px 10px',
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  borderRadius: '5px',
                  fontSize: '0.71rem',
                  fontWeight: 700,
                  color: '#475569',
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase'
                }}>
                  <div style={{ flex: 1, minWidth: 0, paddingLeft: '2px' }}>Subtask Name</div>
                  <div style={{ width: '120px', flexShrink: 0, textAlign: 'left', paddingLeft: '2px' }}>Status</div>
                  <div style={{ width: '135px', flexShrink: 0, textAlign: 'left', paddingLeft: '2px' }}>Assignee</div>
                  <div style={{ width: '210px', flexShrink: 0, textAlign: 'left', paddingLeft: '2px' }}>Attachment / Action</div>
                </div>

                {step.items.map((sub, sIdx) => {
                  const isCompleted = sub.CurrentStatusCode === 'COMPLETED';
                  const isFailed = sub.CurrentStatusCode === 'FAILED';

                  const sourceBg = sub.Source === 'DEFAULT' ? '#eff6ff' : (sub.Source === 'CLIENT' ? '#faf5ff' : '#f8fafc');
                  const sourceColor = sub.Source === 'DEFAULT' ? '#1d4ed8' : (sub.Source === 'CLIENT' ? '#7e22ce' : '#64748b');

                  return (
                    <div
                      key={sub.OnboardingStepItemId}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '5px 10px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        minHeight: '34px',
                        boxSizing: 'border-box'
                      }}
                    >
                      {/* 1. Subtask Name & Tag Column (Flexible 1fr) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#94a3b8', width: '20px', flexShrink: 0 }}>
                          {sIdx + 1}.
                        </span>
                        <span
                          style={{
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={sub.ItemName}
                        >
                          {sub.ItemName}
                        </span>
                        <span style={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: '4px',
                          background: sourceBg,
                          color: sourceColor,
                          border: `1px solid ${sourceColor}33`,
                          letterSpacing: '0.5px',
                          flexShrink: 0
                        }}>
                          {sub.Source || 'DEFAULT'}
                        </span>
                      </div>

                      {/* 2. Status Selector Column (Fixed 120px) */}
                      <div style={{ width: '120px', flexShrink: 0 }}>
                        <select
                          value={sub.CurrentStatusCode || 'PENDING'}
                          disabled={isEffectivelyLocked}
                          onChange={(e) => handleSubtaskStatusChange(step.OnboardingInstanceStepId, sub.OnboardingStepItemId, e.target.value)}
                          style={{
                            width: '100%',
                            height: '26px',
                            padding: '0 6px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            border: '1px solid',
                            borderColor: isCompleted ? '#86efac' : (isFailed ? '#fca5a5' : '#cbd5e1'),
                            borderRadius: '4px',
                            background: isCompleted ? '#f0fdf4' : (isFailed ? '#fef2f2' : '#f8fafc'),
                            color: isCompleted ? '#166534' : (isFailed ? '#991b1b' : '#334155'),
                            cursor: 'pointer',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="PENDING">Pending</option>
                          <option value="IN_PROGRESS">In-Progress</option>
                          <option value="REQUESTED">Requested</option>
                          <option value="COMPLETED">Completed ✓</option>
                          <option value="FAILED">Failed ✕</option>
                          <option value="WAIVED">Waived</option>
                          <option value="NOT_APPLICABLE">N/A</option>
                        </select>
                      </div>

                        {/* 2. Assignee selector (Interchanged: placed second & searchable select dropdown) */}
                        <div style={{ position: 'relative', width: '135px', flexShrink: 0 }}>
                          <input
                            type="text"
                            placeholder="Unassigned"
                            value={
                              subtaskAssigneeSearchQuery[sub.OnboardingStepItemId] !== undefined
                                ? subtaskAssigneeSearchQuery[sub.OnboardingStepItemId]
                                : (step.AssignedToUserName || step.AssignedToUserFullName || sub.AssignedToUserFullName || sub.AssignedToUserName || '')
                            }
                            disabled={isEffectivelyLocked}
                            autoComplete="off"
                            onChange={(e) => {
                              const val = e.target.value;
                              setSubtaskAssigneeSearchQuery(prev => ({ ...prev, [sub.OnboardingStepItemId]: val }));
                              setActiveSubtaskAssigneeDropdownId(sub.OnboardingStepItemId);
                            }}
                            onFocus={() => {
                              setActiveSubtaskAssigneeDropdownId(sub.OnboardingStepItemId);
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setActiveSubtaskAssigneeDropdownId(curr => curr === sub.OnboardingStepItemId ? null : curr);
                              }, 220);
                            }}
                            style={{
                              width: '100%',
                              height: '26px',
                              padding: '0 8px',
                              fontSize: '0.75rem',
                              border: '1px solid #cbd5e1',
                              borderRadius: '4px',
                              background: '#ffffff',
                              color: '#334155',
                              cursor: isEffectivelyLocked ? 'not-allowed' : 'text',
                              boxSizing: 'border-box'
                            }}
                          />
                          {activeSubtaskAssigneeDropdownId === sub.OnboardingStepItemId && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              width: '280px',
                              zIndex: 1000,
                              maxHeight: '260px',
                              overflowY: 'auto',
                              backgroundColor: '#ffffff',
                              border: '1px solid #cbd5e1',
                              borderRadius: '6px',
                              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                              marginTop: '4px'
                            }}>
                              <div
                                style={{
                                  padding: '7px 12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  color: '#94a3b8',
                                  fontSize: '12px',
                                  fontStyle: 'italic',
                                  backgroundColor: '#ffffff',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                onMouseDown={() => {
                                  handleSubtaskAssigneeChange(step.OnboardingInstanceStepId, sub.OnboardingStepItemId, null, null);
                                  setSubtaskAssigneeSearchQuery(prev => ({ ...prev, [sub.OnboardingStepItemId]: '' }));
                                  setActiveSubtaskAssigneeDropdownId(null);
                                }}
                              >
                                -- Unassigned --
                              </div>
                              {getFilteredRecruiters(
                                subtaskAssigneeSearchQuery[sub.OnboardingStepItemId] !== undefined
                                  ? subtaskAssigneeSearchQuery[sub.OnboardingStepItemId]
                                  : (step.AssignedToUserName || step.AssignedToUserFullName || sub.AssignedToUserFullName || sub.AssignedToUserName || '')
                              ).map((r, idx) => {
                                const name = r.FullName || r.name;
                                const extra = [r.EmployeeCode, r.Email].filter(Boolean).join(' • ');
                                return (
                                  <div
                                    key={r.EmployeeId || r.id || idx}
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '8px 12px',
                                      cursor: 'pointer',
                                      textAlign: 'left',
                                      borderBottom: '1px solid #f1f5f9',
                                      backgroundColor: '#ffffff',
                                      transition: 'background-color 0.15s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                                    onMouseDown={() => {
                                      handleSubtaskAssigneeChange(step.OnboardingInstanceStepId, sub.OnboardingStepItemId, r.EmployeeId || r.id, name);
                                      setSubtaskAssigneeSearchQuery(prev => ({ ...prev, [sub.OnboardingStepItemId]: name }));
                                      setActiveSubtaskAssigneeDropdownId(null);
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '13px' }}>{name}</div>
                                      {extra && <div style={{ fontSize: '11px', color: '#64748b' }}>{extra}</div>}
                                    </div>
                                    <User size={13} color="#94a3b8" />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                      {/* 4. Document Attachment & Actions Column (Flexible 210px with multi-attachment support) */}
                      <div style={{
                        width: '210px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap'
                      }}>
                        {/* Multi-Attachment vs Single Attachment Rendering */}
                        {(() => {
                          const isMulti = isMultiAttachmentSubtask(sub);
                          const isUploading = uploadingSubtaskId === sub.OnboardingStepItemId;

                          const attList = (sub.attachments && sub.attachments.length > 0)
                            ? sub.attachments
                            : (sub.OriginalFileName ? [{ CoreDocumentId: sub.CoreDocumentId, OriginalFileName: sub.OriginalFileName, DocumentStoragePath: sub.DocumentStoragePath }] : []);

                          return (
                            <>
                              {/* Hidden subtask file input with multi-select support when isMulti is true */}
                              <input
                                type="file"
                                id={`subtask-file-upload-${sub.OnboardingStepItemId}`}
                                style={{ display: 'none' }}
                                multiple={isMulti}
                                onChange={(e) => {
                                  if (e.target.files && e.target.files.length > 0) {
                                    handleSubtaskFileUpload(sub, isMulti ? e.target.files : e.target.files[0]);
                                  }
                                  e.target.value = '';
                                }}
                              />

                              {isMulti ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', maxWidth: '100%' }}>
                                  {attList.map((att, aIdx) => (
                                    <div
                                      key={att.CoreDocumentId || att.OnboardingStepItemDocumentId || aIdx}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        fontSize: '0.70rem',
                                        color: '#0369a1',
                                        background: '#f0f9ff',
                                        border: '1px solid #bae6fd',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        maxWidth: '120px',
                                        whiteSpace: 'nowrap',
                                        fontWeight: 500
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleOpenDocumentPreview(att.OriginalFileName, att.DocumentStoragePath, att.CoreDocumentId || att.documentId);
                                        }}
                                        title={`Attached File: ${att.OriginalFileName} (Click to preview)`}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          padding: 0,
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '3px',
                                          color: '#0369a1',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          fontSize: '0.70rem',
                                          fontWeight: 500
                                        }}
                                      >
                                        <Paperclip size={11} style={{ flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {att.OriginalFileName}
                                        </span>
                                      </button>
                                      {!isEffectivelyLocked && (
                                        <button
                                          type="button"
                                          disabled={isUploading}
                                          onClick={() => handleRemoveIndividualAttachment(sub, att)}
                                          style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: isUploading ? 'not-allowed' : 'pointer',
                                            padding: '0 2px',
                                            color: '#94a3b8',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            lineHeight: 1
                                          }}
                                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; }}
                                          onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; }}
                                          title={`Remove attachment "${att.OriginalFileName}"`}
                                        >
                                          <X size={11} />
                                        </button>
                                      )}
                                    </div>
                                  ))}

                                  {!isEffectivelyLocked && (
                                    <button
                                      type="button"
                                      disabled={isUploading}
                                      onClick={() => {
                                        const fileInput = document.getElementById(`subtask-file-upload-${sub.OnboardingStepItemId}`);
                                        if (fileInput) fileInput.click();
                                      }}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '3px',
                                        background: isUploading ? '#f1f5f9' : '#f8fafc',
                                        border: '1px dashed #0284c7',
                                        borderRadius: '4px',
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        padding: '2px 6px',
                                        fontSize: '0.69rem',
                                        color: '#0284c7',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        opacity: isUploading ? 0.7 : 1
                                      }}
                                      title={isUploading ? 'Uploading files...' : (attList.length > 0 ? 'Attach another file to this subtask' : 'Attach file(s) to this subtask')}
                                    >
                                      {isUploading ? (
                                        <>
                                          <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                                          <span>Uploading...</span>
                                        </>
                                      ) : (
                                        <>
                                          <Plus size={11} />
                                          <span>Attach</span>
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                // Single Attachment Mode
                                sub.OriginalFileName ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '100%' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleOpenDocumentPreview(sub.OriginalFileName, sub.DocumentStoragePath, sub.CoreDocumentId);
                                }}
                                title={`Attached Document: ${sub.OriginalFileName} (Click to preview)`}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  fontSize: '0.73rem',
                                  color: '#0369a1',
                                  background: '#f0f9ff',
                                  border: '1px solid #bae6fd',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  maxWidth: '125px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontWeight: 500,
                                  cursor: 'pointer'
                                }}
                              >
                                <Paperclip size={12} style={{ flexShrink: 0 }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {sub.OriginalFileName}
                                </span>
                              </button>
                              {!isEffectivelyLocked && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const fileInput = document.getElementById(`subtask-file-upload-${sub.OnboardingStepItemId}`);
                                      if (fileInput) fileInput.click();
                                    }}
                                    style={{
                                      background: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      padding: '2px 4px',
                                      color: '#64748b',
                                      display: 'inline-flex',
                                      alignItems: 'center'
                                    }}
                                    title={`Re-upload or replace "${sub.OriginalFileName}"`}
                                  >
                                    <Upload size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveSubtaskDocument(sub)}
                                    style={{
                                      background: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      padding: '2px 4px',
                                      color: '#94a3b8',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      lineHeight: 1
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                                    title={`Remove document (${sub.OriginalFileName})`}
                                  >
                                    <X size={13} />
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={isEffectivelyLocked}
                              onClick={() => {
                                const fileInput = document.getElementById(`subtask-file-upload-${sub.OnboardingStepItemId}`);
                                if (fileInput) fileInput.click();
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: '#f8fafc',
                                border: '1px dashed #cbd5e1',
                                borderRadius: '4px',
                                cursor: isEffectivelyLocked ? 'default' : 'pointer',
                                padding: '2px 8px',
                                fontSize: '0.72rem',
                                color: '#64748b',
                                fontWeight: 500
                              }}
                              title="Attach document to this subtask"
                            >
                              <Paperclip size={12} />
                              <span>Attach</span>
                            </button>
                          )
                        )}
                      </>
                    );
                  })()}

                        {/* Delete button for ad-hoc */}
                        {sub.Source === 'ADHOC' && !isEffectivelyLocked && (
                          <button
                            type="button"
                            onClick={() => handleDeleteSubtask(step.OnboardingInstanceStepId, sub.OnboardingStepItemId)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', color: '#ef4444', marginLeft: 'auto' }}
                            title="Delete ad-hoc subtask"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="onb-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="onb-modal-container">
        {/* TOP HEADER: Title, Subtitle, & Direct Header Placement Pipeline */}
        <div className="onb-modal-header">
          <div className="onb-header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <h2 className="onb-modal-title">
                {candidateFullName}
              </h2>
              {(() => {
                let rawEmpType = String(
                  Array.isArray(instance?.EmploymentType)
                    ? instance.EmploymentType[0]
                    : (instance?.EmploymentType || '')
                ).trim();
                if (rawEmpType === 'Contract (C2C)' || rawEmpType.toUpperCase() === 'C2C') rawEmpType = 'C2C';
                else if (rawEmpType === 'Contract (W2)' || rawEmpType.toUpperCase() === 'W2') rawEmpType = 'W2';
                else if (rawEmpType === 'Contract (1099)' || rawEmpType.toUpperCase() === '1099') rawEmpType = '1099';

                const isC2C = rawEmpType.toUpperCase() === 'C2C';
                return (
                  <span className={`onb-type-pill ${isC2C ? 'c2c' : 'w2'}`}>
                    {rawEmpType || 'N/A'}
                  </span>
                );
              })()}
              <span className={`onb-status-chip ${instance?.StatusCode === 'COMPLETED' ? 'completed' : (instance?.StatusCode === 'WITHDRAWN' ? 'withdrawn' : (instance?.StatusCode === 'REJECTED' ? 'rejected' : 'in-progress'))}`}>
                {instance?.StatusCode}
              </span>
              {/* <ShareButton 
                title={`Onboarding: ${candidateFullName}`}
                subtitle={`Job: ${instance?.JobTitle || 'N/A'} (ID: ${instance?.JobId || ''})`}
                shareUrl={`${window.location.origin}/crm/onboarding?instanceId=${instanceId}`}
                summaryText={`Candidate Onboarding: ${candidateFullName}\nJob: ${instance?.JobTitle || 'N/A'}\nClient: ${instance?.ClientName || instance?.ClientCompanyName || 'N/A'}\nStatus: ${instance?.StatusCode || 'IN_PROGRESS'}\nLink: ${window.location.origin}/crm/onboarding?instanceId=${instanceId}`}
                variant="compact"
                buttonText="Share"
              /> */}
            </div>
            <div className="onb-modal-subtitle">
              <span><strong>Job:</strong> {instance?.JobTitle} ({instance?.ExternalJobReference && instance?.ExternalJobReference !== 'N/A' ? instance?.ExternalJobReference : (instance?.JobId ? `#${instance?.JobId}` : 'N/A')})</span>
              <span><strong>Entity:</strong> {instance?.EntityName || 'Prophecy Consulting INC'}</span>
              <span><strong>Rate:</strong> {instance?.SubmissionRate || 'Negotiated'}</span>
            </div>
          </div>

          {/* ── TOP HEADER PIPELINE / PLACEMENT CHAIN ── */}
          <div className="onb-header-pipeline-badge">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
              <div className="onb-pipeline-title">PLACEMENT PIPELINE</div>
              <button
                type="button"
                className="onb-pipeline-edit-btn"
                onClick={() => setShowChainEditor(!showChainEditor)}
                title="Edit placement chain nodes"
              >
                <Edit2 size={11} /> {showChainEditor ? 'Hide Chain Editor' : 'Edit Chain'}
              </button>
            </div>
            <div className="onb-pipeline-nodes">
              {/* 1. Client Side Nodes (End Client furthest left -> Intermediary) */}
              {clientNodes.length > 0 ? (
                clientNodes.map((cn, idx) => (
                  <React.Fragment key={cn.SubmissionChainId || cn.JobChainId || idx}>
                    <span
                      className={`onb-node ${cn.IsEndClient ? 'end-client' : 'client'}`}
                      title={cn.IsEndClient ? 'End Client' : 'Client / Intermediary'}
                    >
                      <Building2 size={13} /> {cn.DisplayName || cn.CompanyName}
                      {cn.IsEndClient && (
                        <span className="onb-endclient-tag">End Client ★</span>
                      )}
                    </span>
                    <ChevronLeft size={14} className="onb-node-arrow" />
                  </React.Fragment>
                ))
              ) : (
                <>
                  <span className="onb-node client" title="Direct Client">
                    <Building2 size={13} /> {instance?.ClientCompanyName || 'Direct'}
                  </span>
                  <ChevronLeft size={14} className="onb-node-arrow" />
                </>
              )}

              {/* 2. Internal Direct Employer */}
              <span className="onb-node prophecy" title="Internal Direct Employer">
                Prophecy (You)
              </span>
              <ChevronLeft size={14} className="onb-node-arrow" />

              {/* 3. Supplier / Vendor / W2 Employer Nodes (Between Prophecy & Candidate) */}
              {supplierNodes.map((sn, idx) => (
                <React.Fragment key={sn.SubmissionChainId || sn.JobChainId || idx}>
                  <span
                    className={`onb-node ${sn.IsW2Employer ? 'w2-employer' : 'vendor'}`}
                    title={sn.IsW2Employer ? 'W2 Employer' : 'Supplier / Vendor'}
                  >
                    {sn.DisplayName || sn.CompanyName}
                  </span>
                  <ChevronLeft size={14} className="onb-node-arrow" />
                </React.Fragment>
              ))}

              {/* 4. Candidate */}
              <span className="onb-node candidate" title="Candidate" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <User size={12} /> {candidateFullName.split(' ')[0]}
              </span>
            </div>
          </div>

          <button className="onb-close-btn" onClick={onClose} aria-label="Close Modal">
            <X size={20} />
          </button>
        </div>

        {/* ── 15-STEP RECRUITMENT & ONBOARDING PIPELINE TRACKER (SINGLE ROW, READ-ONLY) ── */}
        <div className="onb-15-steps-container">
          <div className="onb-15-steps-header">
            <div className="onb-15-steps-title">
              <Sparkles size={14} style={{ color: '#00a884' }} /> Recruitment & Onboarding Pipeline
            </div>
            <div className={`onb-15-steps-badge ${instanceStatus === 'WITHDRAWN' ? 'withdrawn' : (instanceStatus === 'REJECTED' ? 'rejected' : '')}`}>
              Stage {getCandidateStageIndex()} of 15 : {
                (() => {
                  const idx = getCandidateStageIndex();
                  const baseName = RECRUITMENT_ONBOARDING_STAGES_15[idx - 1]?.name || 'Onboarding Initiated';
                  if (idx === 13) {
                    if (instanceStatus === 'WITHDRAWN') return 'Onboarding Initiated Withdrawn';
                    if (instanceStatus === 'REJECTED') return 'Onboarding Initiated Rejected';
                  }
                  return baseName;
                })()
              }
            </div>
          </div>

          <div className="onb-15-pipeline-scroll">
            <div className="onb-15-pipeline-wrapper">
              {/* Continuous background track line */}
              <div className="onb-15-track-bg" />

              {/* Dynamic filled green track line */}
              {(() => {
                const currentIdx = getCandidateStageIndex();
                const totalStages = RECRUITMENT_ONBOARDING_STAGES_15.length;
                // Calculate percentage along the node centers
                const fillPct = currentIdx > 1
                  ? Math.min(100, Math.max(0, ((currentIdx - 1) / (totalStages - 1)) * 100))
                  : 0;
                return (
                  <div
                    className="onb-15-track-fill"
                    style={{ width: `calc(${fillPct}% * (100% - 72px) / 100)` }}
                  />
                );
              })()}

              <div className="onb-15-nodes-row">
                {RECRUITMENT_ONBOARDING_STAGES_15.map((stage) => {
                  const currentStageIdx = getCandidateStageIndex();
                  const isCompleted = stage.index < currentStageIdx;
                  const isCurrent = stage.index === currentStageIdx;
                  const isWithdrawnStage = isCurrent && instanceStatus === 'WITHDRAWN';
                  const isRejectedStage = isCurrent && instanceStatus === 'REJECTED';
                  const stageDate = getStageDate(stage);
                  const badgeData = formatStageDateBadge(stageDate);

                  const nodeClass = isWithdrawnStage
                    ? 'onb-15-node-col onb-15-node-withdrawn'
                    : isRejectedStage
                      ? 'onb-15-node-col onb-15-node-rejected'
                      : isCompleted
                        ? 'onb-15-node-col onb-15-node-completed'
                        : isCurrent
                          ? 'onb-15-node-col onb-15-node-current'
                          : 'onb-15-node-col onb-15-node-pending';

                  let displayLabel = stage.name;
                  if (stage.index === 13) {
                    if (instanceStatus === 'WITHDRAWN') {
                      displayLabel = 'Onboarding Initiated Withdrawn';
                    } else if (instanceStatus === 'REJECTED') {
                      displayLabel = 'Onboarding Initiated Rejected';
                    }
                  }

                  return (
                    <div key={stage.index} className={nodeClass} title={`${stage.index}. ${displayLabel}`}>
                      <div className="onb-15-circle-wrap">
                        <div className="onb-15-circle">
                          {isWithdrawnStage ? (
                            <Ban size={13} style={{ strokeWidth: 2.5 }} />
                          ) : isRejectedStage ? (
                            <X size={13} style={{ strokeWidth: 2.5 }} />
                          ) : isCompleted ? (
                            '✓'
                          ) : (
                            stage.index
                          )}
                        </div>
                      </div>

                      <div className="onb-15-label">
                        {displayLabel}
                      </div>

                      {badgeData && (isCompleted || isCurrent) ? (
                        <div className="onb-15-date-badge" title={new Date(stageDate).toLocaleString()}>
                          <div className="onb-15-date-day">{badgeData.date}</div>
                          <div className="onb-15-date-time">{badgeData.time}</div>
                        </div>
                      ) : (
                        <div className="onb-15-date-spacer" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── COLLAPSIBLE PLACEMENT CHAIN EDITOR ── */}
        {showChainEditor && instance?.SubmissionId && (
          <div className="onb-chain-editor-wrapper">
            <div className="onb-chain-editor-header">
              <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f766e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Layers size={15} /> Edit Candidate Placement Chain
              </span>
              <button
                className="onb-chain-editor-close"
                onClick={() => setShowChainEditor(false)}
              >
                ✕ Close
              </button>
            </div>
            <PlacementChainDisplay
              jobId={instance.SubmissionId}
              entityType="submission"
              isEditMode={true}
              clientName={defaultJobClient || instance.ClientName || instance.JobClientName || 'TCS (US)'}
              vendorName={vendorName}
              candidateName={candidateFullName}
              showSupplierCandidate={true}
              relatedJobId={instance.JobId}
              initialChain={placementChain}
              onChainSaved={(updatedChain) => {
                if (updatedChain && Array.isArray(updatedChain)) {
                  setData(prev => prev ? ({ ...prev, placementChain: updatedChain }) : prev);
                }
                fetchDetails(true);
                if (onUpdated) onUpdated();
              }}
              onChainChange={(updatedChain) => {
                if (updatedChain && Array.isArray(updatedChain)) {
                  setData(prev => prev ? ({ ...prev, placementChain: updatedChain }) : prev);
                }
                fetchDetails(true);
                if (onUpdated) onUpdated();
              }}
            />
          </div>
        )}

        {/* COMPACT PROGRESS, TAB SWITCHER & BULK ASSIGN BAR */}
        <div className="onb-compact-toolbar">
          <div className="onb-tab-group">
            <button
              type="button"
              className={`onb-tab-btn ${activeTab === 'CHECKLIST' ? 'active' : ''}`}
              onClick={() => setActiveTab('CHECKLIST')}
            >
              <Building2 size={14} /> Checklist ({completedStepsCount}/{visibleSteps.length})
            </button>
            <button
              type="button"
              className={`onb-tab-btn ${activeTab === 'NOTES' ? 'active' : ''}`}
              onClick={() => setActiveTab('NOTES')}
            >
              <MessageSquare size={14} /> Notes ({candidateNotes.length})
            </button>
          </div>

          {/* Bulk Assignee Control */}
          <div className="onb-bulk-assign-bar">
            <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#475569' }}>Bulk Assign:</span>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <input
                type="text"
                className="onb-bulk-assign-input"
                placeholder="Select team member..."
                value={selectedBulkAssignee}
                onChange={(e) => {
                  setSelectedBulkAssignee(e.target.value);
                  setShowBulkAssignDropdown(true);
                }}
                onFocus={() => setShowBulkAssignDropdown(true)}
                onBlur={() => setTimeout(() => setShowBulkAssignDropdown(false), 220)}
                disabled={!isChecklistEditMode && instance?.StatusCode === 'COMPLETED'}
                autoComplete="off"
              />
              {showBulkAssignDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: '100%',
                  minWidth: '280px',
                  zIndex: 1000,
                  maxHeight: '260px',
                  overflowY: 'auto',
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                  marginTop: '4px'
                }}>
                  {getFilteredRecruiters(selectedBulkAssignee).map((r, idx) => {
                    const name = r.FullName || r.name;
                    const extra = [r.EmployeeCode, r.Email].filter(Boolean).join(' • ');
                    return (
                      <div
                        key={r.EmployeeId || r.id || idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: '#ffffff',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedBulkAssignee(name);
                          setShowBulkAssignDropdown(false);
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                      >
                        <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.86rem' }}>
                          {name}
                        </span>
                        {extra && (
                          <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '12px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {extra}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              className="onb-bulk-assign-btn"
              disabled={!selectedBulkAssignee.trim() || applyingAll || (!isChecklistEditMode && instance?.StatusCode === 'COMPLETED')}
              onClick={handleApplyAllAssignee}
              title="Apply selected assignee to all checklist steps"
            >
              <UserCheck size={12} /> {applyingAll ? 'Applying...' : 'Apply All'}
            </button>
          </div>

          {/* Edit Checklist Toggle for Completed Candidates */}
          {instance?.StatusCode === 'COMPLETED' && (
            <button
              type="button"
              className={`onb-edit-checklist-toggle-btn ${isChecklistEditMode ? 'active' : ''}`}
              onClick={() => setIsChecklistEditMode(prev => !prev)}
              title={isChecklistEditMode ? "Finish editing and lock checklist" : "Unlock checklist to edit statuses, subtasks, assignees & attachments"}
            >
              {isChecklistEditMode ? (
                <>
                  <CheckCircle size={13} />
                  <span>Done Editing</span>
                </>
              ) : (
                <>
                  <Edit2 size={13} />
                  <span>Edit Checklist</span>
                </>
              )}
            </button>
          )}

          <div className="onb-toolbar-progress">
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
              Onboarding Progress: {completedStepsCount}/{visibleSteps.length} ({progressPercent}%)
            </span>
            <div className="onb-progress-track" style={{ width: '110px', height: '6px' }}>
              <div className="onb-progress-fill" style={{ width: `${progressPercent}%` }}></div>
            </div>
          </div>
        </div>

        {/* MODAL BODY: Sequential Checklist with Action Steps */}
        <div className="onb-modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              <Clock size={32} style={{ animation: 'spin 1.5s linear infinite', marginBottom: '12px' }} />
              <p>Loading onboarding checklist & placement data...</p>
            </div>
          ) : (
            <>
              {activeTab === 'CHECKLIST' && (
                <div className="onb-checklist-layout-grid">
                  {/* Left Column: RATE AND REFERRAL FIELDS */}
                  <div className="onb-rates-referral-card">
                    <div className="onb-rates-card-header">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <DollarSign size={16} color="#0f766e" />
                        <h4>RATE & REFERRAL DETAILS</h4>
                      </div>
                      {!isEditingRates ? (
                        <button
                          type="button"
                          className="onb-rate-edit-btn"
                          onClick={() => {
                            setIsEditingRates(true);
                            setShowRateReferralDropdown(false);
                            setRateReferralSearchText('');
                            if (instance) {
                              const rawRef = instance.ReferralSource || '';
                              const activeRefs = Array.isArray(instance.referrals) && instance.referrals.length > 0
                                ? instance.referrals.map(r => ({
                                    submissionReferralId: r.SubmissionReferralId,
                                    referralCompanyId: r.ReferralCompanyId,
                                    displayName: r.DisplayName || r.CompanyName,
                                    companyName: r.CompanyName,
                                    referralFee: r.ReferralFee != null ? r.ReferralFee : '',
                                    referralNetTermsDays: r.ReferralNetTermsDays != null ? r.ReferralNetTermsDays : '',
                                    notes: r.Notes || ''
                                  }))
                                : (rawRef ? rawRef.split(',').map(s => ({
                                    referralCompanyId: null,
                                    displayName: s.trim(),
                                    companyName: '',
                                    referralFee: instance.ReferralFee || '',
                                    referralNetTermsDays: '',
                                    notes: ''
                                  })).filter(r => r.displayName) : []);

                              const totalRefFee = activeRefs.length > 0 
                                ? activeRefs.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0)
                                : (parseFloat(instance.ReferralFee) || 0);

                              const computedM = calculateMargin(
                                instance.RateAfterVms,
                                instance.SupplierRateAfterVms ?? (instance.SupplierRate && instance.SupplierVmsPct ? (instance.SupplierRate * (1 - instance.SupplierVmsPct / 100)).toFixed(2) : ''),
                                instance.ActualPayRate ?? instance.SubmissionRate,
                                instance.EmploymentType,
                                activeRefs,
                                totalRefFee,
                                instance.Expenses,
                                instance.ClientRate ?? instance.SubmissionRate,
                                instance.CompliancePayRate,
                                instance.VmsPct,
                                instance.SupplierRate,
                                instance.SupplierVmsPct
                              );

                              const resolvedEditMargin = (computedM != null && computedM !== '')
                                ? computedM
                                : (instance.Margin != null && Number(instance.Margin) > 0 && Number(instance.RateAfterVms || instance.ClientRate) > 0 ? Number(instance.Margin).toFixed(2) : '0.00');

                              setRateFormData({
                                clientRate: instance.ClientRate ?? instance.SubmissionRate ?? '',
                                vmsPct: instance.VmsPct ?? '',
                                rateAfterVms: instance.RateAfterVms ?? '',
                                supplierRate: instance.SupplierRate ?? '',
                                supplierVmsPct: instance.SupplierVmsPct ?? '',
                                supplierRateAfterVms: instance.SupplierRateAfterVms ?? (instance.SupplierRate && instance.SupplierVmsPct ? (instance.SupplierRate * (1 - instance.SupplierVmsPct / 100)).toFixed(2) : ''),
                                supplierNetTermsDays: instance.SupplierNetTermsDays ?? '',
                                compliancePayRate: instance.CompliancePayRate ?? '',
                                actualPayRate: instance.ActualPayRate ?? instance.SubmissionRate ?? '',
                                expenses: instance.Expenses != null ? instance.Expenses : '',
                                margin: resolvedEditMargin,
                                referralSource: rawRef,
                                referralSources: activeRefs.map(r => r.displayName),
                                referrals: activeRefs,
                                referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : (instance.ReferralFee ?? ''),
                                notes: instance.RateNotes || instance.Notes || deployment?.RateNotes || deployment?.Notes || ''
                              });
                            }
                          }}
                          title="Edit Rate & Referral Details"
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="onb-rate-cancel-btn"
                          onClick={() => {
                            setIsEditingRates(false);
                            setRateReferralSearchText('');
                            if (instance) {
                              const rawRef = instance.ReferralSource || '';
                              const activeRefs = Array.isArray(instance.referrals) && instance.referrals.length > 0
                                ? instance.referrals.map(r => ({
                                    submissionReferralId: r.SubmissionReferralId,
                                    referralCompanyId: r.ReferralCompanyId,
                                    displayName: r.DisplayName || r.CompanyName,
                                    companyName: r.CompanyName,
                                    referralFee: r.ReferralFee != null ? r.ReferralFee : '',
                                    referralNetTermsDays: r.ReferralNetTermsDays != null ? r.ReferralNetTermsDays : '',
                                    notes: r.Notes || ''
                                  }))
                                : (rawRef ? rawRef.split(',').map(s => ({
                                    referralCompanyId: null,
                                    displayName: s.trim(),
                                    companyName: '',
                                    referralFee: instance.ReferralFee || '',
                                    referralNetTermsDays: '',
                                    notes: ''
                                  })).filter(r => r.displayName) : []);

                              const totalRefFee = activeRefs.length > 0 
                                ? activeRefs.reduce((acc, r) => acc + (parseFloat(r.referralFee) || 0), 0)
                                : (parseFloat(instance.ReferralFee) || 0);

                              const computedM = calculateMargin(
                                instance.RateAfterVms,
                                instance.SupplierRateAfterVms ?? (instance.SupplierRate && instance.SupplierVmsPct ? (instance.SupplierRate * (1 - instance.SupplierVmsPct / 100)).toFixed(2) : ''),
                                instance.ActualPayRate ?? instance.SubmissionRate,
                                instance.EmploymentType,
                                activeRefs,
                                totalRefFee,
                                instance.Expenses,
                                instance.ClientRate ?? instance.SubmissionRate,
                                instance.CompliancePayRate,
                                instance.VmsPct,
                                instance.SupplierRate,
                                instance.SupplierVmsPct
                              );

                              const resolvedEditMargin = (computedM != null && computedM !== '')
                                ? computedM
                                : (instance.Margin != null && Number(instance.Margin) > 0 && Number(instance.RateAfterVms || instance.ClientRate) > 0 ? Number(instance.Margin).toFixed(2) : '0.00');

                              setRateFormData({
                                clientRate: instance.ClientRate ?? instance.SubmissionRate ?? '',
                                vmsPct: instance.VmsPct ?? '',
                                rateAfterVms: instance.RateAfterVms ?? '',
                                supplierRate: instance.SupplierRate ?? '',
                                supplierVmsPct: instance.SupplierVmsPct ?? '',
                                supplierRateAfterVms: instance.SupplierRateAfterVms ?? (instance.SupplierRate && instance.SupplierVmsPct ? (instance.SupplierRate * (1 - instance.SupplierVmsPct / 100)).toFixed(2) : ''),
                                supplierNetTermsDays: instance.SupplierNetTermsDays ?? '',
                                compliancePayRate: instance.CompliancePayRate ?? '',
                                actualPayRate: instance.ActualPayRate ?? instance.SubmissionRate ?? '',
                                expenses: instance.Expenses != null ? instance.Expenses : '',
                                margin: resolvedEditMargin,
                                referralSource: rawRef,
                                referralSources: activeRefs.map(r => r.displayName),
                                referrals: activeRefs,
                                referralFee: totalRefFee > 0 ? totalRefFee.toFixed(2) : (instance.ReferralFee ?? ''),
                                notes: instance.RateNotes || instance.Notes || deployment?.RateNotes || deployment?.Notes || ''
                              });
                            }
                          }}
                          title="Cancel Editing"
                        >
                          ✕ Cancel
                        </button>
                      )}
                    </div>

                    {isEditingRates ? (
                      <div className="onb-rates-edit-form">
                        <div className="onb-rate-edit-field">
                          <label>Client Rate ($/hr)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={rateFormData.clientRate}
                            onChange={(e) => handleRateChange('clientRate', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="onb-rate-edit-field">
                          <label>VMS (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={rateFormData.vmsPct}
                            onChange={(e) => handleRateChange('vmsPct', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="onb-rate-edit-field">
                          <label>Rate After VMS ($/hr)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={rateFormData.rateAfterVms}
                            onChange={(e) => handleRateChange('rateAfterVms', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>

                        {/* Divider Line 1: Under Rate After VMS */}
                        <div className="onb-rates-divider"></div>

                        {!isW2 && (
                          <>
                            <div className="onb-rate-edit-field">
                              <label>Supplier Rate ($/hr)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={rateFormData.supplierRate}
                                onChange={(e) => handleRateChange('supplierRate', e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="onb-rate-edit-field">
                              <label>Supplier VMS (%)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={rateFormData.supplierVmsPct}
                                onChange={(e) => handleRateChange('supplierVmsPct', e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="onb-rate-edit-field">
                              <label>Supplier Rate After VMS ($/hr)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={rateFormData.supplierRateAfterVms}
                                onChange={(e) => handleRateChange('supplierRateAfterVms', e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="onb-rate-edit-field">
                              <label>Supplier Net Terms (Days)</label>
                              <input
                                type="number"
                                value={rateFormData.supplierNetTermsDays || ''}
                                onChange={(e) => handleRateChange('supplierNetTermsDays', e.target.value)}
                                placeholder="e.g. 30, 45, 60"
                              />
                            </div>

                            {/* Divider Line 2: Under Supplier Net Terms */}
                            <div className="onb-rates-divider"></div>
                          </>
                        )}

                        <div className="onb-rate-edit-field">
                          <label>Candidate Compliance Pay Rate ($/hr)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={rateFormData.compliancePayRate}
                            onChange={(e) => handleRateChange('compliancePayRate', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="onb-rate-edit-field highlight">
                          <label>Candidate Actual Pay Rate ($/hr)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={rateFormData.actualPayRate}
                            onChange={(e) => handleRateChange('actualPayRate', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>

                        {/* Divider Line 3: Above Referral Source */}
                        <div className="onb-rates-divider"></div>

                        {/* Multi-assign Referral Source with separate Rate & Notes */}
                        <div className="onb-rate-edit-field" style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label>Referral Source(s)</label>
                            <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Assign multiple with individual fees</span>
                          </div>

                          {/* Search & Add Dropdown - wrapped in relative container so dropdown anchors here */}
                          <div style={{ position: 'relative', marginBottom: '8px' }}>
                            <div className="onb-referral-chips-input-container">
                              <input
                                type="text"
                                value={rateReferralSearchText}
                                onChange={(e) => {
                                  setRateReferralSearchText(e.target.value);
                                  setShowRateReferralDropdown(true);
                                }}
                                onClick={() => setShowRateReferralDropdown(true)}
                                onBlur={() => setTimeout(() => setShowRateReferralDropdown(false), 200)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && rateReferralSearchText.trim()) {
                                    e.preventDefault();
                                    handleAddReferralSource(rateReferralSearchText.trim());
                                  }
                                }}
                                placeholder="+ Search company or add referral person..."
                                autoComplete="off"
                              />
                            </div>

                            {showRateReferralDropdown && (
                              <div className="onb-referral-dropdown-menu">
                                {rateReferralSearchText.trim() && !getFilteredCrmCompanies(rateReferralSearchText).some(c => c.displayName.toLowerCase() === rateReferralSearchText.trim().toLowerCase()) && (
                                <div
                                  className="onb-referral-dropdown-item custom-add"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleAddReferralSource(rateReferralSearchText.trim());
                                  }}
                                >
                                  <span style={{ fontWeight: 600, color: '#0f766e' }}>+ Add "{rateReferralSearchText.trim()}"</span>
                                </div>
                              )}
                              {getFilteredCrmCompanies(rateReferralSearchText).map((clientObj, index) => {
                                const isSelected = (rateFormData.referrals || []).some(r => (r.displayName || '').toLowerCase() === clientObj.displayName.toLowerCase());
                                return (
                                  <div
                                    key={index}
                                    className={`onb-referral-dropdown-item ${isSelected ? 'selected' : ''}`}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      if (!isSelected) {
                                        handleAddReferralSource(clientObj);
                                      }
                                    }}
                                  >
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                      <span className="onb-ref-item-title">{clientObj.displayName}</span>
                                      {clientObj.companyName && (
                                        <span className="onb-ref-item-sub">{clientObj.companyName}</span>
                                      )}
                                    </div>
                                    {isSelected && <span className="onb-ref-selected-check">✓</span>}
                                  </div>
                                );
                              })}
                              </div>
                            )}
                          </div>

                          {/* Individual Referral Cards List */}
                          {(rateFormData.referrals || []).length > 0 && (
                            <div className="onb-referral-cards-container">
                              {rateFormData.referrals.map((refItem, rIdx) => (
                                <div key={rIdx} className="onb-referral-item-card">
                                  <div className="onb-referral-item-card-header">
                                    <div className="onb-referral-item-title-wrap">
                                      <UserCheck size={12} color="#0f766e" />
                                      <span className="onb-referral-item-name">{refItem.displayName}</span>
                                      {refItem.companyName && refItem.companyName !== refItem.displayName && (
                                        <span className="onb-referral-item-sub">({refItem.companyName})</span>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      className="onb-referral-remove-btn"
                                      onClick={() => handleRemoveReferralSource(rIdx)}
                                      title={`Remove ${refItem.displayName}`}
                                    >
                                      ✕ Remove
                                    </button>
                                  </div>

                                  <div className="onb-referral-item-fields-grid">
                                    <div className="onb-referral-input-wrapper">
                                      <label>Fee / Rate ($)</label>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={refItem.referralFee}
                                        onChange={(e) => handleReferralItemChange(rIdx, 'referralFee', e.target.value)}
                                      />
                                    </div>
                                    <div className="onb-referral-input-wrapper">
                                      <label>Net Terms (Days)</label>
                                      <input
                                        type="number"
                                        placeholder="e.g. 30, 45, 60"
                                        value={refItem.referralNetTermsDays || ''}
                                        onChange={(e) => handleReferralItemChange(rIdx, 'referralNetTermsDays', e.target.value)}
                                      />
                                    </div>
                                    <div className="onb-referral-input-wrapper">
                                      <label>Referrer Notes</label>
                                      <input
                                        type="text"
                                        placeholder="e.g. Terms, payout conditions..."
                                        value={refItem.notes}
                                        onChange={(e) => handleReferralItemChange(rIdx, 'notes', e.target.value)}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {/* Total Referral Summary */}
                              <div className="onb-referral-total-summary">
                                <span className="onb-referral-total-label">Total Referral Cost:</span>
                                <span className="onb-referral-total-val">
                                  ${Number(rateFormData.referralFee || 0).toFixed(2)}/hr
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Expenses Field in Edit Mode */}
                        <div className="onb-rate-edit-field" style={{ marginTop: '6px' }}>
                          <label>Expenses ($/hr)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={rateFormData.expenses}
                            onChange={(e) => handleRateChange('expenses', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>

                        {/* Auto-computed Profit Margin Preview - Moved immediately before Notes */}
                        <div className="onb-rate-edit-field highlight" style={{ background: '#f0fdf4', padding: '6px 10px', borderRadius: '6px', border: '1px solid #bbf7d0', marginTop: '6px', marginBottom: '6px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ color: '#166534', fontWeight: 700, margin: 0 }}>Profit Margin ($/hr) — Auto Computed</label>
                            <span style={{ fontSize: '0.68rem', color: '#15803d', fontWeight: 600 }}>
                              {isW2 ? '(W2 Net: Rate After VMS - Pay Rate - expenses - Referrals)' : '(C2C Net: Rate After VMS - Supplier Rate After VMS - expenses - Referrals)'}
                            </span>
                          </div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: '#15803d', marginTop: '3px' }}>
                            ${rateFormData.margin != null && rateFormData.margin !== '' ? Number(rateFormData.margin).toFixed(2) : '0.00'}
                          </div>
                        </div>

                        <div className="onb-rates-divider"></div>

                        {/* Dedicated General Notes Section in Edit Mode */}
                        <div className="onb-rate-edit-field">
                          <label>General Placement & Rate Notes</label>
                          <textarea
                            rows={3}
                            className="onb-rate-notes-textarea"
                            value={rateFormData.notes}
                            onChange={(e) => handleRateChange('notes', e.target.value)}
                            placeholder="Add general placement notes or rate comments..."
                          />
                        </div>

                        <div className="onb-rates-edit-actions">
                          <button
                            type="button"
                            className="onb-rate-save-btn"
                            disabled={savingRates}
                            onClick={handleSaveRates}
                          >
                            <CheckCircle size={13} /> {savingRates ? 'Saving...' : 'Save Details'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="onb-rates-breakdown-list">
                        <div className="onb-rate-item">
                          <span className="onb-rate-label">Client Rate</span>
                          <span className="onb-rate-val">
                            {instance?.ClientRate ? `$${Number(instance.ClientRate).toFixed(2)}/hr` : (instance?.SubmissionRate || 'Negotiated')}
                          </span>
                        </div>
                        <div className="onb-rate-item">
                          <span className="onb-rate-label">VMS %</span>
                          <span className="onb-rate-val">
                            {instance?.VmsPct != null && instance?.VmsPct !== '' ? `${instance.VmsPct}%` : '0%'}
                          </span>
                        </div>
                        <div className="onb-rate-item">
                          <span className="onb-rate-label">Rate After VMS</span>
                          <span className="onb-rate-val">
                            {instance?.RateAfterVms ? `$${Number(instance.RateAfterVms).toFixed(2)}/hr` : '-'}
                          </span>
                        </div>

                        {/* Divider Line 1: Under Rate After VMS */}
                        <div className="onb-rates-divider"></div>

                        {!isW2 && (
                          <>
                            <div className="onb-rate-item">
                              <span className="onb-rate-label">Supplier Rate</span>
                              <span className="onb-rate-val">
                                {instance?.SupplierRate ? `$${Number(instance.SupplierRate).toFixed(2)}/hr` : '-'}
                              </span>
                            </div>
                            <div className="onb-rate-item">
                              <span className="onb-rate-label">Supplier VMS %</span>
                              <span className="onb-rate-val">
                                {instance?.SupplierVmsPct != null && instance?.SupplierVmsPct !== '' ? `${instance.SupplierVmsPct}%` : '0%'}
                              </span>
                            </div>
                            <div className="onb-rate-item">
                              <span className="onb-rate-label">Supplier Rate After VMS</span>
                              <span className="onb-rate-val" style={{ color: '#0f766e', fontWeight: 650 }}>
                                {(() => {
                                  const sAfter = instance?.SupplierRateAfterVms ?? (instance?.SupplierRate && instance?.SupplierVmsPct ? (instance.SupplierRate * (1 - instance.SupplierVmsPct / 100)) : null);
                                  return sAfter ? `$${Number(sAfter).toFixed(2)}/hr` : '-';
                                })()}
                              </span>
                            </div>
                            <div className="onb-rate-item">
                              <span className="onb-rate-label">Supplier Net Terms</span>
                              <span className="onb-rate-val">
                                {instance?.SupplierNetTermsDays ? `${instance.SupplierNetTermsDays} Days` : '-'}
                              </span>
                            </div>

                            {/* Divider Line 2: Under Supplier Net Terms */}
                            <div className="onb-rates-divider"></div>
                          </>
                        )}

                        <div className="onb-rate-item">
                          <span className="onb-rate-label">Candidate Compliance Pay Rate</span>
                          <span className="onb-rate-val">
                            {instance?.CompliancePayRate ? `$${Number(instance.CompliancePayRate).toFixed(2)}/hr` : '-'}
                          </span>
                        </div>
                        <div className="onb-rate-item highlight">
                          <span className="onb-rate-label">Candidate Actual Pay Rate</span>
                          <span className="onb-rate-val highlight">
                            {(() => {
                              if (instance?.ActualPayRate != null && instance?.ActualPayRate !== '') {
                                return `$${Number(instance.ActualPayRate).toFixed(2)}/hr`;
                              }
                              return instance?.SubmissionRate || '-';
                            })()}
                          </span>
                        </div>

                        {/* Divider Line 3: Above Referral Breakdown */}
                        <div className="onb-rates-divider"></div>
                        {(() => {
                          const refsList = Array.isArray(instance?.referrals) && instance.referrals.length > 0
                            ? instance.referrals
                            : null;

                          const assignedList = (instance?.ReferralSource || '')
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean);

                          const hasStructuredRefs = refsList && refsList.length > 0;
                          const hasAssignedList = assignedList.length > 0;
                          const hasAnyReferral = hasStructuredRefs || hasAssignedList;

                          if (!hasAnyReferral) {
                            return (
                              <div className="onb-rate-item">
                                <span className="onb-rate-label">Referral Breakdown</span>
                                <span className="onb-rate-val">None</span>
                              </div>
                            );
                          }

                          const totalRefFee = hasStructuredRefs
                            ? refsList.reduce((sum, r) => sum + (parseFloat(r.ReferralFee) || 0), 0)
                            : (parseFloat(instance?.ReferralFee) || 0);

                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
                              <div className="onb-rate-item" style={{ marginBottom: 0 }}>
                                <span className="onb-rate-label">Referral Breakdown</span>
                                <span className="onb-rate-val" style={{ color: '#0f766e', fontWeight: 700 }}>
                                  {totalRefFee > 0 ? `$${totalRefFee.toFixed(2)}/hr` : (hasAssignedList ? 'Assigned' : '-')}
                                </span>
                              </div>

                              {hasStructuredRefs ? (
                                <div className="onb-referral-view-list" style={{ marginTop: 0 }}>
                                  {refsList.map((ref, rIdx) => (
                                    <div key={rIdx} className="onb-referral-view-row">
                                      <div className="onb-referral-view-header">
                                        <span className="onb-referral-view-name">
                                          <UserCheck size={12} color="#0f766e" />
                                          {ref.DisplayName || ref.CompanyName}
                                          {ref.ReferralNetTermsDays != null && ref.ReferralNetTermsDays !== '' && (
                                            <span className="onb-referral-terms-tag">
                                              Net Terms: {ref.ReferralNetTermsDays} Days
                                            </span>
                                          )}
                                        </span>
                                        <span className="onb-referral-view-fee">
                                          {ref.ReferralFee != null && ref.ReferralFee !== '' ? `$${Number(ref.ReferralFee).toFixed(2)}/hr` : '-'}
                                        </span>
                                      </div>
                                      {ref.Notes && (
                                        <div className="onb-referral-view-notes">
                                          "{ref.Notes}"
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {refsList.length > 1 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: '#f0fdfa', borderRadius: '4px', marginTop: '2px', border: '1px solid #ccfbf1' }}>
                                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f766e' }}>Total Fee:</span>
                                      <span style={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f766e' }}>
                                        ${totalRefFee.toFixed(2)}/hr
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="onb-referral-chips-wrap" style={{ justifyContent: 'flex-start', maxWidth: '100%', marginTop: '2px' }}>
                                  {assignedList.map((refItem, rIdx) => (
                                    <span key={rIdx} className="onb-referral-pill">
                                      <UserCheck size={11} />
                                      <span>{refItem}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* Expenses Row in View Mode */}
                        <div className="onb-rate-item">
                          <span className="onb-rate-label">Expenses</span>
                          <span className="onb-rate-val">
                            {instance?.Expenses != null && instance?.Expenses !== '' && Number(instance.Expenses) > 0 ? `$${Number(instance.Expenses).toFixed(2)}/hr` : '-'}
                          </span>
                        </div>

                        {/* Margin in View Mode - Placed immediately before Notes */}
                        <div className="onb-rate-item highlight" style={{ background: '#f0fdf4', borderRadius: '6px', padding: '6px 8px', marginTop: '6px', marginBottom: '6px', border: '1px solid #bbf7d0' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span className="onb-rate-label" style={{ color: '#166534', fontWeight: 700 }}>Profit Margin</span>
                            <span style={{ fontSize: '0.68rem', color: '#15803d' }}>
                              {isW2 ? '(W2 Net: Rate After VMS - Pay Rate - expenses - Referrals)' : '(C2C Net: Rate After VMS - Supplier Rate After VMS - expenses - Referrals)'}
                            </span>
                          </div>
                          <span className="onb-rate-val highlight" style={{ color: '#15803d', fontWeight: 800, fontSize: '0.94rem' }}>
                            {(() => {
                              const calcM = calculateMargin(
                                instance?.RateAfterVms,
                                instance?.SupplierRateAfterVms ?? (instance?.SupplierRate && instance?.SupplierVmsPct ? (instance.SupplierRate * (1 - instance.SupplierVmsPct / 100)) : null),
                                instance?.ActualPayRate ?? instance?.SubmissionRate,
                                instance?.EmploymentType,
                                instance?.referrals,
                                instance?.ReferralFee,
                                instance?.Expenses,
                                instance?.ClientRate,
                                instance?.CompliancePayRate,
                                instance?.VmsPct,
                                instance?.SupplierRate,
                                instance?.SupplierVmsPct
                              );
                              const hasBillRate = Number(instance?.RateAfterVms || instance?.ClientRate) > 0;
                              if (calcM != null && hasBillRate) return `$${Number(calcM).toFixed(2)}/hr`;
                              if (instance?.Margin != null && instance?.Margin !== '' && Number(instance.Margin) > 0 && hasBillRate) {
                                return `$${Number(instance.Margin).toFixed(2)}/hr`;
                              }
                              if (hasBillRate && calcM === '0.00') return '$0.00/hr';
                              return '-';
                            })()}
                          </span>
                        </div>

                        {/* Dedicated Notes Section in View Mode */}
                        <div className="onb-rates-divider"></div>
                        <div className="onb-rate-notes-box">
                          <div className="onb-rate-notes-title">
                            <FileText size={12} color="#0f766e" />
                            <span>Notes</span>
                          </div>
                          <div className="onb-rate-notes-body">
                            {instance?.RateNotes || instance?.Notes || deployment?.RateNotes || deployment?.Notes ? (
                              instance?.RateNotes || instance?.Notes || deployment?.RateNotes || deployment?.Notes
                            ) : (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No notes added</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column: 13 Steps Checklist */}
                  <div className="onb-single-tab-pane" style={{ flex: 1 }}>
                    {isChecklistEditMode && instance?.StatusCode === 'COMPLETED' && (
                      <div className="onb-checklist-edit-banner">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Edit2 size={15} color="#2563eb" style={{ flexShrink: 0 }} />
                          <span>
                            <strong>Checklist Edit Mode Active:</strong> You can edit step statuses, subtasks, assignees, and attach or delete documents. Click <strong>"Done Editing"</strong> when finished.
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsChecklistEditMode(false)}
                          style={{
                            background: '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            marginLeft: '12px'
                          }}
                        >
                          Done Editing
                        </button>
                      </div>
                    )}

                    <div className="onb-column-header client-header" style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Building2 size={16} color="#0369a1" />
                        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                          Onboarding Steps & Compliance ({clientName})
                        </span>
                      </div>
                      <span className="onb-column-count-chip client">
                        {completedStepsCount} / {visibleSteps.length} Completed
                      </span>
                    </div>

                    <div className="onb-steps-scroll-list">
                      {[...visibleSteps].sort((a, b) => (a.StepSequence || 0) - (b.StepSequence || 0)).map((step, idx) => renderStepRow(step, idx))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'NOTES' && (
                <div style={{ background: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #e5e7eb' }}>
                  {/* Top Header: Title on left, + Add Note button on right */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isAddingNote ? '16px' : '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>Candidate Notes</h3>
                    {!isAddingNote && (
                      <button
                        type="button"
                        onClick={() => setIsAddingNote(true)}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#229C8B', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
                      >
                        <Plus size={16} /> Add Note
                      </button>
                    )}
                  </div>

                  {/* Collapsible Add Note Form */}
                  {isAddingNote && (
                    <div style={{ marginBottom: '24px', background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                      <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Category</label>
                        <select
                          value={newNoteCategory}
                          onChange={(e) => setNewNoteCategory(e.target.value)}
                          style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', fontSize: '14px', width: '220px' }}
                        >
                          {Object.entries(NOTE_CATEGORY_LABELS).map(([val, lbl]) => (
                            <option key={val} value={val}>{lbl}</option>
                          ))}
                        </select>
                      </div>
                      <textarea
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                        placeholder="Type your note here..."
                        style={{ width: '100%', minHeight: '100px', padding: '12px', borderRadius: '4px', border: '1px solid #d1d5db', marginBottom: '12px', resize: 'vertical', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => { setIsAddingNote(false); setNewNoteText(''); setNewNoteCategory('GENERAL'); }}
                          style={{ padding: '8px 16px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveNote}
                          disabled={savingNote || !newNoteText.trim()}
                          style={{ padding: '8px 16px', background: savingNote || !newNoteText.trim() ? '#9ca3af' : '#229C8B', color: '#fff', border: 'none', borderRadius: '4px', cursor: savingNote || !newNoteText.trim() ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '14px' }}
                        >
                          {savingNote ? 'Saving...' : 'Save Note'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Notes Timeline List */}
                  {candidateNotes.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                      {candidateNotes.map(note => {
                        const catKey = (note.NoteTag || 'GENERAL').toUpperCase().replace(/\s+/g, '_');
                        const catStyle = NOTE_CATEGORY_COLORS[catKey] || NOTE_CATEGORY_COLORS.GENERAL;
                        const catLabel = NOTE_CATEGORY_LABELS[catKey] || note.NoteTag || 'General';

                        return (
                          <div key={note.OnboardingNoteId} style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                            {/* Left Column: Name & Date/Time */}
                            <div style={{ flex: '0 0 140px', width: '140px', textAlign: 'left', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <strong style={{ color: '#111827', fontSize: '13px' }}>
                                {(() => {
                                  const raw = note.CreatedByUserName || 'Team Member';
                                  if (raw.includes('@')) {
                                    if (raw.toLowerCase().includes('prabhu')) return 'Prabhu Chinnaswamy';
                                    const clean = raw.split('@')[0].replace(/[._-]/g, ' ');
                                    return clean.replace(/\b\w/g, c => c.toUpperCase());
                                  }
                                  return raw;
                                })()}
                              </strong>
                              <span style={{ color: '#6b7280', fontSize: '12px', lineHeight: '1.3' }}>
                                {new Date(note.CreatedAtUtc).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                              </span>
                            </div>

                            {/* Right Column: Note content & Actions */}
                            <div style={{ flex: 1, paddingLeft: '12px', borderLeft: '1px solid #e5e7eb', position: 'relative', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  backgroundColor: catStyle.bg,
                                  color: catStyle.color,
                                  border: `1px solid ${catStyle.border}`
                                }}>
                                  {catLabel}
                                </span>

                                {editingNoteId !== note.OnboardingNoteId && (
                                  <div style={{ display: 'flex', gap: '14px' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingNoteId(note.OnboardingNoteId);
                                        setEditingNoteText(note.NoteText);
                                        setEditingNoteCategory(catKey);
                                      }}
                                      style={{ background: 'none', border: 'none', color: '#229C8B', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: 500, padding: 0 }}
                                    >
                                      <Edit size={14} /> Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteNote(note.OnboardingNoteId)}
                                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: 500, padding: 0 }}
                                    >
                                      <Trash2 size={14} /> Delete
                                    </button>
                                  </div>
                                )}
                              </div>

                              {editingNoteId === note.OnboardingNoteId ? (
                                <div style={{ marginTop: '4px' }}>
                                  <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Category</label>
                                    <select
                                      value={editingNoteCategory}
                                      onChange={(e) => setEditingNoteCategory(e.target.value)}
                                      style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db', background: '#fff', fontSize: '13px', width: '200px' }}
                                    >
                                      {Object.entries(NOTE_CATEGORY_LABELS).map(([val, lbl]) => (
                                        <option key={val} value={val}>{lbl}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <textarea
                                    value={editingNoteText}
                                    onChange={(e) => setEditingNoteText(e.target.value)}
                                    style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '4px', border: '1px solid #d1d5db', marginBottom: '8px', fontSize: '13.5px', boxSizing: 'border-box' }}
                                  />
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingNoteId(null); setEditingNoteText(''); setEditingNoteCategory('GENERAL'); }}
                                      style={{ padding: '6px 12px', fontSize: '13px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleUpdateNote(note.OnboardingNoteId)}
                                      disabled={savingNote || !editingNoteText.trim()}
                                      style={{ padding: '6px 14px', fontSize: '13px', background: savingNote || !editingNoteText.trim() ? '#9ca3af' : '#229C8B', color: '#fff', border: 'none', borderRadius: '4px', cursor: savingNote || !editingNoteText.trim() ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                                    >
                                      {savingNote ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div style={{ whiteSpace: 'pre-wrap', color: '#111827', lineHeight: '1.5', fontSize: '14px' }}>
                                  {note.NoteText}
                                </div>
                              )}

                              {note.UpdatedAtUtc && (
                                <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#9ca3af', marginTop: '2px' }}>
                                  Edited — {new Date(note.UpdatedAtUtc).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
                      <p style={{ margin: 0 }}>No notes have been added yet.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* MODAL FOOTER */}
        <div className="onb-modal-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '0.82rem', color: '#475569' }}>
              <strong>Progress:</strong> {completedStepsCount} of {visibleSteps.length} steps completed ({progressPercent}%)
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {!isTerminatedOrCompleted && (
              <>
                <button
                  type="button"
                  className="onb-btn-outline"
                  style={{ borderColor: '#ef4444', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => handleOpenOutcomeModal('REJECT')}
                >
                  <XCircle size={15} />
                  Reject
                </button>
                <button
                  type="button"
                  className="onb-btn-outline"
                  style={{ borderColor: '#f59e0b', color: '#d97706', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={() => handleOpenOutcomeModal('WITHDRAWN')}
                >
                  <Ban size={15} />
                  Withdrawn
                </button>
              </>
            )}
            <button type="button" className="onb-btn-outline" onClick={onClose}>
              Close
            </button>
            {instance?.StatusCode === 'COMPLETED' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  className={`onb-edit-checklist-toggle-btn ${isChecklistEditMode ? 'active' : ''}`}
                  onClick={() => setIsChecklistEditMode(prev => !prev)}
                  title={isChecklistEditMode ? "Finish editing and lock checklist" : "Unlock checklist to edit statuses, subtasks, assignees & attachments"}
                >
                  {isChecklistEditMode ? (
                    <>
                      <CheckCircle size={14} />
                      <span>Done Editing</span>
                    </>
                  ) : (
                    <>
                      <Edit2 size={13} />
                      <span>Edit Checklist</span>
                    </>
                  )}
                </button>
                <div style={{ color: '#059669', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem' }}>
                  <CheckCircle size={16} /> Onboarding Completed
                </div>
              </div>
            ) : instance?.StatusCode === 'WITHDRAWN' ? (
              <>
                <div style={{ color: '#d97706', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem' }}>
                  <Ban size={16} /> Candidate Withdrawn
                </div>
                <button
                  type="button"
                  className="onb-btn-outline"
                  style={{ borderColor: '#2563eb', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                  onClick={() => handleOpenRevokeModal('WITHDRAWN')}
                >
                  <RotateCcw size={15} /> Revoke Withdrawn
                </button>
              </>
            ) : instance?.StatusCode === 'REJECTED' ? (
              <>
                <div style={{ color: '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem' }}>
                  <XCircle size={16} /> Candidate Rejected
                </div>
                <button
                  type="button"
                  className="onb-btn-outline"
                  style={{ borderColor: '#2563eb', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                  onClick={() => handleOpenRevokeModal('REJECTED')}
                >
                  <RotateCcw size={15} /> Revoke Reject
                </button>
              </>
            ) : instance?.StatusCode === 'CANCELLED' ? (
              <>
                <div style={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem' }}>
                  <XCircle size={16} /> Candidate Cancelled
                </div>
                <button
                  type="button"
                  className="onb-btn-outline"
                  style={{ borderColor: '#2563eb', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                  onClick={() => handleOpenRevokeModal('CANCELLED')}
                >
                  <RotateCcw size={15} /> Revoke Cancelled
                </button>
              </>
            ) : (
              <button
                type="button"
                className="onb-btn-success"
                disabled={completing}
                onClick={() => handleCompleteClick(false)}
              >
                <CheckCircle size={16} />
                {completing ? 'Completing...' : 'Complete Onboarding'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── ACTION MODAL 1: Create / Link Employee Modal ── */}
      {showEmpModal && (
        <div className="onb-submodal-overlay">
          <div className="onb-submodal-content" style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0369a1' }}>
                <UserCheck size={20} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                  {data?.linkedEmployee || steps.some(s => s.StepCode === 'CREATE_LINK_EMP' && s.ResultRefId) ? 'Update / Relink Employee' : 'Create / Link Employee'}
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  className="onb-btn-add-emp"
                  onClick={() => setShowAddEmployeeModal(true)}
                  title="Add New Employee in Timesheet Management"
                >
                  <Plus size={13} /> Add Employee
                </button>
                <button type="button" className="onb-close-btn" onClick={() => setShowEmpModal(false)}>
                  <X size={18} />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateEmployeeSubmit}>
              <div className="onb-form-group">
                <label>Candidate Name</label>
                <input type="text" className="onb-input-disabled" value={candidateFullName} disabled />
              </div>

              <div className="onb-form-row">
                <div className="onb-form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    className="onb-input"
                    value={empFormData.email}
                    onChange={(e) => setEmpFormData({ ...empFormData, email: e.target.value })}
                    placeholder="e.g. candidate@example.com"
                  />
                </div>
                <div className="onb-form-group">
                  <label>Mobile Number</label>
                  <input
                    type="text"
                    className="onb-input"
                    value={empFormData.phone}
                    onChange={(e) => setEmpFormData({ ...empFormData, phone: e.target.value })}
                    placeholder="e.g. +1 555-0199"
                  />
                </div>
              </div>

              <div className="onb-form-row">
                <div className="onb-form-group">
                  <label>Employee Type</label>
                  <select
                    className="onb-select"
                    value={empFormData.employeeType}
                    onChange={(e) => setEmpFormData({ ...empFormData, employeeType: e.target.value })}
                  >
                    <option value="DEPLOYED">DEPLOYED</option>
                    <option value="C2C">C2C</option>
                    <option value="1099">1099</option>
                    <option value="W2">W2</option>
                    <option value="INTERNAL">INTERNAL</option>
                  </select>
                </div>

                <div className="onb-form-group">
                  <label>Employee Code <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    className="onb-input"
                    placeholder="Type name or code (e.g. Dylan, PC1042)"
                    value={empFormData.employeeCode}
                    onChange={(e) => {
                      setEmpFormData({ ...empFormData, employeeCode: e.target.value });
                      setShowEmpSuggestions(true);
                    }}
                    onFocus={() => {
                      if (empSearchResults.length > 0) setShowEmpSuggestions(true);
                    }}
                    required
                  />
                </div>
              </div>

              {/* Employee Code / Name Live Suggestions Box (Open Box directly above Start Date) */}
              {showEmpSuggestions && empFormData.employeeCode && (
                <div style={{
                  marginBottom: '14px',
                  background: '#ffffff',
                  borderRadius: '8px',
                  border: '1.5px solid #0d9488',
                  boxShadow: '0 4px 14px rgba(13, 148, 136, 0.18)',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    padding: '7px 12px',
                    background: '#f0fdfa',
                    borderBottom: '1px solid #ccfbf1',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: '#0f766e'
                  }}>
                    <span>MATCHING EMPLOYEES IN TIMESHEET MANAGEMENT ({empSearchResults.length})</span>
                    <button
                      type="button"
                      onClick={() => setShowEmpSuggestions(false)}
                      style={{ background: 'none', border: 'none', color: '#0f766e', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}
                    >
                      Close ✕
                    </button>
                  </div>

                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {isSearchingEmp && (
                      <div style={{ padding: '10px 14px', fontSize: '0.82rem', color: '#64748b' }}>
                        Searching employees from DB...
                      </div>
                    )}
                    {!isSearchingEmp && empSearchResults.length === 0 && (
                      <div style={{ padding: '10px 14px', fontSize: '0.82rem', color: '#94a3b8' }}>
                        No employee found matching "{empFormData.employeeCode}". You can click "+ Add Employee" above to create one.
                      </div>
                    )}
                    {!isSearchingEmp && empSearchResults.map((emp) => (
                      <div
                        key={emp.EmployeeId}
                        onClick={() => handleSelectEmployeeMatch(emp)}
                        style={{
                          padding: '9px 12px',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.15s',
                          backgroundColor: empFormData.employeeCode === emp.EmployeeCode ? '#ecfdf5' : '#ffffff'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdfa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = empFormData.employeeCode === emp.EmployeeCode ? '#ecfdf5' : '#ffffff'}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <strong style={{ fontSize: '0.88rem', color: '#0f172a' }}>{emp.FullName}</strong>
                            <span style={{
                              fontSize: '0.72rem',
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              backgroundColor: '#f1f5f9',
                              color: '#0f766e',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              border: '1px solid #cbd5e1'
                            }}>
                              {emp.EmployeeCode}
                            </span>
                            <span style={{
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: '999px',
                              backgroundColor: emp.EmployeeType === 'C2C' ? '#fef3c7' : '#e0f2fe',
                              color: emp.EmployeeType === 'C2C' ? '#92400e' : '#0369a1'
                            }}>
                              {emp.EmployeeType}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: '2px' }}>
                            {emp.Email || 'No email'} {emp.Phone ? `• ${emp.Phone}` : ''}
                          </div>
                        </div>
                        <span style={{ fontSize: '0.74rem', color: '#0d9488', fontWeight: 600 }}>Select ➔</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedLinkedEmp && empFormData.employeeCode === selectedLinkedEmp.EmployeeCode && (
                <div style={{
                  fontSize: '0.78rem',
                  color: '#0f766e',
                  fontWeight: 600,
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>✓ Linked to DB Employee: <strong>{selectedLinkedEmp.FullName}</strong> ({selectedLinkedEmp.EmployeeCode})</span>
                </div>
              )}

              <div className="onb-form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  className="onb-input"
                  value={empFormData.startDate}
                  onChange={(e) => setEmpFormData({ ...empFormData, startDate: e.target.value })}
                  required
                />
              </div>

              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '6px',
                padding: '10px 12px',
                fontSize: '0.78rem',
                color: '#1e40af',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Info size={16} style={{ flexShrink: 0 }} />
                <span>
                  <strong>Note:</strong> Candidate must be added in Timesheet Management first. Click <strong>"+ Add Employee"</strong> above to register the employee before linking.
                </span>
              </div>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="onb-btn-outline" onClick={() => setShowEmpModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="onb-btn-primary" disabled={savingEmp}>
                  <UserCheck size={14} /> {savingEmp ? 'Saving...' : (data?.linkedEmployee || steps.some(s => s.StepCode === 'CREATE_LINK_EMP' && s.ResultRefId) ? 'Update Employee' : 'Link Employee')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ACTION MODAL 1.1: Add New Employee Modal (Image 3) ── */}
      {showAddEmployeeModal && (
        <EmployeeModal
          isOpen={showAddEmployeeModal}
          mode="add"
          selectedCompany={{
            id: instance?.EntityId || 4,
            name: instance?.EntityName || 'Prophecy Consulting INC'
          }}
          employeeData={{
            Name: candidateFullName,
            name: candidateFullName,
            Email: empFormData.email || instance?.CandidateEmail || '',
            email: empFormData.email || instance?.CandidateEmail || '',
            Phone: empFormData.phone || instance?.CandidatePhone || '',
            phone: empFormData.phone || instance?.CandidatePhone || '',
            HireDate: empFormData.startDate || (instance?.JobStartDate ? instance.JobStartDate.substring(0, 10) : new Date().toISOString().substring(0, 10)),
            hireDate: empFormData.startDate || (instance?.JobStartDate ? instance.JobStartDate.substring(0, 10) : new Date().toISOString().substring(0, 10)),
            Position: instance?.JobTitle || '',
            position: instance?.JobTitle || '',
            EmploymentType: (() => {
              const rawType = String(
                Array.isArray(instance?.EmploymentType)
                  ? instance.EmploymentType[0]
                  : (instance?.EmploymentType || empFormData.employeeType || '')
              ).trim();
              if (rawType.toUpperCase().includes('C2C')) return 'C2C';
              if (rawType.toUpperCase().includes('W2')) return 'Full Time (W2)';
              if (rawType.toUpperCase().includes('1099')) return 'Contract (1099)';
              return rawType || 'C2C';
            })(),
            employmentType: (() => {
              const rawType = String(
                Array.isArray(instance?.EmploymentType)
                  ? instance.EmploymentType[0]
                  : (instance?.EmploymentType || empFormData.employeeType || '')
              ).trim();
              if (rawType.toUpperCase().includes('C2C')) return 'C2C';
              if (rawType.toUpperCase().includes('W2')) return 'Full Time (W2)';
              if (rawType.toUpperCase().includes('1099')) return 'Contract (1099)';
              return rawType || 'C2C';
            })()
          }}
          onEmployeeCreated={handleEmployeeCreated}
          onClose={() => {
            setShowAddEmployeeModal(false);
            if (typeof fetchAssignees === 'function') fetchAssignees();
          }}
        />
      )}

      {/* ── ACTION MODAL 2: Create Deployment Modal ── */}
      {showDepModal && (
        <div className="onb-submodal-overlay">
          <div className="onb-submodal-content" style={{ maxWidth: '580px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0f766e' }}>
                <Briefcase size={20} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                  {data?.deployment || steps.some(s => s.StepCode === 'CREATE_DEPLOYMENT' && s.ResultRefId) ? 'Update Placement Deployment' : 'Create Placement Deployment'}
                </h3>
              </div>
              <button type="button" className="onb-close-btn" onClick={() => setShowDepModal(false)}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateDeploymentSubmit}>
              <div className="onb-form-group">
                <label>Placed Job Title</label>
                <input
                  type="text"
                  className="onb-input"
                  value={depFormData.placedJobTitle}
                  onChange={(e) => setDepFormData({ ...depFormData, placedJobTitle: e.target.value })}
                  required
                />
              </div>

              <div className="onb-form-row">
                <div className="onb-form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="onb-input"
                    value={depFormData.startDate}
                    onChange={(e) => setDepFormData({ ...depFormData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="onb-form-group">
                  <label>End Date (Optional)</label>
                  <input
                    type="date"
                    className="onb-input"
                    value={depFormData.endDate}
                    onChange={(e) => setDepFormData({ ...depFormData, endDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Rate Breakdown Section */}
              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', margin: '14px 0', boxSizing: 'border-box' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155', display: 'block', marginBottom: '8px' }}>
                  Rate Breakdown & Commercials ($/hr)
                </span>

                <div className="onb-form-row-3col">
                  <div className="onb-form-group">
                    <label>Client Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      className="onb-input"
                       placeholder="e.g. 85.00"
                      value={depFormData.clientRate}
                      onChange={(e) => handleDepFieldChange('clientRate', e.target.value)}
                    />
                  </div>
                  <div className="onb-form-group">
                    <label>VMS %</label>
                    <input
                      type="number"
                      step="0.01"
                      className="onb-input"
                      placeholder="e.g. 3.0"
                      value={depFormData.vmsPct}
                      onChange={(e) => handleDepFieldChange('vmsPct', e.target.value)}
                    />
                  </div>
                  <div className="onb-form-group">
                    <label>Rate After VMS</label>
                    <input
                      type="number"
                      step="0.01"
                      className="onb-input"
                      placeholder="e.g. 82.45"
                      value={depFormData.rateAfterVms}
                      onChange={(e) => handleDepFieldChange('rateAfterVms', e.target.value)}
                    />
                  </div>
                </div>

                {!isW2 && (
                  <div className="onb-form-row-3col">
                    <div className="onb-form-group">
                      <label>Supplier / Vendor Rate</label>
                      <input
                        type="number"
                        step="0.01"
                        className="onb-input"
                        placeholder="e.g. 65.00"
                        value={depFormData.supplierRate}
                        onChange={(e) => handleDepFieldChange('supplierRate', e.target.value)}
                      />
                    </div>
                    <div className="onb-form-group">
                      <label>Supplier VMS %</label>
                      <input
                        type="number"
                        step="0.01"
                        className="onb-input"
                        placeholder="e.g. 2.0"
                        value={depFormData.supplierVmsPct}
                        onChange={(e) => handleDepFieldChange('supplierVmsPct', e.target.value)}
                      />
                    </div>
                    <div className="onb-form-group">
                      <label>Supplier Rate After VMS</label>
                      <input
                        type="number"
                        step="0.01"
                        className="onb-input"
                        placeholder="e.g. 63.70"
                        value={depFormData.supplierRateAfterVms}
                        onChange={(e) => handleDepFieldChange('supplierRateAfterVms', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <div className="onb-form-row-3col">
                  {!isW2 ? (
                    <div className="onb-form-group">
                      <label>Supplier Net Terms (Days)</label>
                      <input
                        type="number"
                        className="onb-input"
                        placeholder="e.g. 30, 45, 60"
                        value={depFormData.supplierNetTermsDays || ''}
                        onChange={(e) => handleDepFieldChange('supplierNetTermsDays', e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="onb-form-group">
                      <label>
                        Candidate Compliance Pay Rate
                        <span style={{ fontSize: '0.72rem', color: '#0284c7', marginLeft: '6px', fontWeight: 500 }}>
                          W2 only
                        </span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="onb-input"
                        placeholder="e.g. 35.00"
                        value={depFormData.compliancePayRate}
                        onChange={(e) => setDepFormData({ ...depFormData, compliancePayRate: e.target.value })}
                      />
                    </div>
                  )}
                  <div className="onb-form-group">
                    <label>Candidate {isW2 ? 'Actual ' : ''}Pay Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      className="onb-input"
                      placeholder="e.g. 60.00"
                      value={depFormData.actualPayRate}
                      onChange={(e) => handleDepFieldChange('actualPayRate', e.target.value)}
                    />
                  </div>
                  <div className="onb-form-group" style={{ background: '#f0fdf4', padding: '6px 8px', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                    <label style={{ color: '#166534', fontWeight: 700 }}>Profit Margin ($/hr)</label>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#15803d', marginTop: '4px' }}>
                      ${depFormData.margin != null && depFormData.margin !== '' ? Number(depFormData.margin).toFixed(2) : '0.00'}
                    </div>
                  </div>
                </div>

                <div className="onb-form-row-3col">
                  <div className="onb-form-group" style={{ position: 'relative' }}>
                    <label>Referral Source</label>
                    <input
                      type="text"
                      className="onb-input"
                      placeholder="Select or type Client / Supplier"
                      value={depFormData.referralSource}
                      onChange={(e) => {
                        setDepFormData({ ...depFormData, referralSource: e.target.value });
                        setShowDepReferralDropdown(true);
                      }}
                      onFocus={() => setShowDepReferralDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDepReferralDropdown(false), 220)}
                      autoComplete="off"
                    />
                    {showDepReferralDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        width: '100%',
                        minWidth: '220px',
                        zIndex: 1000,
                        maxHeight: '210px',
                        overflowY: 'auto',
                        backgroundColor: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
                        marginTop: '2px'
                      }}>
                        {getFilteredCrmCompanies(depFormData.referralSource).map((clientObj, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 12px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              borderBottom: '1px solid #f1f5f9',
                              backgroundColor: '#ffffff',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setDepFormData(prev => ({ ...prev, referralSource: clientObj.displayName }));
                              setShowDepReferralDropdown(false);
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                          >
                            <span style={{ fontWeight: 600, color: '#1f2937', fontSize: '0.86rem' }}>
                              {clientObj.displayName}
                            </span>
                            {clientObj.companyName && (
                              <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '12px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                {clientObj.companyName}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="onb-form-group">
                    <label>Referral Fee ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="onb-input"
                      placeholder="e.g. 500"
                      value={depFormData.referralFee}
                      onChange={(e) => handleDepFieldChange('referralFee', e.target.value)}
                    />
                  </div>
                  <div className="onb-form-group">
                    <label>Expenses ($/hr)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="onb-input"
                      placeholder="e.g. 0.00"
                      value={depFormData.expenses != null ? depFormData.expenses : ''}
                      onChange={(e) => handleDepFieldChange('expenses', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="onb-btn-outline" onClick={() => setShowDepModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="onb-btn-primary" disabled={savingDep}>
                  <Briefcase size={14} /> {savingDep ? 'Saving...' : (data?.deployment || steps.some(s => s.StepCode === 'CREATE_DEPLOYMENT' && s.ResultRefId) ? 'Update Deployment' : 'Create Deployment & Freeze Chain')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Guard Warning Modal */}
      {showGuardModal && (
        <div className="onb-submodal-overlay">
          <div className="onb-submodal-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#d97706', marginBottom: '14px' }}>
              <AlertTriangle size={24} />
              <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Incomplete Steps Warning</h3>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 }}>
              The following required onboarding steps have not reached terminal completion:
            </p>

            <div style={{ maxHeight: '160px', overflowY: 'auto', background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', margin: '10px 0' }}>
              {openStepsList.map((st, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: idx < openStepsList.length - 1 ? '1px solid #f1f5f9' : 'none', fontSize: '0.82rem' }}>
                  <strong>{st.StepName}</strong>
                  <span className="onb-status-chip pending">{st.CurrentStatusCode || 'NOT STARTED'}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                Completion / Override Notes (Optional)
              </label>
              <textarea
                rows={2}
                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box' }}
                placeholder="Reason or notes for completing onboarding..."
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
              />
            </div>

            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="forceOverride"
                checked={forceComplete}
                onChange={(e) => setForceComplete(e.target.checked)}
              />
              <label htmlFor="forceOverride" style={{ fontSize: '0.83rem', fontWeight: 600, color: '#1e293b', cursor: 'pointer' }}>
                I acknowledge incomplete steps and confirm force-completion.
              </label>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="onb-btn-outline" onClick={() => setShowGuardModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="onb-btn-primary"
                disabled={!forceComplete || completing}
                onClick={() => handleCompleteClick(true)}
              >
                {completing ? 'Completing...' : 'Force Complete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Trail Modal */}
      {showHistoryModal && (
        <div className="onb-submodal-overlay">
          <div className="onb-submodal-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', fontWeight: 700 }}>
                Status History: {historyStep?.StepName}
              </h3>
              <button type="button" className="onb-close-btn" onClick={() => setShowHistoryModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {historyLogs
                .filter(h => h.OnboardingInstanceStepId === historyStep?.OnboardingInstanceStepId)
                .length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '20px 0', fontSize: '0.85rem' }}>
                  No status history recorded yet (step has not been updated).
                </p>
              ) : (
                historyLogs
                  .filter(h => h.OnboardingInstanceStepId === historyStep?.OnboardingInstanceStepId)
                  .map((log, idx) => (
                    <div key={idx} style={{ padding: '10px 0', borderBottom: '1px solid #f1f5f9', fontSize: '0.82rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', fontSize: '0.75rem' }}>
                        <span><strong>By:</strong> {log.ChangedByUserName || 'System'}</span>
                        <span>{new Date(log.ChangedAtUtc).toLocaleString()}</span>
                      </div>
                      <div style={{ marginTop: '4px', fontWeight: 600, color: '#0f172a' }}>
                        Transition: <span style={{ color: '#64748b' }}>{log.FromStatusCode || '(Empty)'}</span> <ArrowRight size={12} style={{ display: 'inline', margin: '0 4px', color: '#0f766e' }} /> <span style={{ color: '#0f766e' }}>{log.ToStatusCode}</span>
                      </div>
                      {log.ReasonText && (
                        <div style={{ fontSize: '0.78rem', color: '#475569', marginTop: '2px' }}>
                          Note: {log.ReasonText}
                        </div>
                      )}
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ACTION MODAL: Outcome Modal (Reject / Withdrawn) ── */}
      {outcomeModal.open && (
        <div className="onb-submodal-overlay" style={{ zIndex: 1100 }}>
          <div className="onb-submodal-content" style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: outcomeModal.type === 'WITHDRAWN' ? '#d97706' : '#dc2626' }}>
                {outcomeModal.type === 'WITHDRAWN' ? <Ban size={22} /> : <XCircle size={22} />}
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>
                  {outcomeModal.type === 'WITHDRAWN' ? 'Withdraw Candidate from Onboarding' : 'Reject Candidate from Onboarding'}
                </h3>
              </div>
              <button
                type="button"
                className="onb-close-btn"
                onClick={handleCloseOutcomeModal}
                disabled={outcomeModal.submitting}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ marginBottom: '16px', fontSize: '0.88rem', color: '#475569', lineHeight: 1.5 }}>
              {outcomeModal.type === 'WITHDRAWN' ? (
                <span>
                  Are you sure you want to mark <strong>{candidateFullName}</strong> as <strong>WITHDRAWN</strong>?
                  All uncompleted checklist steps will be automatically set to <strong>N/A</strong>, and the candidate's application will be updated.
                </span>
              ) : (
                <span>
                  Are you sure you want to <strong>REJECT</strong> the onboarding for <strong>{candidateFullName}</strong>?
                  All uncompleted checklist steps will be automatically set to <strong>N/A</strong>, and the candidate's application will be updated.
                </span>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                Reason / Notes <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={outcomeModal.notes}
                onChange={(e) => setOutcomeModal(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={outcomeModal.type === 'WITHDRAWN' ? 'Enter reason for withdrawal (e.g. Candidate accepted another offer, personal reasons)...' : 'Enter rejection reason (e.g. Background check failed, client cancelled position)...'}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.88rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                disabled={outcomeModal.submitting}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="onb-btn-outline"
                onClick={handleCloseOutcomeModal}
                disabled={outcomeModal.submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="onb-btn-primary"
                style={{
                  backgroundColor: outcomeModal.type === 'WITHDRAWN' ? '#d97706' : '#dc2626',
                  borderColor: outcomeModal.type === 'WITHDRAWN' ? '#d97706' : '#dc2626'
                }}
                onClick={handleConfirmOutcome}
                disabled={outcomeModal.submitting || !outcomeModal.notes.trim()}
              >
                {outcomeModal.submitting ? 'Processing...' : (outcomeModal.type === 'WITHDRAWN' ? 'Confirm Withdrawal' : 'Confirm Rejection')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ACTION MODAL: Revoke Outcome Modal ── */}
      {revokeModal.open && (
        <div className="onb-submodal-overlay" style={{ zIndex: 1100 }}>
          <div className="onb-submodal-content" style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2563eb' }}>
                <RotateCcw size={22} />
                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>
                  Revoke {revokeModal.outcomeType === 'WITHDRAWN' ? 'Withdrawal' : (revokeModal.outcomeType === 'REJECTED' ? 'Rejection' : 'Cancellation')}
                </h3>
              </div>
              <button
                type="button"
                className="onb-close-btn"
                onClick={handleCloseRevokeModal}
                disabled={revokeModal.submitting}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ marginBottom: '16px', fontSize: '0.88rem', color: '#475569', lineHeight: 1.5 }}>
              Are you sure you want to restore <strong>{candidateFullName}</strong> to active onboarding?
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', marginTop: '10px', fontSize: '0.82rem', color: '#1e40af', lineHeight: 1.5 }}>
                • Onboarding status will revert back to <strong>In Progress</strong>.<br />
                • Incomplete checklist steps will be restored and editable.<br />
                • Any completed steps will remain completed.
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Reason / Remarks (Optional)
              </label>
              <textarea
                rows={3}
                className="onb-input"
                placeholder="Enter remarks or reason for restoring candidate..."
                value={revokeModal.notes}
                onChange={(e) => setRevokeModal(prev => ({ ...prev, notes: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.85rem',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
                disabled={revokeModal.submitting}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                className="onb-btn-outline"
                onClick={handleCloseRevokeModal}
                disabled={revokeModal.submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="onb-btn-primary"
                style={{
                  backgroundColor: '#2563eb',
                  borderColor: '#2563eb'
                }}
                onClick={handleConfirmRevoke}
                disabled={revokeModal.submitting}
              >
                {revokeModal.submitting ? 'Restoring...' : 'Confirm Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commercial Document Modal (MSA/PO Linking & Drafting) */}
      {commercialDocModal.open && (
        <CommercialDocumentModal
          isOpen={commercialDocModal.open}
          onClose={() => setCommercialDocModal(prev => ({ ...prev, open: false, step: null }))}
          documentTypeCode={commercialDocModal.typeCode}
          counterpartySide={commercialDocModal.side}
          counterpartyCompanyId={commercialDocModal.companyId}
          counterpartyCompanyName={commercialDocModal.companyName}
          governingEntityId={instance?.EntityId || 4}
          candidateContext={{
            candidateName: instance?.CandidateName || '',
            candidateId: instance?.CandidateId || null,
            jobTitle: instance?.JobTitle || '',
            deploymentId: data?.deployment?.DeploymentId || null,
            billRate: instance?.ClientRate || instance?.SubmissionRate || '',
            clientRate: instance?.ClientRate || instance?.SubmissionRate || '',
            supplierRate: instance?.SupplierRate || ''
          }}
          onLinkedSuccess={handleCommercialDocLinked}
          onCreatedSuccess={handleCommercialDocLinked}
        />
      )}

      {/* Employee Details Modal Popup (Step 9 Click) */}
      {selectedEmployeeIdForModal && (
        <div
          className="onb-user-details-modal-overlay"
          onClick={() => setSelectedEmployeeIdForModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(3px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="onb-user-details-modal-container"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '95vw',
              maxWidth: '1380px',
              height: '92vh',
              maxHeight: '94vh',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)',
              position: 'relative'
            }}
          >
            <button
              type="button"
              onClick={() => setSelectedEmployeeIdForModal(null)}
              title="Close Employee Details"
              style={{
                position: 'absolute',
                top: '14px',
                right: '16px',
                zIndex: 1000,
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                transition: 'all 0.2s'
              }}
            >
              <X size={18} color="#334155" />
            </button>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <EmployeeDetailsPage
                employeeId={selectedEmployeeIdForModal}
                passedCompany={{ id: instance?.EntityId || 4, name: instance?.EntityName || 'Prophecy Consulting INC' }}
                isModal={true}
                onClose={() => setSelectedEmployeeIdForModal(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── ACTION MODAL 5: High-Fidelity Document Preview Modal ── */}
      {previewDoc && (
        <div className="onb-doc-preview-overlay" onClick={() => setPreviewDoc(null)}>
          <div className="onb-doc-preview-container" onClick={(e) => e.stopPropagation()}>
            <div className="onb-doc-preview-header">
              <div className="onb-doc-preview-title">
                <FileText size={18} color="#0284c7" />
                <span title={previewDoc.fileName}>{previewDoc.fileName}</span>
              </div>
              <div className="onb-doc-preview-actions">
                <a
                  href={previewDoc.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="onb-doc-preview-action-btn"
                  title="Open document in a separate browser tab"
                >
                  <ExternalLink size={13} />
                  <span>Open in Tab</span>
                </a>
                <a
                  href={previewDoc.fileUrl}
                  download={previewDoc.fileName}
                  className="onb-doc-preview-action-btn"
                  title="Download document to your computer"
                >
                  <Download size={13} />
                  <span>Download</span>
                </a>
                <button
                  type="button"
                  className="onb-doc-preview-close-btn"
                  onClick={() => setPreviewDoc(null)}
                  title="Close preview (Esc)"
                >
                  <X size={17} />
                </button>
              </div>
            </div>
            <div className="onb-doc-preview-body">
              {['pdf'].includes(previewDoc.fileExt) ? (
                <iframe
                  src={previewDoc.fileUrl}
                  title={previewDoc.fileName}
                  className="onb-doc-preview-iframe"
                />
              ) : ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'].includes(previewDoc.fileExt) ? (
                <img
                  src={previewDoc.fileUrl}
                  alt={previewDoc.fileName}
                  className="onb-doc-preview-image"
                />
              ) : ['docx', 'doc'].includes(previewDoc.fileExt) ? (
                <div className="onb-docx-preview-wrapper">
                  {loadingDocx ? (
                    <div className="onb-docx-loading">
                      <Loader2 size={36} className="onb-spin-icon" style={{ animation: 'spin 1.2s linear infinite', color: '#0284c7', marginBottom: '14px' }} />
                      <h4 style={{ margin: '0 0 6px 0', fontSize: '1.05rem', color: '#1e293b' }}>Converting Word Document...</h4>
                      <p style={{ margin: 0, fontSize: '0.84rem', color: '#64748b' }}>Rendering document pages using Mammoth for inline preview.</p>
                    </div>
                  ) : docxError ? (
                    <div className="onb-doc-preview-fallback">
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: '#fef3c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px'
                      }}>
                        <AlertTriangle size={32} color="#d97706" />
                      </div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '1.05rem', color: '#1e293b' }}>
                        Document Preview Notice
                      </h4>
                      <p style={{ margin: '0 0 20px 0', fontSize: '0.84rem', color: '#64748b', maxWidth: '400px' }}>
                        {docxError}
                      </p>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <a
                          href={previewDoc.fileUrl}
                          download={previewDoc.fileName}
                          className="onb-btn-primary"
                          style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        >
                          <Download size={14} /> Download Document
                        </a>
                        <button
                          type="button"
                          className="onb-btn-outline"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                          onClick={() => {
                            setLoadingDocx(true);
                            setDocxError(null);
                            axios.post(`${BASE_URL}/api/onboarding/documents/docx-preview`, {
                              path: previewDoc.storagePath || previewDoc.fileUrl,
                              fileUrl: previewDoc.fileUrl,
                              fileName: previewDoc.fileName,
                              docId: previewDoc.docId
                            })
                              .then(res => {
                                if (res.data?.success && res.data?.html) {
                                  setDocxHtml(res.data.html);
                                } else {
                                  setDocxError(res.data?.message || 'Could not parse Word document content.');
                                }
                              })
                              .catch(err => {
                                setDocxError(err.response?.data?.message || err.message || 'Failed to preview DOCX document.');
                              })
                              .finally(() => {
                                setLoadingDocx(false);
                              });
                          }}
                        >
                          <RotateCcw size={14} /> Retry Preview
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="onb-docx-scroll-wrapper">
                      <div
                        className="onb-docx-paper"
                        dangerouslySetInnerHTML={{ __html: docxHtml }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="onb-doc-preview-fallback">
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: '#e0f2fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <FileText size={32} color="#0284c7" />
                  </div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.05rem', color: '#1e293b' }}>
                    {previewDoc.fileName}
                  </h4>
                  <p style={{ margin: '0 0 20px 0', fontSize: '0.84rem', color: '#64748b', maxWidth: '380px' }}>
                    Direct browser preview is not available for this file format (.{previewDoc.fileExt}). You can download it or open it directly in a new tab.
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <a
                      href={previewDoc.fileUrl}
                      download={previewDoc.fileName}
                      className="onb-btn-primary"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Download size={14} /> Download Document
                    </a>
                    <a
                      href={previewDoc.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="onb-btn-outline"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      <ExternalLink size={14} /> Open in Tab
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingDetailDrawer;
